import { useState, useEffect } from "react";
import { api, beginImpersonation } from "./api.js";

// --- Design Tokens (same dark theme, admin-blue accent) ---
const C = {
  bg: "#0a0a0f", surface: "#12121a", surfaceHover: "#1a1a26",
  border: "#1e1e2e", borderActive: "#3b3b5c",
  text: "#e2e2e8", textMuted: "#7a7a8e", textDim: "#4a4a5e",
  accent: "#4d9ef0", accentDim: "#3578c0", accentBg: "rgba(77,158,240,0.08)",
  danger: "#f04d4d", dangerBg: "rgba(240,77,77,0.08)",
  warn: "#f0a84d", warnBg: "rgba(240,168,77,0.08)",
  green: "#4df07a", greenBg: "rgba(77,240,122,0.08)",
  purple: "#7B61FF",
};
const F = "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace";
const FB = "'DM Sans', 'Segoe UI', system-ui, sans-serif";

// --- Helpers ---
function StatCard({ label, value, sub, icon, color, trend }) {
  return (
    <div style={{ padding: "18px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, flex: 1 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 9, color: C.textDim, fontFamily: F, fontWeight: 600, letterSpacing: "0.06em" }}>{label.toUpperCase()}</span>
        <span style={{ fontSize: 14 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, fontFamily: F, color: color || C.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: trend === "up" ? C.green : trend === "down" ? C.danger : C.accent, fontWeight: 500, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Badge({ text, color }) {
  const cols = { active: C.green, trialling: C.accent, churned: C.danger, suspended: C.warn, starter: "#888", growth: C.accent, scale: C.purple, agency_pro: "#2dd4a8", "pro (agency)": "#2dd4a8", "standard (agency)": C.accent, open: C.warn, resolved: C.green, healthy: C.green, degraded: C.warn, down: C.danger, owner: C.accent, admin: C.purple, member: C.textMuted, connected: C.green, error: C.danger, critical: "#dc2626", warning: C.warn, info: C.accent, debug: C.textMuted };
  const c = cols[text.toLowerCase()] || C.accent;
  return <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: F, fontWeight: 600, background: c + "15", color: c, border: `1px solid ${c}22`, textTransform: "capitalize" }}>{text}</span>;
}

function AdminIntegrationConfigModal({ modal, onClose, onSaved }) {
  if (!modal) return null;
  const { intg, orgId } = modal;
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.admin.organisations.getIntegration(orgId, intg.key).then(res => {
      const creds = res.credentials_json || {};
      const initial = {};
      (intg.credentialFields || []).forEach(f => { initial[f.name] = creds[f.name] || (creds.apiKey && f.name === "api_key" ? creds.apiKey : ""); });
      setForm(initial);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [orgId, intg.key]);
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.admin.organisations.saveIntegration(orgId, intg.key, form);
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
      alert("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };
  const inputStyle = { width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontFamily: F, fontSize: 12, outline: "none", boxSizing: "border-box" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={onClose}>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <span style={{ fontSize: 28 }}>{intg.icon}</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Configure {intg.label}</div>
            <div style={{ fontSize: 12, color: C.textDim }}>{intg.desc}</div>
          </div>
        </div>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: C.textMuted }}>Loading...</div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
              {(intg.credentialFields || []).map(f => (
                <div key={f.name}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 500, marginBottom: 6, color: C.text }}>{f.label}</label>
                  <input type={f.type || "text"} value={form[f.name] || ""} onChange={e => setForm({ ...form, [f.name]: e.target.value })} placeholder={f.placeholder || f.label} style={inputStyle} autoComplete="off" />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button onClick={onClose} style={{ padding: "10px 20px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMuted, fontFamily: F, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: "10px 24px", background: saving ? C.border : C.accent, color: saving ? C.textDim : C.bg, border: "none", borderRadius: 8, fontFamily: F, fontSize: 12, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer" }}>{saving ? "Saving..." : "Save"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// --- Mock Data ---

// PLATFORM PLANS (what Pipeline charges)
const PLATFORM_PLANS = [
  { id: "starter", name: "Starter", price: 97, annual: 970, credits: 500, seats: 1, color: "#888" },
  { id: "growth", name: "Growth", price: 247, annual: 2470, credits: 2000, seats: 3, color: C.accent },
  { id: "scale", name: "Scale", price: 497, annual: 4970, credits: 5000, seats: 10, color: C.purple },
  { id: "agency_pro", name: "Agency Pro", price: 497, annual: 4970, credits: 15000, seats: 3, color: "#2dd4a8", isAgency: true, perWorkspace: 97 },
];

// AGENCIES
const AGENCIES = [
  { id: "ag1", name: "Elevate AI Consulting", owner: "Sarah Mitchell", email: "sarah@elevateai.co", plan: "agency_pro", status: "Active", stripeConnected: true, domain: "app.elevateai.co", clients: 6, clientSlots: 10, creditPool: 15000, creditPoolUsed: 8420, mrr: 4482, workspaceFees: 582, platformFee: 497, created: "2025-09-01", lastActive: "1 hr ago" },
  { id: "ag2", name: "GrowthLab Digital", owner: "Daniel Torres", email: "daniel@growthlabdigital.com", plan: "agency_pro", status: "Active", stripeConnected: true, domain: "app.growthlabdigital.com", clients: 3, clientSlots: 10, creditPool: 15000, creditPoolUsed: 4100, mrr: 1788, workspaceFees: 291, platformFee: 497, created: "2025-11-15", lastActive: "3 hrs ago" },
];

const ORGS = [
  // Direct Platform clients (agency_id: null)
  { id: 1, name: "Dunn Consulting", agency_id: null, plan: "Scale", status: "Active", owner: "Andrew Dunn", email: "andrew@dunnco.co.uk", seats: "8/10", creditsUsed: 3840, creditsTotal: 5000, mrr: 497, created: "2025-10-12", lastActive: "2 hrs ago", projects: 5, leads: 4200, integrations: 6, health: 94 },
  { id: 2, name: "Apex Advisory", agency_id: null, plan: "Growth", status: "Active", owner: "Sarah Mitchell", email: "sarah@apexadvisory.io", seats: "2/3", creditsUsed: 1450, creditsTotal: 2000, mrr: 247, created: "2025-11-03", lastActive: "4 hrs ago", projects: 3, leads: 1850, integrations: 4, health: 82 },
  { id: 3, name: "Perry Salvagne Consulting", agency_id: null, plan: "Growth", status: "Active", owner: "Perry Salvagne", email: "perry@hodgeins.co.uk", seats: "3/3", creditsUsed: 1920, creditsTotal: 2000, mrr: 247, created: "2025-12-01", lastActive: "1 day ago", projects: 2, leads: 920, integrations: 3, health: 76 },
  { id: 4, name: "InsureTech Partners", agency_id: null, plan: "Starter", status: "Trialling", owner: "James Patel", email: "james@insuretechpro.com", seats: "1/1", creditsUsed: 180, creditsTotal: 500, mrr: 0, created: "2026-02-08", lastActive: "3 hrs ago", projects: 1, leads: 340, integrations: 1, health: 58 },
  { id: 5, name: "NexGen Consulting", agency_id: null, plan: "Growth", status: "Active", owner: "Emily Rodriguez", email: "emily@nexgenai.co", seats: "3/3", creditsUsed: 1780, creditsTotal: 2000, mrr: 247, created: "2025-11-15", lastActive: "6 hrs ago", projects: 4, leads: 2100, integrations: 5, health: 85 },
  { id: 6, name: "DataPulse Advisory", agency_id: null, plan: "Scale", status: "Active", owner: "Marcus Webb", email: "marcus@datapulse.com", seats: "6/10", creditsUsed: 4200, creditsTotal: 5000, mrr: 497, created: "2025-09-20", lastActive: "1 hr ago", projects: 7, leads: 5600, integrations: 8, health: 97 },
  { id: 7, name: "CloudMetrics Ltd", agency_id: null, plan: "Starter", status: "Churned", owner: "Lisa Thompson", email: "lisa@cloudmetrics.io", seats: "0/1", creditsUsed: 0, creditsTotal: 500, mrr: 0, created: "2025-08-10", lastActive: "45 days ago", projects: 1, leads: 280, integrations: 0, health: 8 },
  { id: 8, name: "SynthWave Partners", agency_id: null, plan: "Growth", status: "Active", owner: "David Kim", email: "david@synthwave.dev", seats: "2/3", creditsUsed: 890, creditsTotal: 2000, mrr: 247, created: "2026-01-05", lastActive: "12 hrs ago", projects: 2, leads: 640, integrations: 3, health: 52 },
  { id: 9, name: "FinLeap Consulting", agency_id: null, plan: "Growth", status: "Suspended", owner: "Nina Okoro", email: "nina@finleap.io", seats: "0/3", creditsUsed: 0, creditsTotal: 2000, mrr: 0, created: "2025-10-30", lastActive: "14 days ago", projects: 3, leads: 1200, integrations: 2, health: 12 },
  { id: 10, name: "AutoPilot Advisory", agency_id: null, plan: "Starter", status: "Trialling", owner: "Tom Bradley", email: "tom@autopilotai.com", seats: "1/1", creditsUsed: 95, creditsTotal: 500, mrr: 0, created: "2026-02-10", lastActive: "5 hrs ago", projects: 1, leads: 120, integrations: 0, health: 41 },
  // Agency-owned workspaces (via Elevate AI)
  { id: 11, name: "Hodge Insurance", agency_id: "ag1", plan: "Pro (Agency)", status: "Active", owner: "Perry Salvagne", email: "perry@hodgeins.co.uk", seats: "3/5", creditsUsed: 1840, creditsTotal: 2500, mrr: 497, created: "2025-11-01", lastActive: "2 hrs ago", projects: 3, leads: 1420, integrations: 4, health: 88, agency: "Elevate AI Consulting" },
  { id: 12, name: "NexGen Recruitment", agency_id: "ag1", plan: "Pro (Agency)", status: "Active", owner: "Emily Chen", email: "emily@nexgenrecruit.com", seats: "4/5", creditsUsed: 2100, creditsTotal: 2500, mrr: 497, created: "2025-10-20", lastActive: "1 hr ago", projects: 4, leads: 2340, integrations: 6, health: 94, agency: "Elevate AI Consulting" },
  { id: 13, name: "ABC Kitchens", agency_id: "ag1", plan: "Standard (Agency)", status: "Active", owner: "John Davies", email: "john@abckitchens.co.uk", seats: "2/3", creditsUsed: 1200, creditsTotal: 2000, mrr: 347, created: "2025-12-15", lastActive: "4 hrs ago", projects: 2, leads: 860, integrations: 2, health: 76, agency: "Elevate AI Consulting" },
  // Agency-owned workspaces (via GrowthLab)
  { id: 14, name: "Bright Futures Academy", agency_id: "ag2", plan: "Standard (Agency)", status: "Active", owner: "Hannah Lee", email: "hannah@brightfutures.edu", seats: "2/3", creditsUsed: 980, creditsTotal: 2000, mrr: 397, created: "2026-01-05", lastActive: "5 hrs ago", projects: 2, leads: 540, integrations: 3, health: 72, agency: "GrowthLab Digital" },
  { id: 15, name: "TerraVolt Energy", agency_id: "ag2", plan: "Pro (Agency)", status: "Active", owner: "Marcus Hale", email: "marcus@terravolt.io", seats: "3/5", creditsUsed: 1600, creditsTotal: 2500, mrr: 497, created: "2025-12-20", lastActive: "2 hrs ago", projects: 3, leads: 1100, integrations: 5, health: 81, agency: "GrowthLab Digital" },
];

const TICKETS = [
  { id: "T-1024", org: "Apex Advisory", subject: "Enrichment waterfall timing out on large lists", status: "Open", priority: "High", created: "2 hrs ago" },
  { id: "T-1023", org: "Perry Salvagne Consulting", subject: "Process map export not including all steps", status: "Open", priority: "Medium", created: "5 hrs ago" },
  { id: "T-1022", org: "DataPulse Advisory", subject: "API rate limit hit during batch enrichment", status: "Open", priority: "High", created: "8 hrs ago" },
  { id: "T-1021", org: "NexGen Consulting", subject: "Can't connect HeyReach integration", status: "Resolved", priority: "Medium", created: "1 day ago" },
  { id: "T-1020", org: "SynthWave Partners", subject: "AI Council giving outdated recommendations", status: "Resolved", priority: "Low", created: "2 days ago" },
];

const USERS = [
  { name: "Andrew Dunn", email: "andrew@dunnco.co.uk", org: "Dunn Consulting", role: "Owner", lastActive: "2 hrs ago", created: "2025-10-12", logins: 342 },
  { name: "Sarah Mitchell", email: "sarah@apexadvisory.io", org: "Apex Advisory", role: "Owner", lastActive: "4 hrs ago", created: "2025-11-03", logins: 218 },
  { name: "Perry Salvagne", email: "perry@hodgeins.co.uk", org: "Perry Salvagne Consulting", role: "Owner", lastActive: "1 day ago", created: "2025-12-01", logins: 156 },
  { name: "Marcus Webb", email: "marcus@datapulse.com", org: "DataPulse Advisory", role: "Owner", lastActive: "1 hr ago", created: "2025-09-20", logins: 410 },
  { name: "Emily Rodriguez", email: "emily@nexgenai.co", org: "NexGen Consulting", role: "Owner", lastActive: "6 hrs ago", created: "2025-11-15", logins: 198 },
  { name: "James Chen", email: "james@datapulse.com", org: "DataPulse Advisory", role: "Admin", lastActive: "3 hrs ago", created: "2025-10-15", logins: 267 },
  { name: "Rachel Green", email: "rachel@dunnco.co.uk", org: "Dunn Consulting", role: "Admin", lastActive: "5 hrs ago", created: "2025-11-20", logins: 145 },
  { name: "Mike Thompson", email: "mike@dunnco.co.uk", org: "Dunn Consulting", role: "Member", lastActive: "1 day ago", created: "2026-01-05", logins: 67 },
  { name: "James Patel", email: "james@insuretechpro.com", org: "InsureTech Partners", role: "Owner", lastActive: "3 hrs ago", created: "2026-02-08", logins: 12 },
  { name: "Tom Bradley", email: "tom@autopilotai.com", org: "AutoPilot Advisory", role: "Owner", lastActive: "5 hrs ago", created: "2026-02-10", logins: 8 },
];

const CREDIT_COSTS = [
  { action: "Lead Discovery", credits: 1, unit: "per lead", cost: "$0.012", margin: "88%" },
  { action: "Email Verification", credits: 1, unit: "per lead", cost: "$0.008", margin: "92%" },
  { action: "AI Personalisation", credits: 2, unit: "per lead", cost: "$0.035", margin: "82%" },
  { action: "AI Audit Analysis", credits: 25, unit: "per analysis", cost: "$0.42", margin: "83%" },
  { action: "Deck Generation", credits: 15, unit: "per deck", cost: "$0.28", margin: "81%" },
  { action: "Niche Research", credits: 10, unit: "per report", cost: "$0.18", margin: "82%" },
  { action: "Script Generation", credits: 5, unit: "per script", cost: "$0.09", margin: "82%" },
  { action: "Process Map", credits: 10, unit: "per map", cost: "$0.15", margin: "85%" },
  { action: "AI Assistant Query", credits: 1, unit: "per turn", cost: "$0.014", margin: "86%" },
  { action: "AI Council Query", credits: 2, unit: "per turn", cost: "$0.032", margin: "84%" },
];

const PROVIDERS = [
  { name: "BetterContact", type: "Email Enrichment", status: "Healthy", priority: 1, calls: "12,400", errorRate: "0.3%", avgLatency: "1.2s" },
  { name: "Icypeas", type: "Email Enrichment", status: "Healthy", priority: 2, calls: "4,800", errorRate: "0.5%", avgLatency: "1.8s" },
  { name: "Wiza", type: "LinkedIn Enrichment", status: "Degraded", priority: 1, calls: "2,100", errorRate: "2.1%", avgLatency: "3.4s" },
  { name: "Instantly", type: "Email Outreach", status: "Healthy", priority: 1, calls: "8,900", errorRate: "0.1%", avgLatency: "0.4s" },
  { name: "HeyReach", type: "LinkedIn Outreach", status: "Healthy", priority: 1, calls: "3,200", errorRate: "0.4%", avgLatency: "1.1s" },
  { name: "Claude API", type: "AI Engine", status: "Healthy", priority: 1, calls: "45,200", errorRate: "0.02%", avgLatency: "2.8s" },
];

const AI_PROMPTS = [
  { id: "p1", name: "AI Council — Strategic Advisor", model: "claude-sonnet-4-5-20250929", tokensToday: "124K", costToday: "$2.40", lastEdit: "Feb 10" },
  { id: "p2", name: "AI Assistant — Org Knowledge", model: "claude-sonnet-4-5-20250929", tokensToday: "89K", costToday: "$1.72", lastEdit: "Feb 12" },
  { id: "p3", name: "Audit Analysis Engine", model: "claude-sonnet-4-5-20250929", tokensToday: "210K", costToday: "$4.06", lastEdit: "Feb 8" },
  { id: "p4", name: "Messaging Workshop", model: "claude-haiku-4-5-20251001", tokensToday: "56K", costToday: "$0.28", lastEdit: "Feb 11" },
  { id: "p5", name: "Script Generator", model: "claude-haiku-4-5-20251001", tokensToday: "34K", costToday: "$0.17", lastEdit: "Feb 9" },
  { id: "p6", name: "Lead Personalisation", model: "claude-haiku-4-5-20251001", tokensToday: "312K", costToday: "$1.56", lastEdit: "Feb 13" },
  { id: "p7", name: "Process Map Generator", model: "claude-sonnet-4-5-20250929", tokensToday: "67K", costToday: "$1.30", lastEdit: "Feb 7" },
  { id: "p8", name: "Niche Researcher", model: "claude-sonnet-4-5-20250929", tokensToday: "45K", costToday: "$0.87", lastEdit: "Feb 6" },
];

const FEATURE_FLAGS = [
  { key: "lead_discovery", label: "Lead Discovery", desc: "AI-powered lead finding and enrichment", plans: ["Starter", "Growth", "Scale"], core: true },
  { key: "crm", label: "CRM Pipeline", desc: "Contact and deal management", plans: ["Starter", "Growth", "Scale"], core: true },
  { key: "email_campaigns", label: "Email Campaigns", desc: "Automated outreach sequences", plans: ["Starter", "Growth", "Scale"], core: true },
  { key: "ai_assistant", label: "AI Assistant", desc: "General-purpose AI chat and analysis", plans: ["Starter", "Growth", "Scale"], core: true },
  { key: "ai_council", label: "AI Council", desc: "Multi-perspective strategic analysis", plans: ["Growth", "Scale"] },
  { key: "linkedin_campaigns", label: "LinkedIn Campaigns", desc: "Automated LinkedIn outreach", plans: ["Growth", "Scale"] },
  { key: "audit_reports", label: "Audit Reports", desc: "AI-generated consulting audit decks", plans: ["Growth", "Scale"] },
  { key: "call_analyser", label: "Call Analyser", desc: "AI call transcription and insights", plans: ["Growth", "Scale"] },
  { key: "solutions_marketplace", label: "Solutions Marketplace", desc: "Install pre-built + community solutions", plans: ["Growth", "Scale"] },
  { key: "api_access", label: "Public API & Webhooks", desc: "REST API + webhook integrations", plans: ["Growth", "Scale"] },
  { key: "team_collaboration", label: "Team Collaboration", desc: "Multi-seat workspaces with roles", plans: ["Growth", "Scale"] },
  { key: "custom_solutions", label: "Custom Solutions", desc: "Build and deploy bespoke AI tools", plans: ["Scale"] },
  { key: "white_label_reports", label: "White-Label Reports", desc: "Remove Pipeline branding from exports", plans: ["Scale"] },
  { key: "advanced_analytics", label: "Advanced Analytics", desc: "Cohort retention, feature heatmaps", plans: ["Scale"] },
  { key: "custom_integrations", label: "Custom Integrations", desc: "Custom API access and webhook config", plans: ["Scale"] },
  { key: "beta_chrome_ext", label: "🧪 Chrome Extension (Beta)", desc: "AI Course Assistant browser extension", plans: [], beta: true },
  { key: "beta_video_gen", label: "🧪 Video Script AI v2 (Beta)", desc: "Enhanced video script generation with hooks", plans: [], beta: true },
];

// Lead Search Order: metadata for integrations used in lead discovery/enrichment waterfalls
const LEAD_ORDER_META = [
  { key: "icypeas", label: "IcyPeas", icon: "🧊", costLabel: "~$0.02/lead", orderTypes: ["lead_search", "lead_enrichment"] },
  { key: "ai_ark", label: "AI Ark", icon: "🦅", costLabel: "~$0.02/lead", orderTypes: ["lead_search", "lead_enrichment"] },
  { key: "findy", label: "Findy", icon: "🔍", costLabel: "~$0.03/lead", orderTypes: ["lead_search", "lead_enrichment"] },
  { key: "wiza", label: "Wiza", icon: "📊", costLabel: "~$0.04/lead", orderTypes: ["lead_search"] },
  { key: "leadsmagix", label: "Leads Magix", icon: "✨", costLabel: "~$0.025/lead", orderTypes: ["lead_search"] },
  { key: "millionverifier", label: "MillionVerifier", icon: "✅", costLabel: "~$0.0005/verify", orderTypes: ["lead_enrichment"] },
  { key: "bettercontact", label: "BetterContact", icon: "✉️", costLabel: "~$0.01/verify", orderTypes: ["lead_enrichment"] },
  { key: "zerobounce", label: "ZeroBounce", icon: "🛡️", costLabel: "~$0.008/verify", orderTypes: ["lead_enrichment"] },
  { key: "neverbounce", label: "NeverBounce", icon: "✉️", costLabel: "~$0.008/verify", orderTypes: ["lead_enrichment"] },
  { key: "findymail", label: "FindyMail", icon: "✉️", costLabel: "~$0.01/lead", orderTypes: ["lead_enrichment"] },
  { key: "cleanlist", label: "Cleanlist", icon: "🧹", costLabel: "~$0.012/verify", orderTypes: ["lead_enrichment"] },
];
const LEAD_SEARCH_DEFAULTS = LEAD_ORDER_META.filter(i => (i.orderTypes || []).includes("lead_search")).map(i => i.key);
const LEAD_ENRICHMENT_DEFAULTS = LEAD_ORDER_META.filter(i => (i.orderTypes || []).includes("lead_enrichment")).map(i => i.key);

// Admin org-scoped LLM + Enrichment integrations (configured per org in Platform Admin)
const ADMIN_LLM_META = [
  { key: "anthropic", label: "Anthropic", icon: "🧠", desc: "Claude — AI SDR & AI Council", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "openai", label: "OpenAI", icon: "🤖", desc: "GPT — Chat completions & embeddings", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "openrouter", label: "OpenRouter", icon: "🔀", desc: "Unified LLM gateway — access multiple models", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "gemini", label: "Gemini", icon: "✨", desc: "Google Gemini — Multimodal AI", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "grok", label: "Grok", icon: "🪐", desc: "xAI Grok — Reasoning & chat", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
];
const ADMIN_ENRICHMENT_META = [
  { key: "unipile", label: "Unipile", icon: "💼", desc: "LinkedIn campaigns & actions", credentialFields: [{ name: "account_id", label: "Account ID", type: "text" }, { name: "access_token", label: "Access Token", type: "password" }, { name: "dsn", label: "DSN", type: "text", placeholder: "e.g. [REDACTED]" }] },
  { key: "millionverifier", label: "MillionVerifier", icon: "✅", desc: "Email verification & validation", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "icypeas", label: "IcyPeas", icon: "🧊", desc: "Find people & email search", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "bettercontact", label: "BetterContact", icon: "✉️", desc: "Email verification & list cleaning", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "zerobounce", label: "ZeroBounce", icon: "🛡️", desc: "Email verification & validation", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "neverbounce", label: "NeverBounce", icon: "✉️", desc: "Email verification & deliverability", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "ai_ark", label: "AI Ark", icon: "🦅", desc: "B2B data enrichment", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "findy", label: "Findy", icon: "🔍", desc: "Lead discovery & enrichment", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "findymail", label: "FindyMail", icon: "✉️", desc: "Email finder & verification", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "cleanlist", label: "Cleanlist", icon: "🧹", desc: "List cleaning & verification", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "wiza", label: "Wiza", icon: "📊", desc: "Sales intelligence & lead data", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }] },
  { key: "leadsmagix", label: "Leads Magix", icon: "✨", desc: "B2B lead generation", credentialFields: [{ name: "api_key", label: "API Key", type: "password" }, { name: "workspace_id", label: "Workspace ID", type: "text" }] },
];

// --- NAV ---
// Tabs that rendered pure hardcoded mock data (leadgen, knowledge, solutions,
// integrations, content, support, analytics, system) have been removed. They
// can be restored once real data sources are wired.
const NAV = [
  { key: "overview", label: "Overview", icon: "dashboard" },
  { key: "agencies", label: "Agencies", icon: "account_balance" },
  { key: "orgs", label: "Organisations", icon: "domain" },
  { key: "users", label: "Users", icon: "person" },
  { key: "billing", label: "Billing & Revenue", icon: "credit_card" },
  { key: "credits", label: "Credit System", icon: "monetization_on" },
  { key: "ai", label: "AI & Prompts", icon: "psychology" },
  { key: "lead_search_order", label: "Lead Search Order", icon: "reorder" },
  { key: "error_logs", label: "Error Logs", icon: "error" },
  { key: "system", label: "System", icon: "build" },
  { key: "security", label: "Security", icon: "lock" },
];

// =====================
// MAIN APP
// =====================
function AdminPlanEditor() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [bulletDraft, setBulletDraft] = useState('');

  const load = () => api.admin.billingPlans.list().then(d => setPlans(Array.isArray(d) ? d : [])).catch(() => setPlans([]));
  useEffect(() => { load(); }, []);

  const startEdit = (plan) => {
    setEditing(plan.id);
    setForm({
      name: plan.name || '', tagline: plan.tagline || '', description: plan.description || '',
      price_monthly: plan.price_monthly || 0, price_yearly: plan.price_yearly || 0,
      credits_monthly: plan.credits_monthly || 0, max_users: plan.max_users || 1,
      trial_days: plan.trial_days || 0,
      feature_bullets: Array.isArray(plan.feature_bullets) ? [...plan.feature_bullets] : [],
      stripe_price_id_monthly: plan.stripe_price_id_monthly || '',
      stripe_price_id_yearly: plan.stripe_price_id_yearly || '',
      highlight: !!plan.highlight, button_text: plan.button_text || 'Subscribe',
      annual_discount_pct: plan.annual_discount_pct || 0,
      is_active: plan.is_active !== false, sort_order: plan.sort_order || 0,
    });
    setBulletDraft('');
    setMsg(null);
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.admin.billingPlans.update(editing, form);
      setMsg('Saved');
      setEditing(null);
      load();
    } catch (e) { setMsg(e?.message || 'Save failed'); }
    setSaving(false);
  };

  const addBullet = () => {
    if (!bulletDraft.trim()) return;
    setForm(f => ({ ...f, feature_bullets: [...f.feature_bullets, bulletDraft.trim()] }));
    setBulletDraft('');
  };
  const removeBullet = (i) => setForm(f => ({ ...f, feature_bullets: f.feature_bullets.filter((_, j) => j !== i) }));
  const moveBullet = (i, dir) => {
    setForm(f => {
      const arr = [...f.feature_bullets];
      const ni = i + dir;
      if (ni < 0 || ni >= arr.length) return f;
      [arr[i], arr[ni]] = [arr[ni], arr[i]];
      return { ...f, feature_bullets: arr };
    });
  };

  const inp = { width: "100%", padding: "7px 10px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 11, outline: "none", boxSizing: "border-box" };
  const lbl = { fontSize: 9, color: C.textDim, fontFamily: F, marginBottom: 3, fontWeight: 600 };

  if (!plans) return <div style={{ padding: 20, color: C.textDim, fontSize: 12 }}>Loading plans...</div>;

  return (
    <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>SUBSCRIPTION PLANS</span>
        </div>
        {msg && <span style={{ fontSize: 10, fontFamily: F, color: msg === 'Saved' ? C.green : C.danger }}>{msg}</span>}
      </div>

      {/* Plan List */}
      <div style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1fr 0.8fr 0.8fr 0.6fr 0.6fr 0.6fr 0.8fr", gap: 8, padding: "8px 12px", borderBottom: `1px solid ${C.border}`, background: C.bg, borderRadius: "8px 8px 0 0" }}>
        {["#", "Name", "Tagline", "Monthly", "Yearly", "Credits", "Trial", "Active", ""].map(h => (
          <span key={h} style={{ fontSize: 9, fontFamily: F, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em" }}>{h.toUpperCase()}</span>
        ))}
      </div>
      {plans.map((p, i) => (
        <div key={p.id} style={{ display: "grid", gridTemplateColumns: "0.5fr 1.5fr 1fr 0.8fr 0.8fr 0.6fr 0.6fr 0.6fr 0.8fr", gap: 8, padding: "10px 12px", borderBottom: `1px solid ${C.border}`, alignItems: "center", background: editing === p.id ? C.accentBg : "transparent" }}>
          <span style={{ fontSize: 11, fontFamily: F, color: C.textDim }}>{p.sort_order ?? i}</span>
          <div>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{p.name}</span>
            {p.highlight && <span style={{ marginLeft: 6, fontSize: 8, fontFamily: F, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: C.accent + "20", color: C.accent }}>POPULAR</span>}
          </div>
          <span style={{ fontSize: 10, color: C.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.tagline || '—'}</span>
          <span style={{ fontSize: 11, fontFamily: F, fontWeight: 600, color: C.text }}>${Number(p.price_monthly || 0)}</span>
          <span style={{ fontSize: 11, fontFamily: F, color: C.textDim }}>${Number(p.price_yearly || 0)}/yr</span>
          <span style={{ fontSize: 11, fontFamily: F, color: C.accent }}>{p.credits_monthly}</span>
          <span style={{ fontSize: 11, color: C.textDim }}>{p.trial_days || 0}d</span>
          <span style={{ fontSize: 10, fontFamily: F, fontWeight: 700, color: p.is_active !== false ? C.green : C.danger }}>{p.is_active !== false ? 'Yes' : 'No'}</span>
          <button onClick={() => editing === p.id ? setEditing(null) : startEdit(p)} style={{ padding: "4px 10px", background: editing === p.id ? C.danger + "15" : C.accentBg, border: `1px solid ${editing === p.id ? C.danger + '44' : C.accent + '44'}`, borderRadius: 5, color: editing === p.id ? C.danger : C.accent, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: "pointer" }}>
            {editing === p.id ? 'Cancel' : 'Edit'}
          </button>
        </div>
      ))}

      {/* Edit Form */}
      {editing && (
        <div style={{ padding: 20, background: C.bg, border: `1px solid ${C.accent}33`, borderRadius: 10, marginTop: 16 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 700, color: C.accent, marginBottom: 14, letterSpacing: "0.06em" }}>EDIT PLAN: {form.name?.toUpperCase()}</div>

          {/* Row 1: Name, Tagline, Button Text */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 12, marginBottom: 12 }}>
            <div><div style={lbl}>Name</div><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} /></div>
            <div><div style={lbl}>Tagline</div><input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} style={inp} /></div>
            <div><div style={lbl}>Button Text</div><input value={form.button_text} onChange={e => setForm(f => ({ ...f, button_text: e.target.value }))} style={inp} /></div>
          </div>

          {/* Row 2: Description */}
          <div style={{ marginBottom: 12 }}>
            <div style={lbl}>Description</div>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} style={{ ...inp, resize: "vertical" }} />
          </div>

          {/* Row 3: Pricing */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div><div style={lbl}>Monthly ($)</div><input type="number" value={form.price_monthly} onChange={e => setForm(f => ({ ...f, price_monthly: Number(e.target.value) }))} style={inp} /></div>
            <div><div style={lbl}>Yearly ($)</div><input type="number" value={form.price_yearly} onChange={e => setForm(f => ({ ...f, price_yearly: Number(e.target.value) }))} style={inp} /></div>
            <div><div style={lbl}>Credits/mo</div><input type="number" value={form.credits_monthly} onChange={e => setForm(f => ({ ...f, credits_monthly: Number(e.target.value) }))} style={inp} /></div>
            <div><div style={lbl}>Max Users</div><input type="number" value={form.max_users} onChange={e => setForm(f => ({ ...f, max_users: Number(e.target.value) }))} style={inp} /></div>
            <div><div style={lbl}>Trial Days</div><input type="number" value={form.trial_days} onChange={e => setForm(f => ({ ...f, trial_days: Number(e.target.value) }))} style={inp} /></div>
            <div><div style={lbl}>Annual Discount %</div><input type="number" value={form.annual_discount_pct} onChange={e => setForm(f => ({ ...f, annual_discount_pct: Number(e.target.value) }))} style={inp} /></div>
          </div>

          {/* Row 4: Stripe IDs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div><div style={lbl}>Stripe Price ID (Monthly)</div><input value={form.stripe_price_id_monthly} onChange={e => setForm(f => ({ ...f, stripe_price_id_monthly: e.target.value }))} placeholder="price_..." style={inp} /></div>
            <div><div style={lbl}>Stripe Price ID (Yearly)</div><input value={form.stripe_price_id_yearly} onChange={e => setForm(f => ({ ...f, stripe_price_id_yearly: e.target.value }))} placeholder="price_..." style={inp} /></div>
          </div>

          {/* Row 5: Toggles */}
          <div style={{ display: "flex", gap: 24, marginBottom: 16, alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.highlight} onChange={e => setForm(f => ({ ...f, highlight: e.target.checked }))} /> Most Popular
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10, color: C.textDim, fontFamily: F }}>Sort Order:</span>
              <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) }))} style={{ ...inp, width: 60 }} />
            </div>
          </div>

          {/* Feature Bullets */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...lbl, marginBottom: 6 }}>Feature Bullets (shown on pricing card)</div>
            {form.feature_bullets.map((b, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: C.text, flex: 1, padding: "4px 8px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4 }}>{b}</span>
                <button onClick={() => moveBullet(i, -1)} disabled={i === 0} style={{ padding: "2px 6px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 9, cursor: "pointer" }}>▲</button>
                <button onClick={() => moveBullet(i, 1)} disabled={i === form.feature_bullets.length - 1} style={{ padding: "2px 6px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 9, cursor: "pointer" }}>▼</button>
                <button onClick={() => removeBullet(i)} style={{ padding: "2px 6px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 9, cursor: "pointer" }}>✕</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <input value={bulletDraft} onChange={e => setBulletDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && addBullet()} placeholder="Add a feature..." style={{ ...inp, flex: 1 }} />
              <button onClick={addBullet} style={{ padding: "6px 14px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>+ Add</button>
            </div>
          </div>

          {/* Save */}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={save} disabled={saving} style={{ padding: "8px 20px", background: saving ? C.border : C.accent, color: saving ? C.textDim : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 700, cursor: saving ? "default" : "pointer" }}>
              {saving ? 'Saving...' : 'Save Plan'}
            </button>
            <button onClick={() => setEditing(null)} style={{ padding: "8px 20px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 600, color: C.textMuted, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [page, setPage] = useState("overview");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgFilter, setOrgFilter] = useState("All");
  const [orgSearch, setOrgSearch] = useState("");
  const [creditAdjustOrg, setCreditAdjustOrg] = useState(null);
  const [creditAdjustAmount, setCreditAdjustAmount] = useState("");
  const [creditAdjustNote, setCreditAdjustNote] = useState("");
  const [detailTab, setDetailTab] = useState("overview");
  const [orgDecks, setOrgDecks] = useState(null);
  const [orgDecksLoading, setOrgDecksLoading] = useState(false);
  const [orgDecksError, setOrgDecksError] = useState(null);
  const [viewerDeck, setViewerDeck] = useState(null);      // full deck row (with preview_artifacts_json)
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState(null);
  const [viewerSlideIdx, setViewerSlideIdx] = useState(0);
  const [trialEditorOpen, setTrialEditorOpen] = useState(false);
  const [trialAddDays, setTrialAddDays] = useState(7);
  const [trialResetDays, setTrialResetDays] = useState(14);
  const [trialSaving, setTrialSaving] = useState(false);
  const [trialMsg, setTrialMsg] = useState(null);
  const [showAddProvider, setShowAddProvider] = useState(null);
  const [newProviderName, setNewProviderName] = useState("");
  const [newProviderType, setNewProviderType] = useState("Email Enrichment");
  const [newProviderKey, setNewProviderKey] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [showGlobalResults, setShowGlobalResults] = useState(false);
  const [liveAgencies, setLiveAgencies] = useState(null);
  const [liveOrgs, setLiveOrgs] = useState(null);
  const [liveTickets, setLiveTickets] = useState(null);
  const [liveUsers, setLiveUsers] = useState(null);
  const [userSort, setUserSort] = useState({ col: 'created_at', dir: 'desc' });
  const [liveErrors, setLiveErrors] = useState(null);
  const [liveFlags, setLiveFlags] = useState(null);
  const [livePrompts, setLivePrompts] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptForm, setPromptForm] = useState({ name: "", slug: "", module: "messaging_copy", system_prompt: "", user_prompt_template: "", model: "claude-sonnet-4-5-20250929", temperature: 0.7, max_tokens: 4096 });
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptError, setPromptError] = useState(null);
  const [showAddPrompt, setShowAddPrompt] = useState(false);
  const [deckPromptFiles, setDeckPromptFiles] = useState(null);
  const [selectedDeckPromptFileId, setSelectedDeckPromptFileId] = useState(null);
  const [deckPromptDrafts, setDeckPromptDrafts] = useState({});
  const [deckPromptSaving, setDeckPromptSaving] = useState(null);
  const [deckPromptReloading, setDeckPromptReloading] = useState(false);
  const [deckPromptError, setDeckPromptError] = useState(null);
  const [deckPromptStatus, setDeckPromptStatus] = useState(null);
  const [personalisationPrompts, setPersonalisationPrompts] = useState(null);
  const [personalisationDefaults, setPersonalisationDefaults] = useState({});
  const [activePromptKey, setActivePromptKey] = useState('default');
  const [ppEditing, setPpEditing] = useState(null);
  const [ppForm, setPpForm] = useState({ label: "", text: "" });
  const [ppSaving, setPpSaving] = useState(false);
  const [ppError, setPpError] = useState(null);
  const [liveCreditCosts, setLiveCreditCosts] = useState(null);
  const [creditCostEdits, setCreditCostEdits] = useState({});
  const [creditCostSaving, setCreditCostSaving] = useState(null);
  const [leadSearchOrderOrgId, setLeadSearchOrderOrgId] = useState("");
  const [leadSearchOrder, setLeadSearchOrder] = useState({ leadSearch: [], leadEnrichment: [] });
  const [leadOrderLoading, setLeadOrderLoading] = useState(false);
  const [leadOrderSaving, setLeadOrderSaving] = useState(false);
  const [orgIntegrationsOrgId, setOrgIntegrationsOrgId] = useState("");
  const [orgIntegrationsStatus, setOrgIntegrationsStatus] = useState({});
  const [adminConfigModal, setAdminConfigModal] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [maintenanceForm, setMaintenanceForm] = useState({ message: "", eta: "" });
  const [stripeConfig, setStripeConfig] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeMessage, setStripeMessage] = useState('');
  const [webhookDeploying, setWebhookDeploying] = useState(false);
  const [webhookMessage, setWebhookMessage] = useState('');
  const [detectedUrls, setDetectedUrls] = useState({ test: null, live: null });
  const [stripeVerify, setStripeVerify] = useState(null);
  const [stripeVerifying, setStripeVerifying] = useState(false);
  const [signupAccessToken, setSignupAccessToken] = useState("");
  const [signupAccessTokenSource, setSignupAccessTokenSource] = useState("fallback");
  const [signupAccessTokenStatus, setSignupAccessTokenStatus] = useState(null);
  const [signupAccessTokenError, setSignupAccessTokenError] = useState(null);
  const [signupAccessTokenLoading, setSignupAccessTokenLoading] = useState(false);
  const [signupAccessTokenSaving, setSignupAccessTokenSaving] = useState(false);
  const [showSignupAccessToken, setShowSignupAccessToken] = useState(false);
  const [enrichmentFallback, setEnrichmentFallback] = useState(null);
  const [enrichmentFallbackForm, setEnrichmentFallbackForm] = useState({ fallbackMessage: "", fallbackSubject: "" });
  const [enrichmentFallbackSaving, setEnrichmentFallbackSaving] = useState(false);
  const [enrichmentFallbackError, setEnrichmentFallbackError] = useState(null);
  const [enrichmentFallbackStatus, setEnrichmentFallbackStatus] = useState(null);

  // Refetch full org row (with computed seats/projects/leads/integrations/mrr/health_score)
  // whenever an org is selected. The list endpoint returns a stripped row; the detail
  // endpoint returns the row + computed stats.
  useEffect(() => {
    const id = selectedOrg?.id;
    if (!id || selectedOrg.__hydrated) return;
    api.admin.organisations.get(id).then(full => {
      if (full && full.id === id) setSelectedOrg({ ...full, __hydrated: true });
    }).catch(() => {});
  }, [selectedOrg?.id]);

  // Fetch decks lazily when the Decks tab is opened (or when a different org is selected while Decks is active).
  useEffect(() => {
    const id = selectedOrg?.id;
    if (!id || detailTab !== "decks") return;
    setOrgDecksLoading(true);
    setOrgDecksError(null);
    api.admin.organisations.getDecks(id).then(rows => {
      setOrgDecks(Array.isArray(rows) ? rows : []);
    }).catch(err => {
      setOrgDecksError(err?.message || "Failed to load decks");
      setOrgDecks([]);
    }).finally(() => setOrgDecksLoading(false));
  }, [selectedOrg?.id, detailTab]);

  useEffect(() => {
    api.admin.agencies.list().then(d => setLiveAgencies(Array.isArray(d) ? d : d.agencies || null)).catch(() => {});
    api.admin.organisations.list().then(d => setLiveOrgs(Array.isArray(d) ? d : d.organisations || null)).catch(() => {});
    api.admin.supportTickets.list().then(d => setLiveTickets(Array.isArray(d) ? d : d.tickets || null)).catch(() => {});
    api.admin.users.list().then(d => setLiveUsers(Array.isArray(d) ? d : d.users || null)).catch(() => {});
    api.admin.errors.list().then(d => setLiveErrors(Array.isArray(d) ? d : d.errors || null)).catch(() => {});
    api.admin.featureFlags.list().then(d => setLiveFlags(Array.isArray(d) ? d : d.feature_flags || null)).catch(() => {});
    api.admin.creditCosts?.list?.().then(d => setLiveCreditCosts(Array.isArray(d) ? d : [])).catch(() => setLiveCreditCosts([]));
    loadPrompts();
    loadDeckPromptFiles();
    loadPersonalisationPrompts();
    loadStripeConfig();
    loadDetectedUrls();
    loadSignupAccessToken();
    loadEnrichmentFallback();
    loadMaintenance();
  }, []);

  const loadMaintenance = async () => {
    setMaintenanceLoading(true);
    try {
      const m = await api.admin.maintenance.get();
      setMaintenance(m);
      setMaintenanceForm({ message: m?.message || "", eta: m?.eta || "" });
    } catch (e) {
      setMaintenanceMsg(e?.message || "Failed to load maintenance state");
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const saveMaintenance = async (enabled) => {
    setMaintenanceSaving(true);
    setMaintenanceMsg("");
    try {
      const m = await api.admin.maintenance.set({ enabled, message: maintenanceForm.message, eta: maintenanceForm.eta });
      setMaintenance((prev) => ({ ...(prev || {}), ...m, enabled: m.enabled }));
      setMaintenanceMsg(enabled ? "Maintenance mode is ON — non-admin users are locked out." : "Maintenance mode is OFF.");
      setTimeout(() => setMaintenanceMsg(""), 6000);
    } catch (e) {
      setMaintenanceMsg(e?.message || "Failed to update maintenance mode");
    } finally {
      setMaintenanceSaving(false);
    }
  };

  const loadPrompts = () => {
    api.admin.systemPrompts.list().then(d => {
      setLivePrompts(Array.isArray(d) ? d : []);
      // Auto-seed if empty
      if (Array.isArray(d) && d.length === 0) {
        api.admin.systemPrompts.seed().then(() => {
          api.admin.systemPrompts.list().then(d2 => setLivePrompts(Array.isArray(d2) ? d2 : []));
        }).catch(() => {});
      }
    }).catch(() => {});
  };

  const loadPersonalisationPrompts = () => {
    Promise.all([api.prompts.list(), api.prompts.getDefaults(), api.prompts.getActive()])
      .then(([custom, defaults, active]) => {
        setPersonalisationPrompts(Array.isArray(custom) ? custom : []);
        setPersonalisationDefaults(defaults && typeof defaults === 'object' ? defaults : {});
        if (active?.key) setActivePromptKey(active.key);
      })
      .catch(() => { setPersonalisationPrompts([]); setPersonalisationDefaults({}); });
  };

  const loadEnrichmentFallback = () => {
    api.admin.enrichmentFallback.get()
      .then((d) => {
        setEnrichmentFallback(d || null);
        setEnrichmentFallbackForm({
          fallbackMessage: typeof d?.fallbackMessage === 'string' ? d.fallbackMessage : "",
          fallbackSubject: typeof d?.fallbackSubject === 'string' ? d.fallbackSubject : "",
        });
        setEnrichmentFallbackError(null);
      })
      .catch((err) => {
        setEnrichmentFallback({ errorCount: 0, lastErrorAt: null, recentEvents: [] });
        setEnrichmentFallbackError(err?.message || "Failed to load enrichment fallback");
      });
  };

  const saveEnrichmentFallback = async () => {
    setEnrichmentFallbackSaving(true);
    setEnrichmentFallbackError(null);
    setEnrichmentFallbackStatus(null);
    try {
      await api.admin.enrichmentFallback.update({
        fallbackMessage: enrichmentFallbackForm.fallbackMessage,
        fallbackSubject: enrichmentFallbackForm.fallbackSubject,
      });
      setEnrichmentFallbackStatus("Saved.");
      loadEnrichmentFallback();
    } catch (err) {
      setEnrichmentFallbackError(err?.message || "Save failed");
    } finally {
      setEnrichmentFallbackSaving(false);
    }
  };

  const resetEnrichmentFallbackEvents = async () => {
    if (!confirm("Clear the enrichment fallback event log? The counter resets to 0.")) return;
    setEnrichmentFallbackError(null);
    setEnrichmentFallbackStatus(null);
    try {
      await api.admin.enrichmentFallback.resetEvents();
      setEnrichmentFallbackStatus("Event log cleared.");
      loadEnrichmentFallback();
    } catch (err) {
      setEnrichmentFallbackError(err?.message || "Reset failed");
    }
  };

  const loadDeckPromptFiles = ({ resetDrafts = false } = {}) => {
    return api.admin.deckPromptFiles.list().then((files) => {
      const nextFiles = Array.isArray(files) ? files : [];
      setDeckPromptFiles(nextFiles);
      setSelectedDeckPromptFileId((current) => {
        if (current && nextFiles.some((file) => file.id === current)) return current;
        return nextFiles[0]?.id || null;
      });
      setDeckPromptDrafts((current) => {
        const nextDrafts = resetDrafts ? {} : { ...current };
        nextFiles.forEach((file) => {
          if (resetDrafts || nextDrafts[file.id] === undefined) {
            nextDrafts[file.id] = file.content || "";
          }
        });
        return nextDrafts;
      });
      setDeckPromptError(null);
    }).catch((err) => {
      setDeckPromptFiles([]);
      setDeckPromptError(err?.message || "Failed to load deck prompt files");
    });
  };

  const loadStripeConfig = async () => {
    setStripeLoading(true);
    try {
      const token = await api.getToken();
      const res = await fetch('/api/admin/stripe-config', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStripeConfig(await res.json());
    } catch (e) { console.error('Failed to load Stripe config:', e); }
    finally { setStripeLoading(false); }
  };

  const loadDetectedUrls = async () => {
    try {
      const token = await api.getToken();
      const res = await fetch('/api/admin/stripe-webhook/detect-url', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setDetectedUrls(await res.json());
    } catch (e) { console.error('Failed to detect webhook URLs:', e); }
  };

  const loadSignupAccessToken = async () => {
    setSignupAccessTokenLoading(true);
    setSignupAccessTokenError(null);
    try {
      const data = await api.admin.signupAccessToken.get();
      setSignupAccessToken(data?.token || "");
      setSignupAccessTokenSource(data?.source || "fallback");
    } catch (e) {
      setSignupAccessTokenError(e?.message || "Failed to load signup access token");
    } finally {
      setSignupAccessTokenLoading(false);
    }
  };

  const saveSignupAccessToken = async ({ generate = false } = {}) => {
    const nextToken = signupAccessToken.trim();
    if (!generate && !nextToken) {
      setSignupAccessTokenError("Token cannot be empty");
      return;
    }
    setSignupAccessTokenSaving(true);
    setSignupAccessTokenStatus(null);
    setSignupAccessTokenError(null);
    try {
      const data = await api.admin.signupAccessToken.update(generate ? { generate: true } : { token: nextToken });
      setSignupAccessToken(data?.token || "");
      setSignupAccessTokenSource(data?.source || "admin");
      setSignupAccessTokenStatus(generate ? "Signup access token regenerated" : "Signup access token updated");
    } catch (e) {
      setSignupAccessTokenError(e?.message || "Failed to update signup access token");
    } finally {
      setSignupAccessTokenSaving(false);
    }
  };

  const deployWebhook = async (mode) => {
    setWebhookDeploying(true); setWebhookMessage('');
    const webhookUrl = stripeConfig?.[mode]?.webhook_url || detectedUrls[mode];
    if (!webhookUrl) { setWebhookMessage('No webhook URL available'); setWebhookDeploying(false); return; }
    try {
      const token = await api.getToken();
      const res = await fetch('/api/admin/stripe-webhook/deploy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ mode, webhook_url: webhookUrl }),
      });
      const data = await res.json();
      if (res.ok) {
        setWebhookMessage(data.secret_updated ? 'Webhook deployed! Secret auto-saved.' : 'Webhook endpoint updated.');
        await loadStripeConfig();
      } else {
        setWebhookMessage(data.error || 'Deploy failed');
      }
    } catch (e) { setWebhookMessage('Deploy failed'); }
    finally { setWebhookDeploying(false); setTimeout(() => setWebhookMessage(''), 6000); }
  };

  const verifyStripeConnection = async () => {
    setStripeVerifying(true); setStripeVerify(null);
    try {
      const token = await api.getToken();
      const res = await fetch('/api/admin/stripe-config/verify', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setStripeVerify(data);
    } catch (e) { setStripeVerify({ ok: false, error: e.message }); }
    finally { setStripeVerifying(false); }
  };

  const saveStripeConfig = async () => {
    setStripeSaving(true); setStripeMessage('');
    try {
      const token = await api.getToken();
      const res = await fetch('/api/admin/stripe-config', { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(stripeConfig) });
      if (res.ok) { const data = await res.json(); setStripeConfig(data); setStripeMessage('Stripe configuration saved!'); }
      else setStripeMessage('Failed to save configuration');
    } catch (e) { setStripeMessage('Failed to save configuration'); }
    finally { setStripeSaving(false); setTimeout(() => setStripeMessage(''), 3000); }
  };

  const toggleStripeMode = async (mode) => {
    try {
      const token = await api.getToken();
      const res = await fetch('/api/admin/stripe-config/mode', { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ mode }) });
      if (res.ok) setStripeConfig(prev => ({ ...prev, mode }));
    } catch (e) { console.error('Failed to toggle mode:', e); }
  };

  useEffect(() => {
    if (!leadSearchOrderOrgId || page !== "lead_search_order") return;
    setLeadOrderLoading(true);
    api.admin.organisations.getLeadSearchOrder(leadSearchOrderOrgId).then(res => {
      setLeadSearchOrder({
        leadSearch: (res.leadSearch || []).length ? res.leadSearch : LEAD_SEARCH_DEFAULTS,
        leadEnrichment: (res.leadEnrichment || []).length ? res.leadEnrichment : LEAD_ENRICHMENT_DEFAULTS,
      });
    }).catch(() => setLeadSearchOrder({ leadSearch: LEAD_SEARCH_DEFAULTS, leadEnrichment: LEAD_ENRICHMENT_DEFAULTS })).finally(() => setLeadOrderLoading(false));
  }, [leadSearchOrderOrgId, page]);

  useEffect(() => {
    if (!orgIntegrationsOrgId || page !== "integrations") return;
    api.admin.organisations.getIntegrations(orgIntegrationsOrgId).then(res => setOrgIntegrationsStatus(res.integrations || {})).catch(() => setOrgIntegrationsStatus({}));
  }, [orgIntegrationsOrgId, page]);

  const openEditPrompt = (p) => {
    setEditingPrompt(p.id);
    setPromptForm({
      name: p.name || "",
      slug: p.slug || "",
      module: p.module || "messaging_copy",
      system_prompt: p.system_prompt || "",
      user_prompt_template: p.user_prompt_template || "",
      model: p.model || "claude-sonnet-4-5-20250929",
      temperature: p.temperature ?? 0.7,
      max_tokens: p.max_tokens || 4096,
    });
    setPromptError(null);
    setShowAddPrompt(false);
  };

  const openAddPrompt = () => {
    setEditingPrompt(null);
    setPromptForm({ name: "", slug: "", module: "messaging_copy", system_prompt: "", user_prompt_template: "", model: "claude-sonnet-4-5-20250929", temperature: 0.7, max_tokens: 4096 });
    setPromptError(null);
    setShowAddPrompt(true);
  };

  const savePrompt = async () => {
    setPromptSaving(true);
    setPromptError(null);
    try {
      if (editingPrompt) {
        await api.admin.systemPrompts.update(editingPrompt, promptForm);
      } else {
        await api.admin.systemPrompts.create(promptForm);
      }
      setEditingPrompt(null);
      setShowAddPrompt(false);
      loadPrompts();
    } catch (err) {
      setPromptError(err?.message || err?.error || "Failed to save prompt");
    } finally {
      setPromptSaving(false);
    }
  };

  const deletePrompt = async (id) => {
    try {
      await api.admin.systemPrompts.delete(id);
      if (editingPrompt === id) { setEditingPrompt(null); setShowAddPrompt(false); }
      loadPrompts();
    } catch (err) {
      setPromptError(err?.message || "Failed to delete");
    }
  };

  const handleModelChange = async (id, model) => {
    try {
      await api.admin.systemPrompts.update(id, { model });
      loadPrompts();
    } catch (_) {}
  };

  const _DECK_PROMPT_FILES = Array.isArray(deckPromptFiles) ? deckPromptFiles : [];
  const selectedDeckPromptFile = _DECK_PROMPT_FILES.find((file) => file.id === selectedDeckPromptFileId) || _DECK_PROMPT_FILES[0] || null;
  const selectedDeckPromptDraft = selectedDeckPromptFile ? (deckPromptDrafts[selectedDeckPromptFile.id] ?? selectedDeckPromptFile.content ?? "") : "";
  const selectedDeckPromptDirty = !!(selectedDeckPromptFile && selectedDeckPromptDraft !== (selectedDeckPromptFile.content || ""));

  const saveDeckPromptFile = async () => {
    if (!selectedDeckPromptFile) return;
    setDeckPromptSaving(selectedDeckPromptFile.id);
    setDeckPromptError(null);
    setDeckPromptStatus(null);
    try {
      const updated = await api.admin.deckPromptFiles.update(selectedDeckPromptFile.id, { content: selectedDeckPromptDraft });
      setDeckPromptFiles((current) => Array.isArray(current) ? current.map((file) => (file.id === updated.id ? updated : file)) : [updated]);
      setDeckPromptDrafts((current) => ({ ...current, [updated.id]: updated.content || "" }));
      setDeckPromptStatus(`Saved ${updated.fileName}`);
    } catch (err) {
      setDeckPromptError(err?.message || "Failed to save deck prompt file");
    } finally {
      setDeckPromptSaving(null);
    }
  };

  const resetDeckPromptDraft = () => {
    if (!selectedDeckPromptFile) return;
    setDeckPromptDrafts((current) => ({ ...current, [selectedDeckPromptFile.id]: selectedDeckPromptFile.content || "" }));
    setDeckPromptError(null);
    setDeckPromptStatus(null);
  };

  const reloadDeckPromptFiles = async () => {
    setDeckPromptReloading(true);
    setDeckPromptStatus(null);
    try {
      await loadDeckPromptFiles({ resetDrafts: true });
    } finally {
      setDeckPromptReloading(false);
    }
  };

  const setDefaultDeckPromptFile = async (variantId) => {
    if (!variantId) return;
    setDeckPromptSaving(`default:${variantId}`);
    setDeckPromptError(null);
    setDeckPromptStatus(null);
    try {
      const response = await api.admin.deckPromptFiles.setDefault(variantId);
      const nextFiles = Array.isArray(response?.files) ? response.files : [];
      if (nextFiles.length) {
        setDeckPromptFiles(nextFiles);
      } else {
        await loadDeckPromptFiles();
      }
      setDeckPromptStatus("Default deck prompt updated");
    } catch (err) {
      setDeckPromptError(err?.message || "Failed to update default deck prompt");
    } finally {
      setDeckPromptSaving(null);
    }
  };

  const _AGENCIES = liveAgencies || AGENCIES;
  const _ORGS = liveOrgs || ORGS;
  const _TICKETS = liveTickets || TICKETS;
  const _USERS = liveUsers || USERS;

  const inputStyle = { width: "100%", padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, fontFamily: FB, color: C.text, outline: "none", boxSizing: "border-box" };
  const formatDeckPromptTimestamp = (value) => {
    if (!value) return "Unknown";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Unknown";
    return date.toLocaleString("en-GB", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  // ---- OVERVIEW ----
  // Every element below is computed from real DB data loaded via
  // /admin/organisations, /admin/users, /admin/agencies, /admin/support-tickets.
  // Mock MRR/revenue/token-cost/churn-alerts/waterfall have been removed.
  const renderOverview = () => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const toTime = (v) => { if (!v) return 0; const t = new Date(v).getTime(); return Number.isNaN(t) ? 0 : t; };
    const orgCreated = (o) => toTime(o.created_at || o.created);

    // ── Organisation metrics ──────────────────────────────
    const totalOrgs = _ORGS.length;
    const directOrgs = _ORGS.filter(o => !o.agency_id).length;
    const agencyOwnedOrgs = _ORGS.filter(o => !!o.agency_id).length;
    const newOrgs7d = _ORGS.filter(o => orgCreated(o) >= sevenDaysAgo).length;
    const creditsUsed = _ORGS.reduce((s, o) => s + (Number(o.credits_used ?? o.creditsUsed) || 0), 0);
    const creditsAllocated = _ORGS.reduce((s, o) => s + (Number(o.credits_total ?? o.credits_allocated ?? o.creditsTotal) || 0), 0);

    // Plan tier breakdown (real from organisations.plan_tier)
    const planBreakdown = {};
    _ORGS.forEach(o => {
      const key = String(o.plan_tier || o.plan || 'unknown').toLowerCase();
      planBreakdown[key] = (planBreakdown[key] || 0) + 1;
    });
    const planEntries = Object.entries(planBreakdown).sort((a, b) => b[1] - a[1]);
    const planColor = (k) => ({
      free: C.textMuted, trialing: C.warn, trial: C.warn, starter: "#888",
      growth: C.accent, scale: C.purple, enterprise: "#2dd4a8",
    }[k] || C.accent);

    // ── User metrics ──────────────────────────────────────
    const isAdminRole = (r) => { const s = String(r || '').toLowerCase(); return s === 'platform_admin' || s === 'admin'; };
    const totalUsers = _USERS.length;
    const adminCount = _USERS.filter(u => isAdminRole(u.role)).length;
    const suspendedCount = _USERS.filter(u => u.is_suspended).length;
    const activeUsers7d = _USERS.filter(u => toTime(u.last_login_at) >= sevenDaysAgo).length;
    const newUsers7d = _USERS.filter(u => toTime(u.created_at) >= sevenDaysAgo).length;

    // ── Ticket metrics ────────────────────────────────────
    const openTickets = _TICKETS.filter(t => String(t.status || '').toLowerCase() === 'open').length;
    const ticketsSorted = _TICKETS.slice().sort((a, b) => toTime(b.created_at || b.created) - toTime(a.created_at || a.created)).slice(0, 5);

    // ── Recent signups (orgs) ─────────────────────────────
    const recentOrgs = _ORGS.slice().sort((a, b) => orgCreated(b) - orgCreated(a)).slice(0, 5);

    const fmtDate = (v) => {
      const t = toTime(v);
      if (!t) return '—';
      return new Date(t).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const fmtRelative = (v) => {
      const t = toTime(v);
      if (!t) return '—';
      const diff = now - t;
      const h = Math.floor(diff / 3600000);
      if (h < 1) return 'just now';
      if (h < 24) return `${h}h ago`;
      const d = Math.floor(h / 24);
      if (d < 30) return `${d}d ago`;
      return new Date(t).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    };

    const creditsPct = creditsAllocated > 0 ? Math.round((creditsUsed / creditsAllocated) * 100) : 0;

    return (
      <div>
        {/* Row 1 — Organisation + user totals */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <StatCard
            label="Total Organisations"
            value={totalOrgs}
            icon="🏢"
            sub={`${directOrgs} direct · ${agencyOwnedOrgs} agency-owned`}
          />
          <StatCard
            label="Total Users"
            value={totalUsers}
            icon="👥"
            sub={`${adminCount} admin${adminCount === 1 ? '' : 's'} · ${suspendedCount} suspended`}
            color={C.accent}
          />
          <StatCard
            label="Active Users (7d)"
            value={activeUsers7d}
            icon="✅"
            sub={totalUsers > 0 ? `${Math.round((activeUsers7d / totalUsers) * 100)}% of all users` : "No users yet"}
            color={C.green}
          />
          <StatCard
            label="Open Tickets"
            value={openTickets}
            icon="🎧"
            sub={`${_TICKETS.length} total`}
            color={openTickets > 0 ? C.warn : C.text}
          />
        </div>

        {/* Row 2 — Growth + credits */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <StatCard
            label="New Orgs (7d)"
            value={newOrgs7d}
            icon="🆕"
            sub="Signups in the last 7 days"
            color={newOrgs7d > 0 ? C.green : C.text}
          />
          <StatCard
            label="New Users (7d)"
            value={newUsers7d}
            icon="👤"
            sub="User accounts created"
            color={newUsers7d > 0 ? C.green : C.text}
          />
          <StatCard
            label="Credits Consumed"
            value={creditsUsed.toLocaleString()}
            icon="🪙"
            sub={`${creditsPct}% of ${creditsAllocated.toLocaleString()} allocated`}
            color={C.accent}
          />
          <StatCard
            label="Suspended Users"
            value={suspendedCount}
            icon="🚫"
            sub={totalUsers > 0 ? `${Math.round((suspendedCount / totalUsers) * 100)}% of total` : ""}
            color={suspendedCount > 0 ? C.danger : C.text}
          />
        </div>

        {/* Plan Tier Breakdown + Credits Usage */}
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16, marginBottom: 20 }}>
          <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontFamily: F, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em", marginBottom: 16 }}>ORGANISATIONS BY PLAN TIER</div>
            {planEntries.length === 0 && <div style={{ fontSize: 12, color: C.textDim }}>No organisations yet</div>}
            {planEntries.map(([key, count]) => {
              const pct = totalOrgs > 0 ? (count / totalOrgs) * 100 : 0;
              const col = planColor(key);
              const label = key.charAt(0).toUpperCase() + key.slice(1);
              return (
                <div key={key} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: col }}>{label}
                      <span style={{ color: C.textDim, fontWeight: 400, marginLeft: 6 }}>({count} org{count === 1 ? '' : 's'})</span>
                    </span>
                    <span style={{ fontSize: 12, fontFamily: F, fontWeight: 700, color: C.text }}>{pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: C.bg, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: col, boxShadow: `0 0 8px ${col}55` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <div style={{ fontSize: 11, fontFamily: F, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em", marginBottom: 16 }}>CREDIT CONSUMPTION</div>
            <div style={{ fontSize: 32, fontFamily: F, fontWeight: 800, color: C.accent, marginBottom: 4 }}>{creditsPct}%</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 14 }}>
              {creditsUsed.toLocaleString()} used · {creditsAllocated.toLocaleString()} allocated
            </div>
            <div style={{ height: 10, borderRadius: 5, background: C.bg, overflow: "hidden" }}>
              <div style={{
                width: `${Math.min(creditsPct, 100)}%`, height: "100%", borderRadius: 5,
                background: creditsPct > 80 ? C.warn : creditsPct > 95 ? C.danger : C.accent,
                boxShadow: `0 0 10px ${(creditsPct > 80 ? C.warn : C.accent)}66`,
              }} />
            </div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 14, lineHeight: 1.5 }}>
              Aggregated across all {totalOrgs} organisations from <code style={{ fontFamily: F, color: C.textMuted }}>organisations.credits_used</code> / <code style={{ fontFamily: F, color: C.textMuted }}>credits_total</code>.
            </div>
          </div>
        </div>

        {/* Recent Signups + Recent Tickets */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, fontFamily: F, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em" }}>RECENT SIGNUPS</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{recentOrgs.length} shown</span>
            </div>
            {recentOrgs.length === 0 && (
              <div style={{ padding: "20px", fontSize: 12, color: C.textDim, textAlign: "center" }}>No organisations yet</div>
            )}
            {recentOrgs.map(o => {
              const tier = String(o.plan_tier || o.plan || '').toLowerCase();
              const col = planColor(tier);
              return (
                <div key={o.id} onClick={() => { setSelectedOrg(o); setPage("orgs"); }} style={{
                  padding: "12px 20px", display: "flex", alignItems: "center", gap: 12,
                  borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                }}
                  onMouseEnter={e => e.currentTarget.style.background = C.surfaceHover}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ width: 32, height: 32, borderRadius: 7, background: C.accent + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontFamily: F, fontWeight: 800, color: C.accent, flexShrink: 0 }}>
                    {String(o.name || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name || '(unnamed)'}</div>
                    <div style={{ fontSize: 10, color: C.textDim }}>{fmtDate(o.created_at || o.created)}</div>
                  </div>
                  {tier && (
                    <span style={{
                      padding: "3px 10px", borderRadius: 4, fontSize: 9, fontFamily: F, fontWeight: 700,
                      background: col + "1f", color: col, border: `1px solid ${col}55`,
                      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
                    }}>{tier}</span>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, fontFamily: F, fontWeight: 700, color: C.textMuted, letterSpacing: "0.08em" }}>SUPPORT TICKETS</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{openTickets} open / {_TICKETS.length} total</span>
            </div>
            {ticketsSorted.length === 0 && (
              <div style={{ padding: "20px", fontSize: 12, color: C.textDim, textAlign: "center" }}>No support tickets</div>
            )}
            {ticketsSorted.map(t => {
              const status = String(t.status || 'open').toLowerCase();
              const statusCol = status === 'open' ? C.warn : status === 'resolved' ? C.green : C.textMuted;
              return (
                <div key={t.id} style={{ padding: "12px 20px", display: "flex", alignItems: "center", gap: 12, borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div>
                    <div style={{ fontSize: 10, color: C.textDim }}>{fmtRelative(t.created_at || t.created)}</div>
                  </div>
                  <span style={{
                    padding: "3px 10px", borderRadius: 4, fontSize: 9, fontFamily: F, fontWeight: 700,
                    background: statusCol + "1f", color: statusCol, border: `1px solid ${statusCol}55`,
                    letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
                  }}>{status}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ---- AGENCIES ----
  const renderAgencies = () => {
    // Normalize the live-fallback rows into the shape the template expects.
    // The backend returns a subset of the mock fields — default everything
    // missing to 0 / "" so .toLocaleString() / split() never hit undefined.
    const toNum = (...xs) => { for (const x of xs) { const n = Number(x); if (Number.isFinite(n)) return n; } return 0; };
    const AGENCIES = (_AGENCIES || []).map(a => ({
      id: a.id,
      name: a.name || "(unnamed)",
      owner: a.owner || a.owner_name || "",
      status: a.status || "active",
      domain: a.domain || null,
      plan: a.plan || "",
      clients: toNum(a.clients),
      clientSlots: toNum(a.clientSlots, a.client_slots),
      platformFee: toNum(a.platformFee, a.platform_fee),
      workspaceFees: toNum(a.workspaceFees, a.workspace_fees),
      mrr: toNum(a.mrr),
      creditPool: toNum(a.creditPool, a.credit_pool),
      creditPoolUsed: toNum(a.creditPoolUsed, a.credit_pool_used),
    }));
    const ORGS = (_ORGS || []).map(o => ({
      id: o.id,
      name: o.name || "(unnamed)",
      email: o.email || "",
      agency_id: o.agency_id ?? null,
      agency: o.agency || "",
      plan: o.plan || "",
      lastActive: o.lastActive || o.last_active || "",
    }));
    return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Active Agencies" value={AGENCIES.filter(a => a.status === "Active").length} icon="🏛️" color={C.green} />
        <StatCard label="Agency MRR" value={`$${AGENCIES.reduce((s, a) => s + a.platformFee + a.workspaceFees, 0).toLocaleString()}`} icon="💵" sub="Platform + workspace fees" />
        <StatCard label="Agency Clients" value={AGENCIES.reduce((s, a) => s + a.clients, 0)} icon="🏢" sub={`across ${AGENCIES.length} agencies`} />
        <StatCard label="Agency Credit Pools" value={AGENCIES.reduce((s, a) => s + (a.creditPool || 0), 0).toLocaleString()} icon="🪙" sub={`${Math.round((AGENCIES.reduce((s, a) => s + (a.creditPoolUsed || 0), 0) / Math.max(AGENCIES.reduce((s, a) => s + (a.creditPool || 0), 0), 1)) * 100)}% consumed`} />
      </div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 1fr 1fr 0.8fr 0.8fr", padding: "10px 18px", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          {["Agency", "Owner", "Status", "Plan", "Clients", "Revenue", "Credit Pool", ""].map(h => (
            <span key={h} style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.04em" }}>{h.toUpperCase()}</span>
          ))}
        </div>
        {AGENCIES.map(a => (
          <div key={a.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 1fr 1fr 0.8fr 0.8fr", padding: "12px 18px", borderBottom: `1px solid ${C.border}`, alignItems: "center", cursor: "pointer" }}
            onMouseEnter={e => e.currentTarget.style.background = C.surfaceHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</div>
              <div style={{ fontSize: 10, color: C.textDim }}>{a.domain || "No custom domain"}</div>
            </div>
            <div style={{ fontSize: 11 }}>{a.owner}</div>
            <Badge text={a.status} />
            <Badge text="agency_pro" />
            <div>
              <span style={{ fontSize: 12, fontFamily: F, fontWeight: 600 }}>{a.clients}</span>
              <span style={{ fontSize: 10, color: C.textDim }}>/{a.clientSlots}</span>
            </div>
            <div>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: C.green }}>${(a.platformFee + a.workspaceFees).toLocaleString()}/mo</div>
              <div style={{ fontSize: 9, color: C.textDim }}>${a.platformFee} plan + ${a.workspaceFees} workspaces</div>
            </div>
            <div>
              <div style={{ fontSize: 11 }}>{a.creditPoolUsed.toLocaleString()}/{a.creditPool.toLocaleString()}</div>
              <div style={{ height: 3, borderRadius: 2, background: C.bg, marginTop: 2, width: 60 }}>
                <div style={{ width: `${(a.creditPoolUsed / Math.max(a.creditPool, 1)) * 100}%`, height: "100%", borderRadius: 2, background: (a.creditPool > 0 && a.creditPoolUsed / a.creditPool > 0.8) ? C.warn : "#2dd4a8" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.accent}33`, borderRadius: 4, color: C.accent, fontFamily: F, fontSize: 8, cursor: "pointer" }}>View</button>
              <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Impersonate</button>
            </div>
          </div>
        ))}
      </div>
      {/* Agency economics breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>AGENCY ECONOMICS</div>
          {AGENCIES.map(a => (
            <div key={a.id} style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{a.name}</span>
                <span style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: C.green }}>${(a.platformFee + a.workspaceFees).toLocaleString()}/mo</span>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <span style={{ fontSize: 9, color: C.textDim }}>Platform fee: ${a.platformFee}</span>
                <span style={{ fontSize: 9, color: C.textDim }}>Workspace fees: ${a.workspaceFees} ({a.clients} × $97)</span>
                <span style={{ fontSize: 9, color: C.textDim }}>Their MRR from clients: ${a.mrr.toLocaleString()}</span>
                <span style={{ fontSize: 9, color: "#2dd4a8" }}>Their margin: {a.mrr > 0 ? Math.round(((a.mrr - a.platformFee - a.workspaceFees) / a.mrr) * 100) : 0}%</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>AGENCY CLIENT WORKSPACES</div>
          <div style={{ fontSize: 11, color: C.textDim, marginBottom: 10 }}>All orgs owned by agencies. Platform can view and impersonate.</div>
          {ORGS.filter(o => o.agency_id).map(o => (
            <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{o.name}</span>
              <span style={{ fontSize: 9, color: "#2dd4a8" }}>{o.agency}</span>
              <Badge text={(o.plan || "").split(" ")[0].toLowerCase() || "—"} />
              <span style={{ fontSize: 10, color: C.textDim }}>{o.lastActive}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
    );
  };

  // ---- ORGANISATIONS ----
  const renderOrgs = () => {
    if (selectedOrg) return renderOrgDetail(selectedOrg);
    // Normalize live rows — see renderAgencies for the same pattern.
    const toNum = (...xs) => { for (const x of xs) { const n = Number(x); if (Number.isFinite(n)) return n; } return 0; };
    const fmtListDate = (v) => { if (!v) return ''; const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };
    const ORGS = (_ORGS || []).map(o => ({
      id: o.id,
      name: o.name || "(unnamed)",
      email: o.email || "",
      owner: o.owner || o.owner_name || "",
      plan: o.plan || o.plan_name || (o.plan_tier ? String(o.plan_tier).replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : "Free"),
      status: o.status || o.subscription_status || "unknown",
      health: toNum(o.health, o.health_score),
      creditsUsed: toNum(o.creditsUsed, o.credits_used),
      creditsTotal: toNum(o.creditsTotal, o.credits_total),
      mrr: toNum(o.mrr),
      seats: toNum(o.seats),
      projects: toNum(o.projects),
      leads: toNum(o.leads),
      integrations: toNum(o.integrations),
      lastActive: o.lastActive || fmtListDate(o.last_active || o.last_login_at),
      agency_id: o.agency_id ?? null,
      agency: o.agency || "",
      any_suspended: o.any_suspended === true,
    }));
    const statuses = ["All", "Active", "Trialling", "Churned", "Suspended"];
    const matchesFilter = (o) => {
      if (orgFilter === "All") return true;
      if (orgFilter === "Active") return o.status === 'active';
      if (orgFilter === "Trialling") return o.status === 'trialing' || o.status === 'trial_expired';
      if (orgFilter === "Churned") return o.status === 'canceled' || o.status === 'unpaid';
      if (orgFilter === "Suspended") return o.any_suspended;
      return true;
    };
    const searchLc = (orgSearch || "").toLowerCase();
    const filtered = ORGS.filter(o => matchesFilter(o) && (searchLc === "" || (o.name || "").toLowerCase().includes(searchLc) || (o.email || "").toLowerCase().includes(searchLc)));
    return (
      <div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
          <input value={orgSearch} onChange={e => setOrgSearch(e.target.value)} placeholder="Search orgs..." style={{ ...inputStyle, width: 260 }} />
          {statuses.map(s => (
            <button key={s} onClick={() => setOrgFilter(s)} style={{ padding: "6px 12px", borderRadius: 6, border: `1px solid ${orgFilter === s ? C.accent + "55" : C.border}`, background: orgFilter === s ? C.accentBg : "transparent", color: orgFilter === s ? C.accent : C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{s}</button>
          ))}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 10, color: C.textDim }}>{filtered.length} organisations</span>
        </div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 0.8fr 1fr 0.8fr 0.8fr 0.8fr", padding: "10px 18px", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
            {["Organisation", "Plan", "Status", "Health", "Credits", "MRR", "Seats", "Last Active"].map(h => (
              <span key={h} style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>{h.toUpperCase()}</span>
            ))}
          </div>
          {filtered.map(o => (
            <div key={o.id} onClick={() => { setSelectedOrg(o); setDetailTab("overview"); }} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 0.8fr 1fr 0.8fr 0.8fr 0.8fr", padding: "12px 18px", borderBottom: `1px solid ${C.border}`, cursor: "pointer", alignItems: "center" }}
              onMouseEnter={e => e.currentTarget.style.background = C.surfaceHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{o.name}</div>
                <div style={{ fontSize: 10, color: C.textDim }}>{o.email}{o.agency_id && <span style={{ marginLeft: 6, color: "#2dd4a8" }}>via {o.agency}</span>}</div>
              </div>
              <Badge text={o.plan} />
              <Badge text={o.status} />
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", border: `2px solid ${o.health > 70 ? C.green : o.health > 40 ? C.warn : C.danger}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 9, fontFamily: F, fontWeight: 700, color: o.health > 70 ? C.green : o.health > 40 ? C.warn : C.danger }}>{o.health}</span>
                </div>
              </div>
              <div>
                <span style={{ fontSize: 12, color: C.text }}>{o.creditsUsed.toLocaleString()}</span>
                <span style={{ fontSize: 10, color: C.textDim }}>/{o.creditsTotal.toLocaleString()}</span>
                <div style={{ height: 3, borderRadius: 2, background: C.bg, marginTop: 4, width: 60 }}>
                  <div style={{ width: `${(o.creditsUsed / Math.max(o.creditsTotal, 1)) * 100}%`, height: "100%", borderRadius: 2, background: (o.creditsTotal > 0 && o.creditsUsed / o.creditsTotal > 0.9) ? C.danger : (o.creditsTotal > 0 && o.creditsUsed / o.creditsTotal > 0.7) ? C.warn : C.accent }} />
                </div>
              </div>
              <span style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: o.mrr > 0 ? C.green : C.textDim }}>${o.mrr}</span>
              <span style={{ fontSize: 12, color: C.textMuted }}>{o.seats}</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{o.lastActive}</span>
            </div>
          ))}
        </div>

        {/* Bulk Operations */}
        <div style={{ marginTop: 16, padding: "18px 22px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>BULK OPERATIONS</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { label: "Add Bonus Credits", desc: "Give credits to a segment", icon: "🪙" },
              { label: "Enable Beta Feature", desc: "Toggle feature for a plan tier", icon: "🧪" },
              { label: "Send Email Blast", desc: "Targeted email to segment", icon: "📧" },
              { label: "Extend All Trials", desc: "Add days to active trials", icon: "⏱️" },
              { label: "Export All Orgs", desc: "Download CSV of org data", icon: "⬇️" },
            ].map((op, i) => (
              <button key={i} style={{ padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, cursor: "pointer", textAlign: "left", flex: "1 1 180px", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent + "44"; }} onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; }}>
                <span style={{ fontSize: 16 }}>{op.icon}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{op.label}</div>
                  <div style={{ fontSize: 9, color: C.textDim }}>{op.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ---- ORG DETAIL ----
  const renderOrgDetail = (raw) => {
    // Real /admin/organisations rows only carry a subset of what the detail
    // template reads (the template was built against the old mock). Fill in
    // safe defaults so toLocaleString/.split/.includes can't throw.
    const toNumSafe = (...xs) => { for (const x of xs) { const n = Number(x); if (Number.isFinite(n)) return n; } return 0; };
    const fmtDateStr = (v) => { if (!v) return ''; const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); };
    const org = {
      id: raw.id,
      name: raw.name || '(unnamed)',
      email: raw.email || '',
      owner: raw.owner || raw.owner_name || '',
      plan: raw.plan || raw.plan_tier || 'free',
      status: raw.status || raw.subscription_status || 'unknown',
      agency: raw.agency || '',
      agency_id: raw.agency_id || null,
      health: toNumSafe(raw.health, raw.health_score),
      mrr: toNumSafe(raw.mrr),
      seats: String(raw.seats ?? raw.seats_used ?? '1'),
      projects: toNumSafe(raw.projects),
      leads: toNumSafe(raw.leads),
      integrations: toNumSafe(raw.integrations),
      creditsUsed: toNumSafe(raw.creditsUsed, raw.credits_used),
      creditsTotal: toNumSafe(raw.creditsTotal, raw.credits_total, raw.credits_allocated),
      created: raw.created || fmtDateStr(raw.created_at),
      lastActive: raw.lastActive || raw.last_active || fmtDateStr(raw.last_login_at),
      trialEndsAt: raw.trial_ends_at || null,
      subscriptionStatus: raw.subscription_status || raw.status || null,
    };
    const trialEndsDate = org.trialEndsAt ? new Date(org.trialEndsAt) : null;
    const trialEndsValid = trialEndsDate && !Number.isNaN(trialEndsDate.getTime());
    const trialDaysRemaining = trialEndsValid ? Math.ceil((trialEndsDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)) : null;
    const trialLabel = !trialEndsValid
      ? (org.subscriptionStatus === 'active' ? 'Subscribed' : 'No trial set')
      : (trialDaysRemaining > 0
        ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left · ends ${fmtDateStr(org.trialEndsAt)}`
        : `Expired ${fmtDateStr(org.trialEndsAt)}`);
    const saveTrial = async (payload) => {
      setTrialSaving(true); setTrialMsg(null);
      try {
        await api.admin.organisations.updateTrial(org.id, payload);
        // Refetch the computed shape so health_score/seats/projects/etc. aren't
        // clobbered by the raw RETURNING * of the PUT (which has health_score=null).
        const full = await api.admin.organisations.get(org.id);
        setLiveOrgs(prev => Array.isArray(prev) ? prev.map(o => o.id === full.id ? { ...o, ...full } : o) : prev);
        setSelectedOrg({ ...full, __hydrated: true });
        setTrialMsg('Trial updated');
        setTimeout(() => setTrialMsg(null), 3000);
      } catch (e) { setTrialMsg(e?.message || 'Failed'); }
      setTrialSaving(false);
    };
    const creditsPctSafe = org.creditsTotal > 0 ? Math.round((org.creditsUsed / org.creditsTotal) * 100) : 0;
    return (
      <div>
        <button onClick={() => setSelectedOrg(null)} style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", marginBottom: 16 }}>← Back to Organisations</button>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          <div style={{ width: 48, height: 48, borderRadius: 10, background: C.accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontFamily: F, fontWeight: 700, color: C.accent }}>{org.name[0]}</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: F }}>{org.name}</div>
            <div style={{ fontSize: 12, color: C.textDim }}>{org.owner} · {org.email}</div>
          </div>
          <div style={{ flex: 1 }} />
          <Badge text={org.status} />
          <Badge text={org.plan} />
          <button
            onClick={async () => {
              if (!window.confirm(`Impersonate a user in "${org.name}"?\n\nYou will see the app exactly as they do, in a 30-minute session. This is logged to the admin audit trail.`)) return;
              try {
                const r = await api.admin.organisations.impersonate(org.id);
                beginImpersonation(r.token);
                window.location.reload();
              } catch (e) {
                window.alert(`Impersonation failed: ${e?.message || 'Unknown error'}`);
              }
            }}
            style={{ padding: "7px 14px", background: "transparent", border: `1px solid ${C.warn}44`, borderRadius: 6, color: C.warn, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}
          >Impersonate</button>
          <button style={{ padding: "7px 14px", background: "transparent", border: `1px solid ${C.danger}44`, borderRadius: 6, color: C.danger, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Suspend</button>
        </div>
        {org.agency_id && (
          <div style={{ padding: "10px 16px", background: "#2dd4a808", border: "1px solid #2dd4a822", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12 }}>🏛️</span>
            <span style={{ fontSize: 11, color: "#2dd4a8" }}>Managed by <strong>{org.agency}</strong></span>
            <span style={{ fontSize: 10, color: C.textDim }}>·</span>
            <span style={{ fontSize: 10, color: C.textDim }}>Agency-owned workspace — billing flows through agency, not direct</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => { setSelectedOrg(null); setPage("agencies"); }} style={{ padding: "4px 10px", background: "transparent", border: "1px solid #2dd4a833", borderRadius: 6, color: "#2dd4a8", fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>View Agency →</button>
          </div>
        )}

        {/* Detail Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 20 }}>
          {["overview", "credits", "features", "decks", "activity", "notes", "danger"].map(t => (
            <button key={t} onClick={() => setDetailTab(t)} style={{ padding: "10px 18px", background: "transparent", border: "none", borderBottom: detailTab === t ? `2px solid ${C.accent}` : "2px solid transparent", color: detailTab === t ? C.accent : C.textMuted, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>{t}</button>
          ))}
        </div>

        {detailTab === "overview" && (
          <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <StatCard label="Health Score" value={org.health} icon={org.health > 70 ? "💚" : org.health > 40 ? "🟡" : "🔴"} color={org.health > 70 ? C.green : org.health > 40 ? C.warn : C.danger} sub={org.health > 70 ? "Healthy" : org.health > 40 ? "At risk" : "Critical"} />
              <StatCard label="MRR" value={`$${org.mrr}`} icon="💵" color={org.mrr > 0 ? C.green : C.textDim} />
              <StatCard label="Seats" value={org.seats} icon="👤" />
              <StatCard label="Projects" value={org.projects} icon="📋" />
              <StatCard label="Total Leads" value={org.leads.toLocaleString()} icon="⚡" />
              <StatCard label="Integrations" value={org.integrations} icon="🔗" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>SUBSCRIPTION</div>
                {[["Plan", org.plan], ["Status", org.status], ["Trial", trialLabel], ["Created", org.created], ["Last Active", org.lastActive], ["Billing Email", org.email]].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 12, color: C.textDim }}>{k}</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                  <button style={{ padding: "6px 12px", background: C.accentBg, border: `1px solid ${C.accent}22`, borderRadius: 6, color: C.accent, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Change Plan</button>
                  <button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Apply Discount</button>
                  <button
                    onClick={() => { setTrialEditorOpen(o => !o); setTrialMsg(null); }}
                    style={{ padding: "6px 12px", background: trialEditorOpen ? C.accentBg : "transparent", border: `1px solid ${trialEditorOpen ? C.accent + '44' : C.border}`, borderRadius: 6, color: trialEditorOpen ? C.accent : C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}
                  >
                    {trialEditorOpen ? 'Close' : 'Manage Trial'}
                  </button>
                </div>
                {trialEditorOpen && (
                  <div style={{ marginTop: 12, padding: 12, background: C.bg, border: `1px solid ${C.accent}33`, borderRadius: 8 }}>
                    <div style={{ fontSize: 10, fontFamily: F, fontWeight: 700, color: C.accent, letterSpacing: "0.06em", marginBottom: 10 }}>MANAGE TRIAL</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, color: C.textDim, fontFamily: F }}>Add days to trial:</span>
                      <input type="number" value={trialAddDays} onChange={e => setTrialAddDays(Number(e.target.value) || 0)} style={{ width: 60, padding: "4px 8px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontFamily: F, fontSize: 11, outline: "none" }} />
                      <button disabled={trialSaving || !trialAddDays} onClick={() => saveTrial({ add_days: trialAddDays })} style={{ padding: "4px 10px", background: C.accent + "15", border: `1px solid ${C.accent}44`, borderRadius: 4, color: C.accent, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: trialSaving ? "not-allowed" : "pointer" }}>Add</button>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, color: C.textDim, fontFamily: F }}>Reset to fresh trial of:</span>
                      <input type="number" value={trialResetDays} onChange={e => setTrialResetDays(Number(e.target.value) || 0)} style={{ width: 60, padding: "4px 8px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontFamily: F, fontSize: 11, outline: "none" }} />
                      <span style={{ fontSize: 10, color: C.textDim }}>days</span>
                      <button disabled={trialSaving || !trialResetDays} onClick={() => saveTrial({ reset_trial_days: trialResetDays })} style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: trialSaving ? "not-allowed" : "pointer" }}>Reset</button>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button disabled={trialSaving} onClick={() => saveTrial({ end_trial: true })} style={{ padding: "4px 10px", background: C.danger + "10", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: trialSaving ? "not-allowed" : "pointer" }}>End trial now</button>
                      {trialMsg && <span style={{ fontSize: 10, color: trialMsg === 'Trial updated' ? C.green : C.danger, fontFamily: F }}>{trialMsg}</span>}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>TEAM MEMBERS</div>
                {[
                  { name: org.owner, role: "Owner", active: org.lastActive },
                  { name: "Team Member 2", role: "Admin", active: "1 day ago" },
                  { name: "Team Member 3", role: "Member", active: "3 days ago" },
                ].slice(0, parseInt(org.seats[0]) || 1).map((m, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: C.accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontFamily: F, fontWeight: 700, color: C.accent }}>{m.name[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{m.name}</div>
                    </div>
                    <Badge text={m.role} />
                    <span style={{ fontSize: 9, color: C.textDim }}>{m.active}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {detailTab === "credits" && (
          <div>
            <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
              <StatCard label="Credits Used" value={org.creditsUsed.toLocaleString()} icon="🪙" sub={`of ${org.creditsTotal.toLocaleString()} allocated`} />
              <StatCard label="Usage Rate" value={`${creditsPctSafe}%`} icon="📊" color={creditsPctSafe > 90 ? C.danger : C.accent} />
              <StatCard label="Days Remaining" value="14" icon="📅" sub="in current billing cycle" />
            </div>
            <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>MANUAL CREDIT ADJUSTMENT</div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 4 }}>AMOUNT (positive to add, negative to remove)</div>
                  <input value={creditAdjustAmount} onChange={e => setCreditAdjustAmount(e.target.value)} placeholder="e.g. 500 or -200" type="number" style={inputStyle} />
                </div>
                <div style={{ flex: 2 }}>
                  <div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 4 }}>REASON</div>
                  <input value={creditAdjustNote} onChange={e => setCreditAdjustNote(e.target.value)} placeholder="e.g. Goodwill credit for service interruption" style={inputStyle} />
                </div>
                <button style={{ padding: "8px 16px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", height: 34 }}>Apply</button>
              </div>
            </div>
            <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>CREDIT HISTORY</div>
              {[
                { action: "Lead Discovery — 150 leads", credits: -150, date: "Feb 14, 10:34am" },
                { action: "AI Audit Analysis — Hodge Insurance", credits: -25, date: "Feb 13, 3:15pm" },
                { action: "Email Verification — 89 emails", credits: -89, date: "Feb 13, 11:20am" },
                { action: "Monthly Allocation", credits: 5000, date: "Feb 1, 12:00am" },
                { action: "Bonus — Beta tester reward", credits: 500, date: "Jan 28, 9:00am" },
              ].map((h, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ flex: 1, fontSize: 12 }}>{h.action}</span>
                  <span style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: h.credits > 0 ? C.green : C.danger, marginRight: 16 }}>{h.credits > 0 ? "+" : ""}{h.credits}</span>
                  <span style={{ fontSize: 10, color: C.textDim }}>{h.date}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {detailTab === "features" && (
          <div>
            <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 16 }}>FEATURE TOGGLES — {org.name.toUpperCase()}</div>
              <div style={{ fontSize: 11, color: C.textDim, marginBottom: 16 }}>Override plan defaults. Toggling a feature ON gives this org access regardless of their plan. Toggling OFF removes access even if their plan includes it.</div>
              {FEATURE_FLAGS.map(ff => {
                const includedInPlan = ff.plans.includes(org.plan);
                const isBeta = ff.key.startsWith("beta_");
                return (
                  <div key={ff.key} style={{ display: "flex", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${C.border}`, gap: 12 }}>
                    <div onClick={e => { const toggle = e.currentTarget; const dot = toggle.querySelector("div"); if (toggle.dataset.on === "true") { toggle.dataset.on = "false"; toggle.style.background = C.border; dot.style.transform = "translateX(0)"; } else { toggle.dataset.on = "true"; toggle.style.background = C.accent; dot.style.transform = "translateX(14px)"; }}} data-on={includedInPlan ? "true" : "false"} style={{ width: 28, height: 14, borderRadius: 7, background: includedInPlan ? C.accent : C.border, position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FFF", position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: includedInPlan ? "translateX(14px)" : "translateX(0)" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{ff.label}</div>
                      <div style={{ fontSize: 11, color: C.textDim }}>{ff.desc}</div>
                    </div>
                    {isBeta && <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 8, fontFamily: F, fontWeight: 600, background: C.warn + "15", color: C.warn, border: `1px solid ${C.warn}22` }}>BETA</span>}
                    {!isBeta && <span style={{ fontSize: 9, color: C.textDim }}>{ff.plans.length > 0 ? `Included in: ${ff.plans.join(", ")}` : "Not in any plan"}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {detailTab === "activity" && (
          <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>RECENT ACTIVITY</div>
            {[
              { action: "Ran lead discovery", detail: "Insurance vertical — 150 leads found", time: "2 hrs ago", icon: "⚡" },
              { action: "Generated AI audit deck", detail: "Hodge Insurance — 24 slides", time: "5 hrs ago", icon: "📊" },
              { action: "Created custom skill", detail: "Competitor Pricing Analyst", time: "1 day ago", icon: "🧩" },
              { action: "Connected integration", detail: "HeyReach — LinkedIn outreach", time: "1 day ago", icon: "🔗" },
              { action: "Invited team member", detail: "sarah@apexadvisory.io — Admin role", time: "2 days ago", icon: "👤" },
              { action: "Upgraded plan", detail: "Starter → Growth", time: "5 days ago", icon: "💳" },
              { action: "Created project", detail: "Hodge Insurance Strategy", time: "7 days ago", icon: "📋" },
              { action: "Completed onboarding", detail: "Brand Voice + Buyer Persona submitted", time: "8 days ago", icon: "✅" },
            ].map((a, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 14, width: 28, textAlign: "center" }}>{a.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{a.action}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>{a.detail}</div>
                </div>
                <span style={{ fontSize: 9, color: C.textDim }}>{a.time}</span>
              </div>
            ))}
          </div>
        )}

        {detailTab === "notes" && (
          <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>INTERNAL NOTES</div>
            <textarea placeholder="Add internal notes about this organisation..." rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, marginBottom: 12 }} />
            <button style={{ padding: "8px 16px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Save Note</button>
            <div style={{ marginTop: 16, padding: "12px 14px", background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Andrew Dunn</span>
                <span style={{ fontSize: 9, color: C.textDim }}>Feb 12, 2026</span>
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>Spoke with {org.owner} about upgrading to Scale. Interested in custom skills and white-label reports. Follow up next week with pricing proposal.</div>
            </div>
          </div>
        )}

        {detailTab === "decks" && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>AUDIT DECKS ({orgDecks?.length ?? 0})</div>
              {orgDecksLoading && <span style={{ fontSize: 10, color: C.textDim }}>Loading...</span>}
            </div>
            {orgDecksError && (
              <div style={{ padding: "10px 14px", background: C.danger + "10", border: `1px solid ${C.danger}33`, borderRadius: 6, fontSize: 11, color: C.danger, marginBottom: 12 }}>{orgDecksError}</div>
            )}
            {!orgDecksLoading && !orgDecksError && orgDecks && orgDecks.length === 0 && (
              <div style={{ padding: 32, textAlign: "center", color: C.textDim, fontSize: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>No decks generated for this organisation yet.</div>
            )}
            {orgDecks && orgDecks.length > 0 && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 0.7fr 0.7fr 1fr 1.4fr", padding: "10px 16px", borderBottom: `1px solid ${C.border}`, background: C.bg, fontSize: 9, fontFamily: F, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em" }}>
                  <span>DECK</span><span>PROJECT</span><span>STATUS</span><span>SLIDES</span><span>CREATED</span><span>ACTIONS</span>
                </div>
                {orgDecks.map((d) => {
                  const dateStr = d.created_at ? new Date(d.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                  const gen = d.generator_used || d.generator_column || '—';
                  return (
                    <div key={d.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 0.7fr 0.7fr 1fr 1.4fr", padding: "12px 16px", borderBottom: `1px solid ${C.border}`, alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{d.title || '(untitled)'}</div>
                        <div style={{ fontSize: 9, color: C.textDim, marginTop: 2 }}>gen: {gen} · id: {String(d.id).slice(0, 8)}…</div>
                      </div>
                      <span style={{ fontSize: 11, color: C.textMuted }}>{d.project_name || '—'}</span>
                      <Badge text={d.status || 'unknown'} />
                      <span style={{ fontSize: 11, color: C.textMuted }}>{d.slide_count ?? '—'}</span>
                      <span style={{ fontSize: 10, color: C.textDim }}>{dateStr}</span>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {(d.has_preview || d.has_slide_data) && (
                          <button
                            onClick={async () => {
                              setViewerLoading(true);
                              setViewerError(null);
                              setViewerSlideIdx(0);
                              setViewerDeck({ id: d.id, title: d.title, __loading: true });
                              try {
                                const full = await api.admin.auditDecks.get(d.id);
                                setViewerDeck(full);
                              } catch (err) {
                                setViewerError(err?.message || "Failed to load deck");
                                setViewerDeck({ id: d.id, title: d.title, __error: true });
                              } finally {
                                setViewerLoading(false);
                              }
                            }}
                            style={{ padding: "4px 10px", background: C.accent, border: "none", borderRadius: 4, color: C.bg, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: "pointer" }}
                          >View</button>
                        )}
                        {d.pptx_url && (
                          <a href={d.pptx_url} target="_blank" rel="noopener noreferrer" title="Editable copy — Manus's shape-flattened export. Some formatting is lost vs the native Manus view." style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 700, textDecoration: "none" }}>.pptx (lossy)</a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {detailTab === "danger" && (
          <div>
            <div style={{ padding: "16px 20px", background: C.dangerBg, border: `1px solid ${C.danger}22`, borderRadius: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.danger, letterSpacing: "0.06em", marginBottom: 4 }}>⚠️ DANGER ZONE</div>
              <div style={{ fontSize: 11, color: C.textDim }}>These actions are destructive and may not be reversible. Proceed with caution.</div>
            </div>
            {[
              { label: "Export Organisation Data", desc: "Download all org data as JSON: projects, leads, campaigns, settings, team info. Useful before deletion or for data portability requests.", button: "Export Data", color: C.accent, icon: "⬇️" },
              { label: "Cancel Subscription", desc: "Immediately cancels billing. Org retains read-only access until end of current billing cycle. Credits are not refunded.", button: "Cancel Subscription", color: C.warn, icon: "💳" },
              { label: "Reset Organisation", desc: "Deletes all projects, leads, campaigns, and generated content. Keeps account, plan, and team intact. Cannot be undone.", button: "Reset All Data", color: C.warn, icon: "🔄" },
              { label: "Transfer Ownership", desc: "Move the Owner role to a different team member. Current owner becomes Admin. Requires the target user to already be a member.", button: "Transfer", color: C.accent, icon: "👤" },
              { label: "Delete Organisation", desc: "Permanently removes this organisation, all users, all data, all projects. Triggers GDPR data deletion. This cannot be undone.", button: "Delete Organisation", color: C.danger, icon: "🗑️" },
            ].map((action, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: C.surface, border: `1px solid ${action.color === C.danger ? C.danger + "22" : C.border}`, borderRadius: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{action.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{action.label}</div>
                  <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.4 }}>{action.desc}</div>
                </div>
                <button style={{ padding: "8px 16px", background: action.color === C.danger ? C.danger + "15" : "transparent", border: `1px solid ${action.color}44`, borderRadius: 6, color: action.color, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{action.button}</button>
              </div>
            ))}
          </div>
        )}

        {viewerDeck && (
          // Manus is the canonical viewer — the deck lives natively as slides.json
          // and Manus renders it in-browser with full fidelity. The .pptx is a
          // lossy shape-flattened export produced by Manus's server-side converter;
          // we offer it as a download only, never as a preview.
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{viewerDeck.title || "Deck Viewer"}</div>
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
                  {viewerDeck.__loading ? "Loading..." : viewerDeck.__error ? (viewerError || "Failed to load") : (viewerDeck.slide_count ? `${viewerDeck.slide_count} slides · ${viewerDeck.status || ""}` : (viewerDeck.status || ""))}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {viewerDeck.pptx_url && (
                  <a href={viewerDeck.pptx_url} target="_blank" rel="noopener noreferrer" title="Editable copy — Manus's shape-flattened conversion. Some formatting fidelity is lost vs the native view above." style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 11, fontWeight: 600, textDecoration: "none" }}>Download .pptx (lossy)</a>
                )}
                <button onClick={() => { setViewerDeck(null); setViewerError(null); }} style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 11, cursor: "pointer" }}>Close</button>
              </div>
            </div>
            {(() => {
              const shots = Array.isArray(viewerDeck.slide_screenshots_json) ? viewerDeck.slide_screenshots_json : [];
              const shotIdx = Math.min(viewerSlideIdx, Math.max(0, shots.length - 1));
              const shot = shots[shotIdx];
              return (
                <>
                  {shots.length === 0 && viewerDeck.manus_task_url && (
                    <div style={{ padding: "8px 20px", background: C.surface, borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.textDim, lineHeight: 1.4 }}>
                      <span style={{ fontWeight: 600, color: C.textMuted }}>Preview blank?</span> Your browser may be blocking third-party scripts (tracking prevention, ad blockers, or strict privacy mode). Try refreshing, or <b>Download .pptx (lossy)</b> for offline viewing.
                    </div>
                  )}
                  <div style={{ flex: 1, background: "#000", position: "relative", minHeight: 0, display: "flex" }}>
                    {viewerDeck.__loading && (
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 13 }}>Loading deck…</div>
                    )}
                    {!viewerDeck.__loading && shots.length > 0 && shot && (
                      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(0, 1fr) 148px", gap: 12, padding: 16, minHeight: 0 }}>
                        <div style={{ display: "flex", flexDirection: "column", minHeight: 0, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", background: C.bg }}>
                          <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#0b1220" }}>
                            <img src={shot.image_url} alt={shot.title || `Slide ${shotIdx + 1}`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                          </div>
                          <div style={{ padding: "10px 14px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ minWidth: 0, fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{shot.title || `Slide ${shotIdx + 1}`}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                              <button onClick={() => setViewerSlideIdx(Math.max(0, shotIdx - 1))} disabled={shotIdx <= 0} style={{ padding: "5px 11px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: shotIdx <= 0 ? C.textDim : C.text, fontFamily: F, fontSize: 11, cursor: shotIdx <= 0 ? "default" : "pointer" }}>↑ Prev</button>
                              <span style={{ fontSize: 11, color: C.textDim, minWidth: 44, textAlign: "center" }}>{shotIdx + 1} / {shots.length}</span>
                              <button onClick={() => setViewerSlideIdx(Math.min(shots.length - 1, shotIdx + 1))} disabled={shotIdx >= shots.length - 1} style={{ padding: "5px 11px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: shotIdx >= shots.length - 1 ? C.textDim : C.text, fontFamily: F, fontSize: 11, cursor: shotIdx >= shots.length - 1 ? "default" : "pointer" }}>Next ↓</button>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, overflow: "auto", paddingRight: 2 }}>
                          {shots.map((s, i) => (
                            <button key={s.id || i} onClick={() => setViewerSlideIdx(i)} style={{ border: `1px solid ${i === shotIdx ? C.accent : C.border}`, borderRadius: 8, background: i === shotIdx ? C.accentBg : C.surface, padding: 6, cursor: "pointer", textAlign: "left" }}>
                              <img src={s.image_url} alt="" loading="lazy" style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", borderRadius: 4, display: "block", background: "#0b1220" }} />
                              <div style={{ marginTop: 4, fontSize: 9, color: C.textDim, lineHeight: 1.3 }}>{i + 1}. {(s.title || "").slice(0, 42)}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {!viewerDeck.__loading && shots.length === 0 && viewerDeck.manus_task_url && (
                      <iframe
                        key={`admin-viewer-manus-${viewerDeck.id}`}
                        title={viewerDeck.title || "Deck viewer"}
                        src={viewerDeck.manus_task_url}
                        style={{ width: "100%", height: "100%", border: "none", background: "#fff" }}
                      />
                    )}
                    {!viewerDeck.__loading && shots.length === 0 && !viewerDeck.manus_task_url && (
                      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, gap: 8, textAlign: "center", padding: 24 }}>
                        <div style={{ fontSize: 48, opacity: 0.6 }}>📊</div>
                        <div>This deck has no embeddable preview.</div>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{viewerDeck.pptx_url ? "Download the .pptx to view it." : "Manus task URL is not available."}</div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const saveCreditCost = async (actionKey, credits) => {
    if (creditCostSaving) return;
    setCreditCostSaving(actionKey);
    try {
      await api.admin.creditCosts.update(actionKey, { credits: parseInt(credits, 10) });
      setLiveCreditCosts(prev => (prev || []).map(c => c.action_key === actionKey ? { ...c, credits: parseInt(credits, 10) } : c));
      setCreditCostEdits(e => { const n = { ...e }; delete n[actionKey]; return n; });
    } catch (err) {
      console.error("Failed to save credit cost:", err);
    } finally {
      setCreditCostSaving(null);
    }
  };

  // ---- CREDITS ----
  const renderCredits = () => {
    const costs = (liveCreditCosts && liveCreditCosts.length > 0) ? liveCreditCosts : CREDIT_COSTS.map(c => ({ action_key: c.action.toLowerCase().replace(/\s+/g, "_"), action_label: c.action, credits: c.credits, unit: c.unit, cost_cents: null }));
    return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Credits Consumed Today" value="2,140" icon="🪙" />
        <StatCard label="This Week" value="12,800" icon="📊" />
        <StatCard label="This Month" value="14,355" icon="📅" sub="of 21,500 total allocated" />
        <StatCard label="Your Cost" value="$18.42" icon="💵" sub="Today's API spend" />
      </div>

      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>CREDIT COST CONFIGURATION</div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>Adjust how many credits each action costs. Changes apply immediately to all orgs.</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 0.5fr", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
          {["Action", "Credits", "Unit", "Cost (cents)", ""].map(h => (
            <span key={h} style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim }}>{h.toUpperCase()}</span>
          ))}
        </div>
        {costs.map((cc, i) => {
          const key = cc.action_key || cc.action?.toLowerCase?.()?.replace(/\s+/g, "_") || `row-${i}`;
          const label = cc.action_label || cc.action || key;
          const creditsVal = creditCostEdits[key] ?? cc.credits ?? 1;
          const isSaving = creditCostSaving === key;
          return (
          <div key={key} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 0.5fr", padding: "10px 0", borderBottom: `1px solid ${C.border}`, alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
            <div>
              <input
                value={creditsVal}
                onChange={e => setCreditCostEdits(prev => ({ ...prev, [key]: e.target.value }))}
                onBlur={() => { if (creditCostEdits[key] != null && String(creditCostEdits[key]) !== String(cc.credits)) saveCreditCost(key, creditCostEdits[key]); }}
                style={{ width: 50, padding: "4px 8px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12, fontFamily: F, color: C.accent, textAlign: "center", outline: "none" }}
              />
            </div>
            <span style={{ fontSize: 11, color: C.textDim }}>{cc.unit || "per unit"}</span>
            <span style={{ fontSize: 11, color: C.textDim }}>{cc.cost_cents != null ? `$${(cc.cost_cents / 100).toFixed(2)}` : "—"}</span>
            <button
              disabled={isSaving || (creditCostEdits[key] == null)}
              onClick={() => saveCreditCost(key, creditsVal)}
              style={{ padding: "3px 8px", background: (creditCostEdits[key] != null && !isSaving) ? C.accent : "transparent", color: (creditCostEdits[key] != null && !isSaving) ? C.bg : C.textDim, border: `1px solid ${C.border}`, borderRadius: 4, fontFamily: F, fontSize: 8, cursor: isSaving ? "wait" : "pointer", justifySelf: "center" }}
            >{isSaving ? "..." : "Save"}</button>
          </div>
        ); })}
      </div>

      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>TOP CREDIT CONSUMERS</div>
        {ORGS.filter(o => o.creditsUsed > 0).sort((a, b) => b.creditsUsed - a.creditsUsed).slice(0, 5).map(o => (
          <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{o.name}</span>
            <Badge text={o.plan} />
            <div style={{ width: 120 }}>
              <div style={{ height: 4, borderRadius: 2, background: C.bg }}>
                <div style={{ width: `${(o.creditsUsed / o.creditsTotal) * 100}%`, height: "100%", borderRadius: 2, background: o.creditsUsed / o.creditsTotal > 0.9 ? C.danger : C.accent }} />
              </div>
            </div>
            <span style={{ fontSize: 11, fontFamily: F, fontWeight: 600, width: 80, textAlign: "right" }}>{o.creditsUsed.toLocaleString()}/{o.creditsTotal.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
  };

  // ---- LEAD GEN CONFIG ----
  const renderLeadGen = () => {
    const VALIDATION_PROVIDERS = [
      { name: "MillionVerifier", status: "Healthy", priority: 1, calls: "8,200", errorRate: "0.1%", avgLatency: "0.8s", bounceRate: "2.1%" },
      { name: "ZeroBounce", status: "Healthy", priority: 2, calls: "3,400", errorRate: "0.3%", avgLatency: "1.4s", bounceRate: "2.4%" },
      { name: "NeverBounce", status: "Healthy", priority: 3, calls: "1,200", errorRate: "0.2%", avgLatency: "1.1s", bounceRate: "2.8%" },
      { name: "Reoon", status: "Degraded", priority: 4, calls: "600", errorRate: "1.8%", avgLatency: "2.9s", bounceRate: "3.2%" },
    ];
    const makeWaterfall = (title, desc, providers, showBounce, waterfallKey) => (
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>{title}</div>
          <button onClick={() => setShowAddProvider(showAddProvider === waterfallKey ? null : waterfallKey)} style={{ padding: "5px 12px", background: showAddProvider === waterfallKey ? C.danger : C.accent, color: showAddProvider === waterfallKey ? "#FFF" : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{showAddProvider === waterfallKey ? "✕ Cancel" : "+ Add Provider"}</button>
        </div>
        <div style={{ fontSize: 11, color: C.textDim, marginBottom: 14 }}>{desc}</div>
        {showAddProvider === waterfallKey && (
          <div style={{ padding: "14px 16px", background: C.accentBg, border: `1px solid ${C.accent}22`, borderRadius: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.accent, marginBottom: 8 }}>ADD NEW PROVIDER</div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>PROVIDER NAME</div><input value={newProviderName} onChange={e => setNewProviderName(e.target.value)} placeholder="e.g. Clearout" style={inputStyle} /></div>
              <div style={{ flex: 1 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>API KEY</div><input value={newProviderKey} onChange={e => setNewProviderKey(e.target.value)} placeholder="sk-..." type="password" style={inputStyle} /></div>
              <div style={{ width: 100 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>PRIORITY</div><select style={{ ...inputStyle, padding: "8px 8px" }}><option>Last</option><option>#1</option><option>#2</option><option>#3</option></select></div>
              <button style={{ padding: "8px 16px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", height: 34, whiteSpace: "nowrap" }}>Add</button>
            </div>
          </div>
        )}
        {providers.map((p, i) => (
          <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontFamily: F, fontWeight: 700, color: C.textDim, width: 20 }}>#{i + 1}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{p.name}</span>
                <Badge text={p.status} />
              </div>
              <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>{p.type || (showBounce ? "Email Validation" : "Email Enrichment")}</div>
            </div>
            <div style={{ textAlign: "center", marginRight: 12 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600 }}>{p.calls}</div>
              <div style={{ fontSize: 8, color: C.textDim }}>CALLS/MO</div>
            </div>
            <div style={{ textAlign: "center", marginRight: 12 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: parseFloat(p.errorRate) > 1 ? C.warn : C.green }}>{p.errorRate}</div>
              <div style={{ fontSize: 8, color: C.textDim }}>ERROR</div>
            </div>
            {showBounce && <div style={{ textAlign: "center", marginRight: 12 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: parseFloat(p.bounceRate) > 3 ? C.warn : C.green }}>{p.bounceRate}</div>
              <div style={{ fontSize: 8, color: C.textDim }}>BOUNCE</div>
            </div>}
            <div style={{ textAlign: "center", marginRight: 8 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600 }}>{p.avgLatency}</div>
              <div style={{ fontSize: 8, color: C.textDim }}>LATENCY</div>
            </div>
            <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.accent}33`, borderRadius: 4, color: C.accent, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Test</button>
            <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Edit</button>
            <div onClick={e => { const el = e.currentTarget; el.dataset.enabled = el.dataset.enabled === "true" ? "false" : "true"; const dot = el.querySelector("div"); if (el.dataset.enabled === "true") { el.style.background = C.accent; dot.style.transform = "translateX(14px)"; } else { el.style.background = C.border; dot.style.transform = "translateX(0)"; }}} data-enabled="true" style={{ width: 28, height: 14, borderRadius: 7, background: C.accent, position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FFF", position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: "translateX(14px)" }} />
            </div>
            <button style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 8, cursor: "pointer" }}>✕</button>
          </div>
        ))}
      </div>
    );
    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <StatCard label="Leads Enriched (MTD)" value="4,820" icon="⚡" />
          <StatCard label="Emails Validated (MTD)" value="4,340" icon="✅" sub="98.2% deliverable" color={C.green} />
          <StatCard label="Enrichment Cost (MTD)" value="$57.84" icon="💵" />
          <StatCard label="Validation Cost (MTD)" value="$34.72" icon="💵" />
        </div>
        {makeWaterfall(
          "WATERFALL 1 — EMAIL ENRICHMENT",
          "Finds email addresses from name + company. Tries Provider 1 first, falls back to Provider 2 if no result, etc.",
          PROVIDERS.filter(p => p.type === "Email Enrichment"),
          false, "enrichment"
        )}
        {makeWaterfall(
          "WATERFALL 2 — EMAIL VALIDATION",
          "Verifies found emails are real and deliverable before outreach. Protects sender reputation.",
          VALIDATION_PROVIDERS,
          true, "validation"
        )}
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>OUTREACH PLATFORM CONNECTIONS</div>
            <button onClick={() => setShowAddProvider(showAddProvider === "outreach" ? null : "outreach")} style={{ padding: "5px 12px", background: showAddProvider === "outreach" ? C.danger : C.accent, color: showAddProvider === "outreach" ? "#FFF" : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{showAddProvider === "outreach" ? "✕ Cancel" : "+ Add Platform"}</button>
          </div>
          <div style={{ fontSize: 11, color: C.textDim, marginBottom: 14 }}>Email and LinkedIn outreach platforms. These receive validated leads from the pipeline.</div>
          {showAddProvider === "outreach" && (
            <div style={{ padding: "14px 16px", background: C.accentBg, border: `1px solid ${C.accent}22`, borderRadius: 8, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>PLATFORM NAME</div><input placeholder="e.g. Lemlist" style={inputStyle} /></div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>TYPE</div><select style={{ ...inputStyle, padding: "8px 8px" }}><option>Email Outreach</option><option>LinkedIn Outreach</option></select></div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>API KEY</div><input placeholder="sk-..." type="password" style={inputStyle} /></div>
                <button style={{ padding: "8px 16px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", height: 34 }}>Add</button>
              </div>
            </div>
          )}
          {PROVIDERS.filter(p => p.type !== "Email Enrichment" && p.type !== "AI Engine").map((p, i) => (
            <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, boxShadow: `0 0 4px ${C.green}66` }} />
              <span style={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{p.name}</span>
              <span style={{ fontSize: 11, color: C.textDim }}>{p.type}</span>
              <span style={{ fontSize: 11, fontFamily: F }}>{p.calls} calls/mo</span>
              <Badge text={p.status} />
              <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Edit</button>
              <button style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 8, cursor: "pointer" }}>✕</button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---- AI & PROMPTS ----
  const _PROMPTS = livePrompts || AI_PROMPTS.map(p => ({ ...p, slug: p.id, system_prompt: '', model: p.model }));

  const renderAI = () => (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="System Prompts" value={_PROMPTS.length} icon="🧠" />
        <StatCard label="Active" value={_PROMPTS.filter(p => p.status !== 'inactive').length} icon="✅" color={C.green} />
        <StatCard label="Models Used" value={[...new Set(_PROMPTS.map(p => p.model))].length} icon="🤖" />
        <StatCard label="Avg Response Time" value="2.4s" icon="⏱️" />
      </div>

      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>DECK GENERATION PROMPT FILES</div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>These two editors write directly to the markdown files used by the deck brief generation flow. They are separate from the database-backed system prompts below.</div>
          </div>
          <button onClick={reloadDeckPromptFiles} disabled={deckPromptReloading} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: deckPromptReloading ? "wait" : "pointer", color: deckPromptReloading ? C.textDim : C.textMuted }}>
            {deckPromptReloading ? "Reloading..." : "Reload From Disk"}
          </button>
        </div>

        {(deckPromptError || deckPromptStatus) && (
          <div style={{ padding: "10px 14px", background: deckPromptError ? C.dangerBg : C.greenBg, border: `1px solid ${(deckPromptError ? C.danger : C.green)}33`, borderRadius: 8, marginBottom: 12, fontSize: 11, color: deckPromptError ? C.danger : C.green }}>
            {deckPromptError || deckPromptStatus}
          </div>
        )}

        {!deckPromptFiles ? (
          <div style={{ padding: 24, textAlign: "center", color: C.textDim, fontSize: 12 }}>Loading deck prompt files...</div>
        ) : _DECK_PROMPT_FILES.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: C.textDim, fontSize: 12 }}>No editable deck prompt files were found.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "280px minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {_DECK_PROMPT_FILES.map((file) => {
                const isSelected = file.id === selectedDeckPromptFile?.id;
                const isDirty = (deckPromptDrafts[file.id] ?? file.content ?? "") !== (file.content || "");
                return (
                  <button
                    key={file.id}
                    onClick={() => {
                      setSelectedDeckPromptFileId(file.id);
                      setDeckPromptError(null);
                      setDeckPromptStatus(null);
                    }}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "14px",
                      background: isSelected ? C.accentBg : C.bg,
                      border: `1px solid ${isSelected ? C.accent + "55" : C.border}`,
                      borderRadius: 10,
                      color: C.text,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{file.name}</div>
                        {file.isDefault && (
                          <span style={{ alignSelf: "flex-start", padding: "2px 8px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accent}33`, color: C.accent, fontFamily: F, fontSize: 8, fontWeight: 700 }}>
                            DEFAULT
                          </span>
                        )}
                      </div>
                      <span style={{ padding: "2px 8px", borderRadius: 999, background: isDirty ? C.warnBg : C.surface, border: `1px solid ${(isDirty ? C.warn : C.border)}33`, color: isDirty ? C.warn : C.textDim, fontFamily: F, fontSize: 8, fontWeight: 700 }}>
                        {isDirty ? "UNSAVED" : "SAVED"}
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: C.textDim, lineHeight: 1.55, marginBottom: 8 }}>{file.description}</div>
                    <div style={{ fontSize: 9, color: C.textDim, fontFamily: F, display: "flex", flexDirection: "column", gap: 4 }}>
                      <span>{file.fileName}</span>
                      <span>Updated {formatDeckPromptTimestamp(file.updatedAt)}</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedDeckPromptFile && (
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{selectedDeckPromptFile.name}</div>
                      {selectedDeckPromptFile.isDefault && (
                        <span style={{ padding: "2px 8px", borderRadius: 999, background: C.accentBg, border: `1px solid ${C.accent}33`, color: C.accent, fontFamily: F, fontSize: 8, fontWeight: 700 }}>
                          DEFAULT
                        </span>
                      )}
                      <span style={{ padding: "2px 8px", borderRadius: 999, background: selectedDeckPromptDirty ? C.warnBg : C.greenBg, border: `1px solid ${(selectedDeckPromptDirty ? C.warn : C.green)}33`, color: selectedDeckPromptDirty ? C.warn : C.green, fontFamily: F, fontSize: 8, fontWeight: 700 }}>
                        {selectedDeckPromptDirty ? "DIRTY" : "IN SYNC"}
                      </span>
                    </div>
                    <div style={{ fontSize: 10, color: C.textDim, fontFamily: F, marginTop: 6 }}>{selectedDeckPromptFile.fileName}</div>
                    <div style={{ fontSize: 11, color: C.textDim, marginTop: 6, lineHeight: 1.55 }}>{selectedDeckPromptFile.description}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    {!selectedDeckPromptFile.isDefault && (
                      <button onClick={() => setDefaultDeckPromptFile(selectedDeckPromptFile.id)} disabled={deckPromptSaving === `default:${selectedDeckPromptFile.id}`} style={{ padding: "7px 12px", background: "transparent", border: `1px solid ${C.accent}55`, borderRadius: 6, color: C.accent, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: deckPromptSaving === `default:${selectedDeckPromptFile.id}` ? "wait" : "pointer" }}>
                        {deckPromptSaving === `default:${selectedDeckPromptFile.id}` ? "Updating..." : "Set As Default"}
                      </button>
                    )}
                    <button onClick={resetDeckPromptDraft} disabled={!selectedDeckPromptDirty || deckPromptSaving === selectedDeckPromptFile.id} style={{ padding: "7px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: !selectedDeckPromptDirty ? C.textDim : C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: !selectedDeckPromptDirty ? "not-allowed" : "pointer" }}>Reset Draft</button>
                    <button onClick={saveDeckPromptFile} disabled={!selectedDeckPromptDirty || deckPromptSaving === selectedDeckPromptFile.id} style={{ padding: "7px 14px", background: !selectedDeckPromptDirty || deckPromptSaving === selectedDeckPromptFile.id ? C.border : C.accent, color: !selectedDeckPromptDirty || deckPromptSaving === selectedDeckPromptFile.id ? C.textDim : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: !selectedDeckPromptDirty ? "not-allowed" : deckPromptSaving === selectedDeckPromptFile.id ? "wait" : "pointer" }}>
                      {deckPromptSaving === selectedDeckPromptFile.id ? "Saving..." : "Save File"}
                    </button>
                  </div>
                </div>

                <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 16, flexWrap: "wrap", fontSize: 10, color: C.textDim, fontFamily: F }}>
                  <span>{selectedDeckPromptDraft.split("\n").length} lines</span>
                  <span>{selectedDeckPromptDraft.length.toLocaleString()} chars</span>
                  <span>{selectedDeckPromptDraft.trim() ? selectedDeckPromptDraft.trim().split(/\s+/).length.toLocaleString() : 0} words</span>
                  <span>Last saved {formatDeckPromptTimestamp(selectedDeckPromptFile.updatedAt)}</span>
                </div>

                <div style={{ padding: 16 }}>
                  <textarea
                    value={selectedDeckPromptDraft}
                    onChange={e => {
                      const nextValue = e.target.value;
                      setDeckPromptDrafts(current => ({ ...current, [selectedDeckPromptFile.id]: nextValue }));
                      setDeckPromptError(null);
                      setDeckPromptStatus(null);
                    }}
                    spellCheck={false}
                    style={{
                      width: "100%",
                      minHeight: 640,
                      resize: "vertical",
                      padding: "18px",
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      color: C.text,
                      fontFamily: F,
                      fontSize: 12,
                      lineHeight: 1.7,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>SYSTEM PROMPTS & MODEL SELECTION</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={async () => { setPromptError(null); try { const r = await api.admin.systemPrompts.sync(); setPromptError(null); loadPrompts(); alert(`Synced ${r.synced} production prompts (${r.total} total)`); } catch (e) { setPromptError(e?.message || 'Sync failed'); } }} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer", color: C.textMuted }}>Sync Production</button>
            <button onClick={openAddPrompt} style={{ padding: "5px 12px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>+ Add Prompt</button>
          </div>
        </div>

        {promptError && (
          <div style={{ padding: "10px 14px", background: C.dangerBg, border: `1px solid ${C.danger}33`, borderRadius: 8, marginBottom: 12, fontSize: 11, color: C.danger }}>{promptError}</div>
        )}

        {/* Add / Edit Form */}
        {(showAddPrompt || editingPrompt) && (
          <div style={{ padding: 18, background: C.bg, border: `1px solid ${C.accent}33`, borderRadius: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.accent, letterSpacing: "0.06em", marginBottom: 12 }}>
              {editingPrompt ? "EDIT SYSTEM PROMPT" : "NEW SYSTEM PROMPT"}
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 10, color: C.textDim, fontFamily: F, display: "block", marginBottom: 4 }}>NAME</label>
                <input value={promptForm.name} onChange={e => setPromptForm({ ...promptForm, name: e.target.value })} placeholder="e.g. AI Council — Strategic Advisor" style={inputStyle} />
              </div>
              <div style={{ width: 180 }}>
                <label style={{ fontSize: 10, color: C.textDim, fontFamily: F, display: "block", marginBottom: 4 }}>MODULE</label>
                <select value={promptForm.module} onChange={e => setPromptForm({ ...promptForm, module: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="audit_analysis">Audit Analysis</option>
                  <option value="call_analysis">Call Analysis</option>
                  <option value="content_post">Content Post</option>
                  <option value="audit_deck">Audit Deck</option>
                  <option value="icp_scoring">ICP Scoring</option>
                  <option value="lead_personalisation">Lead Personalisation</option>
                  <option value="messaging_copy">Messaging Copy</option>
                  <option value="niche_discovery">Niche Discovery</option>
                  <option value="sales_script">Sales Script</option>
                </select>
              </div>
              <div style={{ width: 180 }}>
                <label style={{ fontSize: 10, color: C.textDim, fontFamily: F, display: "block", marginBottom: 4 }}>MODEL</label>
                <select value={promptForm.model} onChange={e => setPromptForm({ ...promptForm, model: e.target.value })} style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="claude-sonnet-4-6">Sonnet 4.6</option>
                  <option value="claude-sonnet-4-5-20250929">Sonnet 4.5</option>
                  <option value="claude-sonnet-4-20250514">Sonnet 4</option>
                  <option value="claude-haiku-4-5-20251001">Haiku 4.5</option>
                  <option value="claude-opus-4-6">Opus 4.6</option>
                  <option value="openrouter/perplexity/sonar-pro">Perplexity Sonar Pro</option>
                </select>
              </div>
              <div style={{ width: 90 }}>
                <label style={{ fontSize: 10, color: C.textDim, fontFamily: F, display: "block", marginBottom: 4 }}>TEMP</label>
                <input type="number" step="0.1" min="0" max="1" value={promptForm.temperature} onChange={e => setPromptForm({ ...promptForm, temperature: parseFloat(e.target.value) || 0 })} style={inputStyle} />
              </div>
              <div style={{ width: 90 }}>
                <label style={{ fontSize: 10, color: C.textDim, fontFamily: F, display: "block", marginBottom: 4 }}>MAX TOKENS</label>
                <input type="number" value={promptForm.max_tokens} onChange={e => setPromptForm({ ...promptForm, max_tokens: parseInt(e.target.value) || 4096 })} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 10, color: C.textDim, fontFamily: F, display: "block", marginBottom: 4 }}>SYSTEM PROMPT</label>
              <textarea value={promptForm.system_prompt} onChange={e => setPromptForm({ ...promptForm, system_prompt: e.target.value })} rows={5} placeholder="You are an expert..." style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 10, color: C.textDim, fontFamily: F, display: "block", marginBottom: 4 }}>USER PROMPT TEMPLATE <span style={{ color: C.textDim, fontWeight: 400 }}>(optional — use {"{{variable}}"} for placeholders)</span></label>
              <textarea value={promptForm.user_prompt_template} onChange={e => setPromptForm({ ...promptForm, user_prompt_template: e.target.value })} rows={3} placeholder="Analyse this: {{transcript}}" style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { setEditingPrompt(null); setShowAddPrompt(false); setPromptError(null); }} style={{ padding: "7px 16px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={savePrompt} disabled={promptSaving || !promptForm.name.trim()} style={{ padding: "7px 20px", background: promptSaving || !promptForm.name.trim() ? C.border : C.accent, color: promptSaving || !promptForm.name.trim() ? C.textDim : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: promptSaving ? "wait" : "pointer" }}>
                {promptSaving ? "Saving..." : editingPrompt ? "Save Changes" : "Create Prompt"}
              </button>
            </div>
          </div>
        )}

        {/* Prompt List */}
        {!livePrompts ? (
          <div style={{ padding: 20, textAlign: "center", color: C.textDim, fontSize: 12 }}>Loading prompts...</div>
        ) : _PROMPTS.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: C.textDim, fontSize: 12 }}>No system prompts yet. Click "+ Add Prompt" or prompts will be auto-seeded.</div>
        ) : (
          _PROMPTS.map(p => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 10, color: C.textDim, marginTop: 2, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {p.module && <span style={{ padding: "1px 6px", borderRadius: 3, background: C.accent + "15", color: C.accent, fontFamily: F, fontSize: 8, fontWeight: 600, textTransform: "uppercase" }}>{p.module.replace(/_/g, ' ')}</span>}
                  {p.slug && <span style={{ fontFamily: F }}>{p.slug}</span>}
                  {p.system_prompt && <span style={{ color: C.textDim }}>{p.system_prompt.length} chars</span>}
                  {p.updated_at && <span>{new Date(p.updated_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}</span>}
                </div>
                {editingPrompt === p.id && <div style={{ fontSize: 9, color: C.accent, fontFamily: F, marginTop: 2 }}>EDITING</div>}
              </div>
              <div style={{ padding: "4px 10px", borderRadius: 4, background: C.bg, border: `1px solid ${C.border}` }}>
                <select value={p.model || "claude-sonnet-4-5-20250929"} onChange={e => handleModelChange(p.id, e.target.value)} style={{ background: "transparent", border: "none", color: C.accent, fontFamily: F, fontSize: 9, fontWeight: 600, outline: "none", cursor: "pointer" }}>
                  <option value="claude-sonnet-4-6">Sonnet 4.6</option>
                  <option value="claude-sonnet-4-5-20250929">Sonnet 4.5</option>
                  <option value="claude-sonnet-4-20250514">Sonnet 4</option>
                  <option value="claude-haiku-4-5-20251001">Haiku 4.5</option>
                  <option value="claude-opus-4-6">Opus 4.6</option>
                  <option value="openrouter/perplexity/sonar-pro">Perplexity Sonar Pro</option>
                </select>
              </div>
              <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, fontFamily: F, fontWeight: 600, background: p.status === 'active' ? C.greenBg : C.warnBg, color: p.status === 'active' ? C.green : C.warn }}>{p.status || 'active'}</span>
              <button onClick={() => openEditPrompt(p)} style={{ padding: "6px 12px", background: editingPrompt === p.id ? C.accent + "15" : "transparent", border: `1px solid ${editingPrompt === p.id ? C.accent + "44" : C.border}`, borderRadius: 6, color: editingPrompt === p.id ? C.accent : C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Edit</button>
              <button onClick={() => deletePrompt(p.id)} style={{ padding: "6px 8px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 6, color: C.danger, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>✕</button>
            </div>
          ))
        )}
      </div>

      {/* PERSONALISATION PROMPTS (messaging_copies value_prop) */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>PERSONALISATION PROMPT TEMPLATES</div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>Activate one prompt for the organisation. Import, enrichment, and personalisation use it server-side; the app shows the active prompt read-only.</div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={loadPersonalisationPrompts} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer", color: C.textMuted }}>Refresh</button>
            <button onClick={() => { setPpEditing("new"); setPpForm({ label: "", text: "" }); setPpError(null); }} style={{ padding: "5px 12px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>+ Add Prompt</button>
          </div>
        </div>

        {ppError && <div style={{ padding: "10px 14px", background: C.dangerBg || "#3a1c1c", border: `1px solid ${C.danger}33`, borderRadius: 8, marginBottom: 12, fontSize: 11, color: C.danger }}>{ppError}</div>}

        {ppEditing && (() => {
          const isDefault = typeof ppEditing === "string" && ppEditing.startsWith("default:");
          return (
          <div style={{ padding: 16, background: C.bg, border: `1px solid ${C.accent}33`, borderRadius: 8, marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.accent, marginBottom: 10 }}>{ppEditing === "new" ? "NEW PROMPT" : isDefault ? "EDIT DEFAULT PROMPT" : "EDIT PROMPT"}</div>
            <input
              placeholder="Prompt label (e.g. Cold Intro)"
              value={ppForm.label}
              onChange={(e) => setPpForm(f => ({ ...f, label: e.target.value }))}
              style={{ width: "100%", padding: "8px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 12, marginBottom: 8, boxSizing: "border-box" }}
            />
            <textarea
              placeholder="Prompt text..."
              value={ppForm.text}
              onChange={(e) => setPpForm(f => ({ ...f, text: e.target.value }))}
              rows={8}
              style={{ width: "100%", padding: "8px 12px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 11, resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button
                disabled={ppSaving || !ppForm.text.trim() || !ppForm.label.trim()}
                onClick={async () => {
                  setPpSaving(true); setPpError(null);
                  try {
                    if (ppEditing === "new") {
                      await api.prompts.create({ label: ppForm.label, text: ppForm.text });
                    } else if (isDefault) {
                      const defaultKey = ppEditing.replace("default:", "");
                      await api.prompts.updateDefault(defaultKey, { text: ppForm.text, label: ppForm.label });
                    } else {
                      await api.prompts.update(ppEditing, { label: ppForm.label, text: ppForm.text });
                    }
                    setPpEditing(null);
                    loadPersonalisationPrompts();
                  } catch (e) { setPpError(e?.message || "Save failed"); }
                  setPpSaving(false);
                }}
                style={{ padding: "6px 16px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: ppSaving ? "wait" : "pointer", opacity: ppSaving || !ppForm.text.trim() || !ppForm.label.trim() ? 0.5 : 1 }}
              >{ppSaving ? "Saving..." : "Save"}</button>
              <button onClick={() => setPpEditing(null)} style={{ padding: "6px 16px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", color: C.textMuted }}>Cancel</button>
            </div>
          </div>
          );
        })()}

        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 8 }}>DEFAULT PROMPTS</div>
        {Object.entries(personalisationDefaults).map(([key, p]) => {
          const isActive = activePromptKey === key;
          return (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: isActive ? C.accent + "0c" : ppEditing === `default:${key}` ? C.accent + "08" : C.bg, border: `1px solid ${isActive ? C.accent + "55" : ppEditing === `default:${key}` ? C.accent + "44" : C.border}`, borderRadius: 8, marginBottom: 6 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: F, fontSize: 12, fontWeight: 600, color: C.text }}>{p.label}</div>
              <div style={{ fontFamily: F, fontSize: 10, color: C.textDim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.text?.slice(0, 120)}...</div>
            </div>
            {isActive ? (
              <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, fontFamily: F, fontWeight: 600, background: C.accent + "22", color: C.accent, flexShrink: 0 }}>Active</span>
            ) : (
              <button onClick={async () => { try { await api.prompts.setActive(key); setActivePromptKey(key); } catch (e) { setPpError(e?.message || "Activate failed"); } }} style={{ fontSize: 9, padding: "3px 10px", borderRadius: 4, fontFamily: F, fontWeight: 600, background: "transparent", border: `1px solid ${C.accent}44`, color: C.accent, cursor: "pointer", flexShrink: 0 }}>Activate</button>
            )}
            <button onClick={() => { setPpEditing(`default:${key}`); setPpForm({ label: p.label, text: p.text }); setPpError(null); }} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Edit</button>
            <button onClick={async () => { if (!confirm(`Delete default prompt "${p.label}"? It can be restored by refreshing defaults.`)) return; try { await api.prompts.deleteDefault(key); loadPersonalisationPrompts(); } catch (e) { setPpError(e?.message || "Delete failed"); } }} style={{ padding: "6px 8px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 6, color: C.danger, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>✕</button>
          </div>
          );
        })}

        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginTop: 14, marginBottom: 8 }}>CUSTOM PROMPTS</div>
        {!personalisationPrompts ? (
          <div style={{ fontSize: 11, color: C.textDim, padding: 12 }}>Loading...</div>
        ) : personalisationPrompts.length === 0 ? (
          <div style={{ fontSize: 11, color: C.textDim, padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>No custom prompts yet. Click "+ Add Prompt" to create one.</div>
        ) : personalisationPrompts.map(p => {
          const isActive = activePromptKey === p.id;
          return (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: isActive ? C.accent + "0c" : ppEditing === p.id ? C.accent + "08" : C.bg, border: `1px solid ${isActive ? C.accent + "55" : ppEditing === p.id ? C.accent + "44" : C.border}`, borderRadius: 8, marginBottom: 6 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: F, fontSize: 12, fontWeight: 600, color: C.text }}>{p.label}</div>
              <div style={{ fontFamily: F, fontSize: 10, color: C.textDim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.text?.slice(0, 120)}...</div>
            </div>
            {isActive ? (
              <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, fontFamily: F, fontWeight: 600, background: C.accent + "22", color: C.accent, flexShrink: 0 }}>Active</span>
            ) : (
              <button onClick={async () => { try { await api.prompts.setActive(p.id); setActivePromptKey(p.id); } catch (e) { setPpError(e?.message || "Activate failed"); } }} style={{ fontSize: 9, padding: "3px 10px", borderRadius: 4, fontFamily: F, fontWeight: 600, background: "transparent", border: `1px solid ${C.accent}44`, color: C.accent, cursor: "pointer", flexShrink: 0 }}>Activate</button>
            )}
            <button onClick={() => { setPpEditing(p.id); setPpForm({ label: p.label, text: p.text }); setPpError(null); }} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Edit</button>
            <button onClick={async () => { if (!confirm(`Delete prompt "${p.label}"?`)) return; try { await api.prompts.delete(p.id); loadPersonalisationPrompts(); } catch (e) { setPpError(e?.message || "Delete failed"); } }} style={{ padding: "6px 8px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 6, color: C.danger, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>✕</button>
          </div>
          );
        })}
      </div>

      {/* ENRICHMENT FALLBACK (scrape/LLM failure safety net) */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>ENRICHMENT FALLBACK MESSAGE</div>
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
              Used as the lead's personalisation body/subject when website scraping fails, returns empty content, or the LLM refuses. Default is blank. Edit here to customize the fallback copy.
            </div>
          </div>
          <button onClick={loadEnrichmentFallback} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer", color: C.textMuted }}>Refresh</button>
        </div>

        {enrichmentFallbackError && (
          <div style={{ padding: "10px 14px", background: C.dangerBg || "#3a1c1c", border: `1px solid ${C.danger}33`, borderRadius: 8, marginBottom: 12, fontSize: 11, color: C.danger }}>{enrichmentFallbackError}</div>
        )}
        {enrichmentFallbackStatus && (
          <div style={{ padding: "10px 14px", background: C.accent + "15", border: `1px solid ${C.accent}44`, borderRadius: 8, marginBottom: 12, fontSize: 11, color: C.accent }}>{enrichmentFallbackStatus}</div>
        )}

        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, padding: "14px 18px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
            <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>TOTAL FALLBACK EVENTS</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: enrichmentFallback?.errorCount ? C.warn : C.text, fontFamily: F, marginTop: 4 }}>
              {enrichmentFallback?.errorCount ?? "—"}
            </div>
            {enrichmentFallback?.lastErrorAt && (
              <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>Last: {new Date(enrichmentFallback.lastErrorAt).toLocaleString()}</div>
            )}
          </div>
          <button
            onClick={resetEnrichmentFallbackEvents}
            disabled={!enrichmentFallback?.errorCount}
            style={{ alignSelf: "flex-start", padding: "8px 14px", background: "transparent", border: `1px solid ${C.danger}44`, borderRadius: 6, color: C.danger, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: enrichmentFallback?.errorCount ? "pointer" : "default", opacity: enrichmentFallback?.errorCount ? 1 : 0.4 }}
          >
            Reset Event Log
          </button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 6 }}>FALLBACK SUBJECT</label>
          <input
            value={enrichmentFallbackForm.fallbackSubject}
            onChange={(e) => setEnrichmentFallbackForm((f) => ({ ...f, fallbackSubject: e.target.value }))}
            placeholder="(blank — server will derive &quot;Connecting with {company}&quot;)"
            style={{ width: "100%", padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 12, boxSizing: "border-box" }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 6 }}>FALLBACK MESSAGE (BODY)</label>
          <textarea
            value={enrichmentFallbackForm.fallbackMessage}
            onChange={(e) => setEnrichmentFallbackForm((f) => ({ ...f, fallbackMessage: e.target.value }))}
            placeholder="(blank — no personalization body will be sent)"
            rows={6}
            style={{ width: "100%", padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 11, resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
          />
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <button
            onClick={saveEnrichmentFallback}
            disabled={enrichmentFallbackSaving}
            style={{ padding: "8px 18px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: enrichmentFallbackSaving ? "wait" : "pointer", opacity: enrichmentFallbackSaving ? 0.6 : 1 }}
          >
            {enrichmentFallbackSaving ? "Saving..." : "Save"}
          </button>
        </div>

        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 8 }}>RECENT EVENTS (LAST 50)</div>
        {!enrichmentFallback ? (
          <div style={{ fontSize: 11, color: C.textDim, padding: 12 }}>Loading...</div>
        ) : !Array.isArray(enrichmentFallback.recentEvents) || enrichmentFallback.recentEvents.length === 0 ? (
          <div style={{ fontSize: 11, color: C.textDim, padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>No fallback events yet.</div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: "auto", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, fontFamily: F }}>
              <thead>
                <tr style={{ background: C.surface, position: "sticky", top: 0 }}>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, letterSpacing: "0.06em" }}>WHEN</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, letterSpacing: "0.06em" }}>REASON</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, letterSpacing: "0.06em" }}>USER / ORG</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, letterSpacing: "0.06em" }}>LEAD</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: C.textDim, fontWeight: 600, letterSpacing: "0.06em" }}>DETAIL</th>
                </tr>
              </thead>
              <tbody>
                {enrichmentFallback.recentEvents.map((ev) => {
                  const reasonColor = ev.reason === 'scrape_error' ? C.danger : ev.reason === 'llm_refusal' ? C.warn : C.textMuted;
                  return (
                    <tr key={ev.id} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ padding: "8px 10px", color: C.textMuted, whiteSpace: "nowrap" }}>{new Date(ev.created_at).toLocaleString()}</td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 4, fontWeight: 600, background: reasonColor + "22", color: reasonColor }}>{ev.reason}</span>
                      </td>
                      <td style={{ padding: "8px 10px", color: C.text }}>
                        <div>{ev.sender_name || "—"}</div>
                        <div style={{ fontSize: 9, color: C.textDim }}>{ev.org_id || "—"}</div>
                      </td>
                      <td style={{ padding: "8px 10px", color: C.text }}>
                        <div>{ev.lead_name || "—"} — {ev.lead_email || "—"}</div>
                        <div style={{ fontSize: 9, color: C.textDim }}>
                          {ev.lead_company || "—"}{ev.lead_domain ? ` · ${ev.lead_domain}` : ""}
                        </div>
                      </td>
                      <td style={{ padding: "8px 10px", color: C.textMuted, maxWidth: 320, wordBreak: "break-word" }}>{ev.detail || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ---- ERROR LOGS ----
  const _ERRORS = liveErrors || [];
  const unresolvedCount = _ERRORS.filter(e => !e.is_resolved).length;
  const renderErrorLogs = () => (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Total Errors" value={_ERRORS.length} icon="🔴" color={C.danger} />
        <StatCard label="Unresolved" value={unresolvedCount} icon="⚠️" color={unresolvedCount > 0 ? C.warn : C.green} />
        <StatCard label="Critical" value={_ERRORS.filter(e => e.severity === "critical").length} icon="🚨" color={C.danger} />
        <StatCard label="Last 24h" value={_ERRORS.filter(e => new Date(e.created_at) > new Date(Date.now() - 86400000)).length} icon="🕐" />
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>PLATFORM ERROR LOGS</div>
          <button onClick={() => api.admin.errors.list().then(d => setLiveErrors(Array.isArray(d) ? d : [])).catch(() => {})} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, color: C.textMuted, cursor: "pointer" }}>Refresh</button>
        </div>
        {_ERRORS.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: C.textDim, fontSize: 13 }}>No errors logged yet.</div>
        ) : (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "120px 70px 100px 1fr 120px 80px", padding: "8px 0", borderBottom: `1px solid ${C.border}`, gap: 10 }}>
              {["TIMESTAMP", "SEVERITY", "CATEGORY", "ERROR MESSAGE", "USER / ORG", "ACTIONS"].map(h => (
                <span key={h} style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.04em" }}>{h}</span>
              ))}
            </div>
            {_ERRORS.map(e => (
              <div key={e.id} style={{ display: "grid", gridTemplateColumns: "120px 70px 100px 1fr 120px 80px", padding: "10px 0", borderBottom: `1px solid ${C.border}`, gap: 10, alignItems: "start", opacity: e.is_resolved ? 0.5 : 1 }}>
                <span style={{ fontSize: 10, fontFamily: F, color: C.textDim }}>{new Date(e.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                <Badge text={e.severity || "error"} />
                <span style={{ fontSize: 10, fontFamily: F, color: C.accent }}>{e.category || "-"}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.error_message}</div>
                  <div style={{ fontSize: 10, color: C.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.request_method} {e.request_url}</div>
                  {e.stack_trace && <details style={{ marginTop: 4 }}><summary style={{ fontSize: 9, color: C.textDim, cursor: "pointer" }}>Stack trace</summary><pre style={{ fontSize: 9, color: C.textDim, whiteSpace: "pre-wrap", wordBreak: "break-all", margin: "4px 0 0", maxHeight: 120, overflow: "auto" }}>{e.stack_trace}</pre></details>}
                </div>
                <div style={{ fontSize: 10, color: C.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.org_id ? e.org_id.slice(0, 8) + "..." : "-"}{e.user_id ? " / " + e.user_id.slice(0, 8) + "..." : ""}</div>
                <div>
                  {e.is_resolved ? (
                    <Badge text="resolved" />
                  ) : (
                    <button onClick={async () => { try { await api.admin.errors.resolve(e.id, { resolution_notes: "Resolved from admin panel" }); const d = await api.admin.errors.list(); setLiveErrors(Array.isArray(d) ? d : []); } catch (_) {} }} style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.green}44`, borderRadius: 4, color: C.green, fontFamily: F, fontSize: 8, fontWeight: 600, cursor: "pointer" }}>Resolve</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ---- SYSTEM HEALTH ----
  const renderSystem = () => (
    <div>
      {/* Maintenance mode — locks out all non-admin users */}
      {(() => {
        const on = !!maintenance?.enabled;
        const envForced = !!maintenance?.env_override;
        return (
          <div style={{ padding: "20px 24px", background: on ? C.warn + "0d" : C.surface, border: `1px solid ${on ? C.warn + "55" : C.border}`, borderRadius: 10, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 18 }}>🛠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Maintenance Mode</div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>
                  Locks out all non-admin users with a maintenance screen. Platform admins keep full access. Background jobs keep running.
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: on ? C.warn + "22" : C.green + "18", color: on ? C.warn : C.green }}>
                  {on ? "ACTIVE" : "OFF"}
                </span>
                <button
                  disabled={maintenanceSaving || maintenanceLoading || envForced}
                  onClick={() => {
                    if (!on && !window.confirm("Turn ON maintenance mode?\n\nAll non-admin users will be immediately locked out and shown the maintenance screen. You (platform admin) keep access.")) return;
                    saveMaintenance(!on);
                  }}
                  style={{ padding: "7px 16px", background: on ? "transparent" : C.warn, border: `1px solid ${on ? C.border : C.warn}`, borderRadius: 6, color: on ? C.text : "#1a1205", fontFamily: F, fontSize: 11, fontWeight: 700, cursor: (maintenanceSaving || envForced) ? "default" : "pointer", opacity: (maintenanceSaving || envForced) ? 0.5 : 1 }}
                >
                  {maintenanceSaving ? "Saving…" : on ? "Turn OFF" : "Turn ON"}
                </button>
              </div>
            </div>
            {envForced && (
              <div style={{ fontSize: 10, color: C.warn, marginBottom: 10 }}>⚠️ Forced ON by the MAINTENANCE_MODE env var — clear it in Heroku config to control from here.</div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
              <div>
                <div style={{ fontSize: 9, color: C.textDim, fontWeight: 600, marginBottom: 4 }}>MESSAGE (shown to users)</div>
                <input value={maintenanceForm.message} onChange={(e) => setMaintenanceForm((f) => ({ ...f, message: e.target.value }))} placeholder="We'll be back shortly…" style={{ width: "100%", padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 9, color: C.textDim, fontWeight: 600, marginBottom: 4 }}>ETA (optional)</div>
                <input value={maintenanceForm.eta} onChange={(e) => setMaintenanceForm((f) => ({ ...f, eta: e.target.value }))} placeholder="e.g. 15:00 UTC" style={{ width: "100%", padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>
            {on && (
              <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                <button disabled={maintenanceSaving} onClick={() => saveMaintenance(true)} style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Update message</button>
              </div>
            )}
            {maintenanceMsg && <div style={{ marginTop: 10, fontSize: 11, color: C.accent }}>{maintenanceMsg}</div>}
          </div>
        );
      })()}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Uptime (30d)" value="99.94%" icon="🖥️" color={C.green} />
        <StatCard label="Avg API Response" value="124ms" icon="⏱️" sub="P95: 340ms" />
        <StatCard label="DB Queries/sec" value="847" icon="💾" />
        <StatCard label="Error Rate (24h)" value="0.03%" icon="⚠️" color={C.green} />
      </div>

      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 16 }}>SERVICE STATUS</div>
        {[
          { name: "Web Application", status: "Operational", uptime: "99.99%" },
          { name: "API Gateway", status: "Operational", uptime: "99.97%" },
          { name: "Database (Supabase)", status: "Operational", uptime: "99.98%" },
          { name: "Claude API", status: "Operational", uptime: "99.92%" },
          { name: "Background Workers", status: "Operational", uptime: "99.95%" },
          { name: "Email Delivery (Resend)", status: "Operational", uptime: "99.99%" },
          { name: "File Storage", status: "Operational", uptime: "100%" },
        ].map(s => (
          <div key={s.name} style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}`, gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, boxShadow: `0 0 4px ${C.green}88` }} />
            <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{s.name}</span>
            <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>{s.status}</span>
            <span style={{ fontSize: 10, color: C.textDim, fontFamily: F, width: 60, textAlign: "right" }}>{s.uptime}</span>
          </div>
        ))}
      </div>

      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>BACKGROUND QUEUES</div>
        {[
          { name: "Enrichment Queue", pending: 34, processing: 8, failed: 0 },
          { name: "Email Send Queue", pending: 128, processing: 15, failed: 2 },
          { name: "AI Analysis Queue", pending: 3, processing: 1, failed: 0 },
          { name: "Webhook Delivery", pending: 12, processing: 4, failed: 1 },
        ].map(q => (
          <div key={q.name} style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}`, gap: 16 }}>
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{q.name}</span>
            <span style={{ fontSize: 10, color: C.warn }}>{q.pending} pending</span>
            <span style={{ fontSize: 10, color: C.accent }}>{q.processing} processing</span>
            <span style={{ fontSize: 10, color: q.failed > 0 ? C.danger : C.textDim }}>{q.failed} failed</span>
          </div>
        ))}
      </div>

      {/* Recent Error Log */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>RECENT ERRORS — LAST 24 HOURS</div>
        {[
          { time: "14:22:08", level: "ERROR", service: "Enrichment Worker", message: "Wiza API timeout after 10s — fallback to Icypeas", org: "DataPulse Advisory", trace: "enrichment/waterfall.ts:142" },
          { time: "13:45:31", level: "WARN", service: "Email Delivery", message: "Resend rate limit hit (429) — queued for retry in 60s", org: "NexGen Consulting", trace: "campaigns/email-sender.ts:89" },
          { time: "11:12:44", level: "ERROR", service: "AI Analysis", message: "Claude API 529 overloaded — retry 2/3 succeeded", org: "Dunn Consulting", trace: "ai/claude-client.ts:201" },
          { time: "09:33:17", level: "WARN", service: "Webhook Delivery", message: "Webhook to https://hooks.zapier.com/... returned 502 — retry queued", org: "Apex Advisory", trace: "integrations/webhooks.ts:67" },
          { time: "08:01:52", level: "ERROR", service: "Auth", message: "Magic link expired for collaborator invite — user shown re-request page", org: "Perry Salvagne Consulting", trace: "auth/magic-link.ts:34" },
          { time: "02:15:09", level: "WARN", service: "Background Jobs", message: "Weekly digest generation took 45s (threshold: 30s) — performance degradation", org: "System", trace: "jobs/digest-worker.ts:18" },
        ].map((err, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 9, fontFamily: F, color: C.textDim, width: 55, flexShrink: 0 }}>{err.time}</span>
            <span style={{ fontSize: 8, fontFamily: F, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: err.level === "ERROR" ? C.danger + "15" : C.warn + "15", color: err.level === "ERROR" ? C.danger : C.warn, flexShrink: 0 }}>{err.level}</span>
            <span style={{ fontSize: 10, fontFamily: F, color: C.accent, width: 110, flexShrink: 0 }}>{err.service}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: C.text }}>{err.message}</div>
              <div style={{ fontSize: 9, color: C.textDim, marginTop: 2 }}>{err.org} · {err.trace}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  const renderKnowledge = () => {
    const KBS = [
      { id: "kb1", name: "Audit & Strategy Analysis", status: "Active", docs: 24, lastSync: "2 hrs ago", size: "4.2 MB", queries: 312, avgRetrieval: "0.28s", usedBy: ["Audit Analysis Engine", "AI Council", "Proposal Builder"], desc: "Core consulting methodology, VALUE scoring framework, ROI calculation models, McKinsey-style deck templates, industry benchmarks." },
      { id: "kb2", name: "Sales & Outreach", status: "Active", docs: 18, lastSync: "6 hrs ago", size: "2.8 MB", queries: 248, avgRetrieval: "0.22s", usedBy: ["Lead Personalisation", "Script Generator", "Messaging Workshop"], desc: "Cold email frameworks, objection handling playbooks, discovery call scripts, follow-up sequences, ICP targeting guides." },
      { id: "kb3", name: "Video & Content", status: "Active", docs: 31, lastSync: "1 day ago", size: "5.1 MB", queries: 156, avgRetrieval: "0.34s", usedBy: ["Content Engine", "Script Generator"], desc: "Hook frameworks, storytelling structures, LinkedIn content patterns, video script templates, engagement formulas." },
      { id: "kb4", name: "Platform Documentation", status: "Active", docs: 42, lastSync: "3 hrs ago", size: "3.6 MB", queries: 89, avgRetrieval: "0.18s", usedBy: ["AI Assistant", "Support Bot"], desc: "Pipeline feature docs, API reference, onboarding guides, troubleshooting FAQ, integration setup guides." },
      { id: "kb5", name: "Industry Knowledge — Insurance", status: "Active", docs: 15, lastSync: "3 days ago", size: "1.9 MB", queries: 34, avgRetrieval: "0.31s", usedBy: ["AI Council", "Niche Researcher"], desc: "Insurance industry AI use cases, regulatory considerations, Hodge Insurance case study, competitor landscape." },
      { id: "kb6", name: "Process Mapping", status: "Active", docs: 8, lastSync: "5 days ago", size: "1.1 MB", queries: 8, avgRetrieval: "0.15s", usedBy: ["Process Map Generator"], desc: "BPMN notation guide, process categorisation taxonomy, common business process templates, optimisation frameworks." },
    ];
    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <StatCard label="Total Knowledge Bases" value={KBS.length} icon="📚" />
          <StatCard label="Total Documents" value={KBS.reduce((s, k) => s + k.docs, 0)} icon="📄" />
          <StatCard label="Total Size" value="18.7 MB" icon="💾" />
          <StatCard label="RAG Queries Today" value="847" icon="🔍" sub="Avg retrieval: 0.3s" />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: C.textDim }}>Knowledge bases power AI quality across the platform. Customer-uploaded KBs via Custom Solutions use the same RAG pipeline.</div>
          <button style={{ padding: "8px 16px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>+ Create Knowledge Base</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {KBS.map(kb => (
            <div key={kb.id} style={{ padding: "18px 22px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: C.accent + "12", border: `1px solid ${C.accent}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>📚</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{kb.name}</span>
                    <Badge text={kb.status} />
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5, marginBottom: 8 }}>{kb.desc}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {kb.usedBy.map(u => (
                      <span key={u} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: F, background: C.bg, border: `1px solid ${C.border}`, color: C.textDim }}>🧠 {u}</span>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 16, fontSize: 10, color: C.textDim }}>
                    <span>📄 {kb.docs} documents</span>
                    <span>💾 {kb.size}</span>
                    <span>🔍 {kb.queries} queries today</span>
                    <span>⚡ {kb.avgRetrieval} avg retrieval</span>
                    <span>🔄 Synced {kb.lastSync}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Upload</button>
                  <button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Manage</button>
                  <button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Re-sync</button>
                  <button style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 6, color: C.danger, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, padding: "16px 20px", background: C.surface, border: `1px dashed ${C.accent}33`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 8 }}>CUSTOMER-UPLOADED KNOWLEDGE BASES</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>When customers create Custom Solutions with RAG, their uploaded documents are stored here. Monitor storage usage and content policy compliance.</div>
          {[
            { org: "Dunn Consulting", kb: "Insurance Case Studies", docs: 8, size: "1.4 MB" },
            { org: "DataPulse Advisory", kb: "Internal SOP Library", docs: 22, size: "3.8 MB" },
            { org: "Apex Advisory", kb: "Client Deliverables Archive", docs: 14, size: "2.1 MB" },
          ].map((ckb, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{ckb.org}</span>
              <span style={{ fontSize: 11, color: C.textMuted }}>{ckb.kb}</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{ckb.docs} docs · {ckb.size}</span>
              <button style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Review</button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---- USERS ----
  const renderUsers = () => {
    const toggleSuspend = async (userId, currentState) => {
      try {
        const res = await api.admin.users.updateStatus(userId, !currentState);
        setLiveUsers(prev => prev ? prev.map(u => u.id === userId ? { ...u, is_suspended: res.is_suspended } : u) : null);
      } catch (err) {
        alert(err.message || 'Failed to update user status');
      }
    };
    const handleResetPw = async (userId, userEmail) => {
      if (!confirm(`Generate a password reset link for ${userEmail}?`)) return;
      try {
        const res = await api.admin.users.resetPassword(userId);
        if (res.resetLink) {
          prompt(`Reset link for ${userEmail} (expires in 1 hour).\nCopy and send to the user:`, res.resetLink);
        } else {
          alert('Failed to generate reset link');
        }
      } catch (err) {
        alert(err.message || 'Failed to generate reset link');
      }
    };
    // Real metrics from DB-backed _USERS
    const isAdminRole = (r) => { const s = String(r || '').toLowerCase(); return s === 'platform_admin' || s === 'admin'; };
    const isOwnerRole = (r) => { const s = String(r || '').toLowerCase(); return s === 'org_owner' || s === 'owner'; };
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const totalUsers = _USERS.length;
    const adminCount = _USERS.filter(u => isAdminRole(u.role)).length;
    const ownerCount = _USERS.filter(u => isOwnerRole(u.role)).length;
    const suspendedCount = _USERS.filter(u => u.is_suspended).length;
    const activeCount = _USERS.filter(u => {
      const t = u.last_login_at ? new Date(u.last_login_at).getTime() : 0;
      return t && t >= sevenDaysAgo;
    }).length;
    const totalLogins = _USERS.reduce((s, u) => s + (Number(u.logins) || 0), 0);
    const avgLogins = totalUsers > 0 ? (totalLogins / totalUsers).toFixed(1) : '0';

    const roleLabel = (r) => {
      const s = String(r || '').toLowerCase();
      if (s === 'platform_admin' || s === 'admin') return 'Platform Admin';
      if (s === 'org_owner' || s === 'owner') return 'Owner';
      if (s === 'org_member' || s === 'member') return 'Member';
      return r || 'Member';
    };
    const roleColor = (r) => {
      const s = String(r || '').toLowerCase();
      if (s === 'platform_admin' || s === 'admin') return C.purple;
      if (s === 'org_owner' || s === 'owner') return C.accent;
      return C.green;
    };

    return (
      <div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <StatCard label="Total Users" value={totalUsers} icon="👤" />
          <StatCard label="Platform Admins" value={adminCount} icon="🛡️" color={C.purple} />
          <StatCard label="Owners" value={ownerCount} icon="👑" color={C.accent} />
          <StatCard label="Suspended" value={suspendedCount} icon="🚫" color={suspendedCount > 0 ? C.danger : C.text} />
          <StatCard label="Active (7d)" value={activeCount} icon="✅" color={C.green} />
          <StatCard label="Avg Logins/User" value={avgLogins} icon="📊" />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <button style={{ padding: "8px 18px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+ Create User Account</button>
        </div>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          {(() => {
            const cols = [
              { key: 'name',          label: 'User',         getValue: (u) => String(u.name || u.email || '').toLowerCase(), sortable: true },
              { key: 'org_id',        label: 'Organisation', getValue: (u) => String(u.org || u.org_id || '').toLowerCase(), sortable: true },
              { key: 'role',          label: 'Role',         getValue: (u) => String(u.role || '').toLowerCase(),             sortable: true },
              { key: 'logins',        label: 'Logins',       getValue: (u) => Number(u.logins) || 0,                          sortable: true },
              { key: 'last_login_at', label: 'Last Active',  getValue: (u) => u.last_login_at ? new Date(u.last_login_at).getTime() : 0, sortable: true },
              { key: null,            label: 'Actions',      sortable: false },
            ];
            const handleHeaderClick = (k) => {
              if (!k) return;
              setUserSort(prev => prev.col === k
                ? { col: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { col: k, dir: 'asc' });
            };
            return (
              <div style={{ display: "grid", gridTemplateColumns: "2.2fr 2fr 1.3fr 0.7fr 1.4fr 1.3fr", gap: 12, padding: "12px 20px", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
                {cols.map(c => {
                  const active = c.sortable && userSort.col === c.key;
                  const col = active ? C.accent : C.textMuted;
                  return (
                    <span
                      key={c.label}
                      onClick={() => handleHeaderClick(c.key)}
                      style={{
                        fontSize: 10, fontFamily: F, fontWeight: 700, color: col,
                        letterSpacing: "0.08em", display: "inline-flex", alignItems: "center", gap: 4,
                        cursor: c.sortable ? "pointer" : "default",
                        userSelect: "none",
                      }}
                    >
                      {c.label.toUpperCase()}
                      {c.sortable && (
                        <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.7, fontSize: 8, marginLeft: 2 }}>
                          <span style={{ color: active && userSort.dir === 'asc'  ? C.accent : C.textDim }}>▲</span>
                          <span style={{ color: active && userSort.dir === 'desc' ? C.accent : C.textDim }}>▼</span>
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            );
          })()}
          {(() => {
            const colsForSort = {
              name:          (u) => String(u.name || u.email || '').toLowerCase(),
              org_id:        (u) => String(u.org || u.org_id || '').toLowerCase(),
              role:          (u) => String(u.role || '').toLowerCase(),
              logins:        (u) => Number(u.logins) || 0,
              last_login_at: (u) => u.last_login_at ? new Date(u.last_login_at).getTime() : 0,
              created_at:    (u) => u.created_at ? new Date(u.created_at).getTime() : 0,
            };
            const getter = colsForSort[userSort.col] || colsForSort.created_at;
            const mult = userSort.dir === 'asc' ? 1 : -1;
            const sortedUsers = _USERS.slice().sort((a, b) => {
              const av = getter(a), bv = getter(b);
              if (av < bv) return -1 * mult;
              if (av > bv) return  1 * mult;
              return 0;
            });
            return sortedUsers;
          })().map((u, i) => {
            const rCol = roleColor(u.role);
            const rLabel = roleLabel(u.role);
            return (
              <div key={u.id || i} style={{
                display: "grid", gridTemplateColumns: "2.2fr 2fr 1.3fr 0.7fr 1.4fr 1.3fr", gap: 12,
                padding: "14px 20px", borderBottom: `1px solid ${C.border}`, alignItems: "center",
                background: u.is_suspended ? C.dangerBg : "transparent",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
                </div>
                <span style={{ fontSize: 11, fontFamily: F, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{u.org || u.org_id}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
                  <span style={{
                    padding: "4px 10px", borderRadius: 5, fontSize: 10, fontFamily: F, fontWeight: 700,
                    background: rCol + "1f", color: rCol, border: `1px solid ${rCol}55`,
                    boxShadow: `0 0 0 1px ${rCol}14, 0 2px 8px ${rCol}26`,
                    letterSpacing: "0.02em", whiteSpace: "nowrap",
                  }}>{rLabel}</span>
                  {u.is_suspended && <span style={{ fontSize: 9, fontFamily: F, fontWeight: 800, color: C.danger, padding: "3px 7px", background: C.danger + "22", borderRadius: 4, border: `1px solid ${C.danger}66`, letterSpacing: "0.06em" }}>BANNED</span>}
                </div>
                <span style={{ fontSize: 13, fontFamily: F, fontWeight: 700, color: (Number(u.logins) || 0) > 0 ? C.text : C.textDim }}>{u.logins != null ? u.logins : 0}</span>
                <span style={{ fontSize: 11, fontFamily: FB, color: u.last_login_at ? C.text : C.textDim, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => u.id && handleResetPw(u.id, u.email)} style={{ padding: "6px 12px", background: C.accentBg, border: `1px solid ${C.accent}55`, borderRadius: 5, color: C.accent, fontFamily: F, fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>Reset PW</button>
                  <button onClick={() => u.id && toggleSuspend(u.id, !!u.is_suspended)} style={{ padding: "6px 12px", background: u.is_suspended ? C.greenBg : C.dangerBg, border: `1px solid ${u.is_suspended ? C.green : C.danger}55`, borderRadius: 5, color: u.is_suspended ? C.green : C.danger, fontFamily: F, fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>{u.is_suspended ? "Activate" : "Suspend"}</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---- BILLING ----
  const renderBilling = () => {
    const directOrgMrr = ORGS.filter(o => !o.agency_id && o.mrr > 0).reduce((s, o) => s + o.mrr, 0);
    const agencyFees = AGENCIES.reduce((s, a) => s + a.platformFee + a.workspaceFees, 0);
    const billingTotal = directOrgMrr + agencyFees;
    const payingOrgs = ORGS.filter(o => o.mrr > 0).length;
    const updateStripeField = (mode, field, value) => {
      setStripeConfig(prev => ({ ...prev, [mode]: { ...prev[mode], [field]: value } }));
    };
    const stripeInputStyle = { width: "100%", padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 12, outline: "none", boxSizing: "border-box" };
    const currentMode = stripeConfig?.mode || 'test';
    const currentModeConfig = stripeConfig?.[currentMode] || {};
    const isConnected = !!(currentModeConfig.secret_key && currentModeConfig.secret_key.length > 0 && !/^\*+$/.test(currentModeConfig.secret_key));
    return (
    <div>
      {/* Stripe Configuration */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 20 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 16 }}>💳</span>
            <span style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>STRIPE CONFIGURATION</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {stripeLoading && <span style={{ fontSize: 10, color: C.textDim, fontFamily: F }}>Loading...</span>}
            {isConnected
              ? <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: F, fontWeight: 600, background: C.green + "15", color: C.green, border: `1px solid ${C.green}22` }}>Connected</span>
              : <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 9, fontFamily: F, fontWeight: 600, background: C.warn + "15", color: C.warn, border: `1px solid ${C.warn}22` }}>Not configured</span>
            }
          </div>
        </div>

        {/* Mode Toggle */}
        <div style={{ display: "flex", gap: 0, marginBottom: 20, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 4, width: "fit-content" }}>
          {[{ id: 'test', label: 'Sandbox / Test' }, { id: 'live', label: 'Live / Production' }].map(m => (
            <button
              key={m.id}
              onClick={() => toggleStripeMode(m.id)}
              style={{ padding: "6px 16px", background: currentMode === m.id ? C.accent : "transparent", color: currentMode === m.id ? C.bg : C.textDim, border: "none", borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}
            >{m.label}</button>
          ))}
        </div>

        {stripeConfig && (
          <>
            {/* Credentials Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              {[{ id: 'test', label: 'Test Credentials' }, { id: 'live', label: 'Live Credentials' }].map(col => (
                <div key={col.id} style={{ padding: "16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: col.id === 'live' ? C.green : C.accent, letterSpacing: "0.06em", marginBottom: 12, textTransform: "uppercase" }}>{col.label}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 10, color: C.textDim, fontFamily: F, marginBottom: 4 }}>Publishable Key</div>
                      <input type="text" value={stripeConfig[col.id]?.publishable_key || ''} onChange={e => updateStripeField(col.id, 'publishable_key', e.target.value)} placeholder="pk_test_..." style={stripeInputStyle} autoComplete="off" />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: C.textDim, fontFamily: F, marginBottom: 4 }}>Secret Key</div>
                      <input type="password" value={stripeConfig[col.id]?.secret_key || ''} onChange={e => updateStripeField(col.id, 'secret_key', e.target.value)} placeholder="Leave blank to keep existing value" style={stripeInputStyle} autoComplete="new-password" />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: C.textDim, fontFamily: F, marginBottom: 4 }}>Webhook Secret</div>
                      <input type="password" value={stripeConfig[col.id]?.webhook_secret || ''} onChange={e => updateStripeField(col.id, 'webhook_secret', e.target.value)} placeholder="Leave blank to keep existing value" style={stripeInputStyle} autoComplete="new-password" />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: C.textDim, fontFamily: F, marginBottom: 4 }}>Product ID</div>
                      <input type="text" value={stripeConfig[col.id]?.product_id || ''} onChange={e => updateStripeField(col.id, 'product_id', e.target.value)} placeholder="prod_..." style={stripeInputStyle} autoComplete="off" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Webhook Setup */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 10 }}>WEBHOOK SETUP</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
                {[{ id: 'test', label: 'Test Webhook' }, { id: 'live', label: 'Live Webhook' }].map(col => {
                  const endpointId = stripeConfig[col.id]?.webhook_endpoint_id;
                  const endpointShort = endpointId ? `${endpointId.slice(0, 6)}...${endpointId.slice(-4)}` : null;
                  const keyVal = stripeConfig[col.id]?.secret_key || '';
                  const keyIsSet = keyVal.length > 0 && !/^\*{4}/.test(keyVal);
                  const urlValue = stripeConfig[col.id]?.webhook_url || detectedUrls[col.id] || '';
                  const isLocalUrl = urlValue.includes('localhost') || urlValue.includes('127.0.0.1');
                  const canDeploy = (keyIsSet || !!endpointId) && !isLocalUrl && urlValue.startsWith('https://');
                  return (
                    <div key={col.id} style={{ padding: "12px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: col.id === 'live' ? C.green : C.accent, letterSpacing: "0.06em", textTransform: "uppercase" }}>{col.label}</span>
                        {endpointId
                          ? <span style={{ padding: "2px 6px", borderRadius: 4, fontSize: 8, fontFamily: F, fontWeight: 600, background: C.green + "15", color: C.green, border: `1px solid ${C.green}22` }}>Deployed ({endpointShort})</span>
                          : <span style={{ padding: "2px 6px", borderRadius: 4, fontSize: 8, fontFamily: F, fontWeight: 600, background: C.warn + "15", color: C.warn, border: `1px solid ${C.warn}22` }}>Not deployed</span>
                        }
                      </div>
                      <div style={{ fontSize: 10, color: C.textDim, fontFamily: F, marginBottom: 4 }}>Webhook URL</div>
                      <input type="text" value={urlValue} onChange={e => updateStripeField(col.id, 'webhook_url', e.target.value)} placeholder={col.id === 'test' ? 'http://localhost:3000/api/billing/stripe-webhook' : 'https://your-app.herokuapp.com/api/billing/stripe-webhook'} style={{ ...stripeInputStyle, marginBottom: 8 }} autoComplete="off" />
                      <button onClick={() => deployWebhook(col.id)} disabled={webhookDeploying || !canDeploy} style={{ padding: "6px 14px", background: webhookDeploying || !canDeploy ? C.border : col.id === 'live' ? C.green : C.accent, color: webhookDeploying || !canDeploy ? C.textDim : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: webhookDeploying || !canDeploy ? "not-allowed" : "pointer", width: "100%" }}>
                        {webhookDeploying ? 'Deploying...' : endpointId ? `Update ${col.id === 'live' ? 'Live' : 'Test'} Webhook` : `Deploy ${col.id === 'live' ? 'Live' : 'Test'} Webhook`}
                      </button>
                      {!keyIsSet && <div style={{ fontSize: 9, color: C.warn, marginTop: 4 }}>Save your {col.id} secret key first</div>}
                      {isLocalUrl && <div style={{ fontSize: 9, color: C.textDim, marginTop: 4 }}>Use <span style={{ fontFamily: "monospace", color: C.accent }}>stripe listen</span> for local dev — Deploy requires a public HTTPS URL</div>}
                    </div>
                  );
                })}
              </div>
              {webhookMessage && <div style={{ fontSize: 11, fontFamily: F, color: webhookMessage.includes('deployed') || webhookMessage.includes('updated') ? C.green : C.danger, marginBottom: 8 }}>{webhookMessage}</div>}
              <div style={{ padding: "10px 14px", background: C.bg, borderRadius: 6, border: `1px solid ${C.border}`, marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: C.textDim, fontFamily: F }}>
                  <span style={{ fontWeight: 600 }}>Local dev:</span> Run <span style={{ fontFamily: "monospace", color: C.accent, fontSize: 9 }}>stripe listen --forward-to localhost:3000/api/billing/stripe-webhook</span> to forward Stripe events to your local server.
                </div>
              </div>
              <button onClick={verifyStripeConnection} disabled={stripeVerifying} style={{ padding: "8px 16px", background: stripeVerifying ? C.border : C.accent, color: stripeVerifying ? C.textDim : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 600, cursor: stripeVerifying ? "not-allowed" : "pointer", width: "100%" }}>
                {stripeVerifying ? 'Verifying...' : '🔍 Verify Stripe Connection'}
              </button>
              {stripeVerify && (
                <div style={{ marginTop: 8, padding: "12px 14px", background: stripeVerify.ok ? C.green + "08" : C.danger + "08", border: `1px solid ${stripeVerify.ok ? C.green : C.danger}22`, borderRadius: 8, fontSize: 10, fontFamily: F }}>
                  {!stripeVerify.ok && <div style={{ color: C.danger, fontWeight: 600 }}>Connection failed: {stripeVerify.error}</div>}
                  {stripeVerify.ok && stripeVerify.account && (
                    <div>
                      <div style={{ fontWeight: 600, color: C.green, marginBottom: 6 }}>Connected to Stripe</div>
                      <div style={{ color: C.text }}>Account: <span style={{ fontWeight: 600 }}>{stripeVerify.account.name}</span> ({stripeVerify.account.id})</div>
                      {stripeVerify.account.email && <div style={{ color: C.textDim }}>Email: {stripeVerify.account.email}</div>}
                      <div style={{ color: C.textDim }}>Mode: <span style={{ fontWeight: 600, color: stripeVerify.mode === 'live' ? C.green : C.accent }}>{stripeVerify.mode}</span> · Country: {stripeVerify.account.country || '—'}</div>
                    </div>
                  )}
                  {stripeVerify.ok && stripeVerify.webhook && !stripeVerify.webhook.error && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ fontWeight: 600, color: C.green }}>Webhook: {stripeVerify.webhook.status}</div>
                      <div style={{ color: C.textDim, wordBreak: "break-all" }}>URL: {stripeVerify.webhook.url}</div>
                      <div style={{ color: C.textDim }}>Events: {stripeVerify.webhook.enabled_events?.join(', ')}</div>
                      <div style={{ color: C.textDim }}>Livemode: {stripeVerify.webhook.livemode ? 'Yes' : 'No'}</div>
                    </div>
                  )}
                  {stripeVerify.ok && stripeVerify.webhook?.error && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ fontWeight: 600, color: C.warn }}>Webhook: {stripeVerify.webhook.status} — {stripeVerify.webhook.error}</div>
                    </div>
                  )}
                  {stripeVerify.ok && !stripeVerify.webhook && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ color: C.warn }}>No webhook deployed yet — click Deploy above</div>
                    </div>
                  )}
                  {stripeVerify.expected_webhook_url && (
                    <div style={{ marginTop: 6, color: C.textDim, fontSize: 9, wordBreak: "break-all" }}>Expected URL: {stripeVerify.expected_webhook_url}</div>
                  )}
                </div>
              )}
            </div>

            {/* Credit Pack Settings */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>CREDIT PACK SETTINGS</div>
                <button
                  onClick={() => setStripeConfig(prev => {
                    const packs = Array.isArray(prev.credit_packs) ? [...prev.credit_packs] : [];
                    packs.push({ id: `pack_${Date.now()}`, label: '', credits: 0, price_cents: 0, price_display: '', is_active: true, sort_order: packs.length });
                    return { ...prev, credit_packs: packs };
                  })}
                  style={{ padding: "4px 10px", background: C.accent + "15", border: `1px solid ${C.accent}44`, borderRadius: 4, color: C.accent, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: "pointer" }}
                >+ Add Pack</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "80px 1fr 80px 90px 70px 60px", gap: 8, padding: "6px 10px", borderBottom: `1px solid ${C.border}`, background: C.bg, borderRadius: "6px 6px 0 0" }}>
                {["Credits", "Label", "Cents", "Display", "Active", ""].map(h => (
                  <span key={h} style={{ fontSize: 8, fontFamily: F, fontWeight: 700, color: C.textMuted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</span>
                ))}
              </div>
              {(Array.isArray(stripeConfig.credit_packs) ? stripeConfig.credit_packs : []).length === 0 && (
                <div style={{ padding: "12px 10px", fontSize: 11, color: C.textDim, fontFamily: F, fontStyle: "italic", borderBottom: `1px solid ${C.border}` }}>No credit packs. Click "+ Add Pack" to create one.</div>
              )}
              {(stripeConfig.credit_packs || []).map((pack, idx) => {
                const updatePack = (patch) => setStripeConfig(prev => ({
                  ...prev,
                  credit_packs: prev.credit_packs.map((p, i) => i === idx ? { ...p, ...patch } : p),
                }));
                const removePack = () => setStripeConfig(prev => ({
                  ...prev,
                  credit_packs: prev.credit_packs.filter((_, i) => i !== idx),
                }));
                return (
                  <div key={pack.id || idx} style={{ display: "grid", gridTemplateColumns: "80px 1fr 80px 90px 70px 60px", gap: 8, padding: "8px 10px", borderBottom: `1px solid ${C.border}`, alignItems: "center", opacity: pack.is_active === false ? 0.55 : 1 }}>
                    <input type="number" value={pack.credits || ''} onChange={e => updatePack({ credits: parseInt(e.target.value) || 0 })} placeholder="1000" style={{ ...stripeInputStyle, padding: "5px 8px", fontSize: 11 }} />
                    <input type="text" value={pack.label || ''} onChange={e => updatePack({ label: e.target.value })} placeholder="1000 Credit Pack" style={{ ...stripeInputStyle, padding: "5px 8px", fontSize: 11 }} />
                    <input type="number" value={pack.price_cents || ''} onChange={e => updatePack({ price_cents: parseInt(e.target.value) || 0 })} placeholder="4900" style={{ ...stripeInputStyle, padding: "5px 8px", fontSize: 11 }} />
                    <input type="text" value={pack.price_display || ''} onChange={e => updatePack({ price_display: e.target.value })} placeholder="$49.00" style={{ ...stripeInputStyle, padding: "5px 8px", fontSize: 11 }} />
                    <div onClick={() => updatePack({ is_active: !(pack.is_active !== false) })} style={{ width: 36, height: 20, borderRadius: 10, cursor: "pointer", background: pack.is_active !== false ? C.accent : C.border, position: "relative", transition: "background 0.2s" }}>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: pack.is_active !== false ? 19 : 3, transition: "left 0.2s", boxShadow: "0 1px 2px rgba(0,0,0,0.3)" }} />
                    </div>
                    <button onClick={removePack} style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.danger}44`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: "pointer" }}>Delete</button>
                  </div>
                );
              })}
            </div>

            {/* Billing Options */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 10 }}>BILLING OPTIONS</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 10 }}>
                <div onClick={() => setStripeConfig(prev => ({ ...prev, annual_billing_enabled: !prev.annual_billing_enabled }))} style={{ width: 44, height: 24, borderRadius: 12, cursor: "pointer", background: stripeConfig.annual_billing_enabled !== false ? C.accent : C.border, position: "relative", transition: "background 0.25s", flexShrink: 0 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: stripeConfig.annual_billing_enabled !== false ? 23 : 3, transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Annual billing</div>
                  <div style={{ fontSize: 10, color: C.textDim }}>Show the Monthly / Annual toggle on the pricing page. When disabled, only monthly billing is offered.</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                <input
                  type="number"
                  min={0}
                  value={stripeConfig.default_trial_days ?? 14}
                  onChange={e => setStripeConfig(prev => ({ ...prev, default_trial_days: Math.max(0, parseInt(e.target.value) || 0) }))}
                  style={{ width: 70, padding: "6px 10px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 12, outline: "none", textAlign: "center" }}
                />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Default trial days</div>
                  <div style={{ fontSize: 10, color: C.textDim }}>Trial length given to new signups when no single plan is active. Falls back to the active plan's trial_days if exactly one plan is active.</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Save Row */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={saveStripeConfig} disabled={stripeSaving || !stripeConfig} style={{ padding: "8px 20px", background: stripeSaving || !stripeConfig ? C.border : C.accent, color: stripeSaving || !stripeConfig ? C.textDim : C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 600, cursor: stripeSaving || !stripeConfig ? "not-allowed" : "pointer" }}>
            {stripeSaving ? "Saving..." : "Save Stripe Config"}
          </button>
          {stripeMessage && (
            <span style={{ fontSize: 11, fontFamily: F, color: stripeMessage.includes('saved') ? C.green : C.danger }}>{stripeMessage}</span>
          )}
        </div>
      </div>

      {/* ── Plan Management ── */}
      <AdminPlanEditor />
    </div>
  );};


  // ---- SOLUTIONS ----
  const renderSolutions = () => (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Published Solutions" value="8" icon="🧩" />
        <StatCard label="Total Installs" value="3,430" icon="⬇️" />
        <StatCard label="Custom Skills (all orgs)" value="14" icon="🔧" />
        <StatCard label="Most Popular" value="AI Assistant" icon="⭐" sub="847 installs" />
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>PUBLISHED SOLUTIONS</div>
          <button style={{ padding: "6px 14px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>+ Add Solution</button>
        </div>
        {[
          { name: "AI Assistant", installs: 847, active: 620, category: "Productivity", status: "Published" },
          { name: "Proposal Builder", installs: 689, active: 410, category: "Sales", status: "Published" },
          { name: "ROI Tracker", installs: 523, active: 340, category: "Consulting", status: "Published" },
          { name: "Client Intake Forms", installs: 431, active: 280, category: "Consulting", status: "Published" },
          { name: "Client Health Dashboard", installs: 312, active: 190, category: "Delivery", status: "Published" },
          { name: "Board Report Generator", installs: 276, active: 150, category: "Consulting", status: "Published" },
          { name: "Training Hub", installs: 198, active: 90, category: "Delivery", status: "Published" },
          { name: "Contract Analyser", installs: 154, active: 80, category: "Operations", status: "Draft" },
        ].map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{s.name}</span>
            <span style={{ fontSize: 10, color: C.textDim, width: 80 }}>{s.category}</span>
            <span style={{ fontSize: 11, fontFamily: F, width: 60, textAlign: "right" }}>{s.installs}</span>
            <span style={{ fontSize: 10, color: C.textDim, width: 50 }}>installs</span>
            <span style={{ fontSize: 11, fontFamily: F, color: C.green, width: 50, textAlign: "right" }}>{s.active}</span>
            <span style={{ fontSize: 10, color: C.textDim, width: 40 }}>active</span>
            <Badge text={s.status === "Draft" ? "Draft" : "Active"} />
            <button style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Edit</button>
          </div>
        ))}
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>CUSTOMER CUSTOM SKILLS — MONITOR</div>
        {[
          { org: "Dunn Consulting", skill: "Competitor Pricing Analyst", created: "Feb 12", queries: 34 },
          { org: "DataPulse Advisory", skill: "Client Onboarding Tracker", created: "Feb 8", queries: 89 },
          { org: "DataPulse Advisory", skill: "Sales Forecast Engine", created: "Feb 5", queries: 112 },
          { org: "Apex Advisory", skill: "Compliance Checker", created: "Feb 10", queries: 23 },
        ].map((cs, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{cs.org}</span>
            <span style={{ fontSize: 11, color: C.textMuted, flex: 1 }}>{cs.skill}</span>
            <span style={{ fontSize: 10, color: C.textDim }}>{cs.queries} queries</span>
            <span style={{ fontSize: 10, color: C.textDim }}>{cs.created}</span>
            <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Review</button>
          </div>
        ))}
      </div>

      {/* Assign Bespoke Solution */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.accent}18`, borderRadius: 10, marginTop: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.accent, letterSpacing: "0.06em", marginBottom: 8 }}>ASSIGN SOLUTION TO ORGANISATION</div>
        <div style={{ fontSize: 11, color: C.textDim, marginBottom: 12 }}>Push a bespoke or private solution to a specific client account. They'll see it in their Solutions sidebar.</div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 14 }}>
          <div style={{ flex: 1 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>SOLUTION</div><select style={{ ...inputStyle, padding: "8px 8px" }}><option>Select solution...</option><option>AI Assistant</option><option>Proposal Builder</option><option>ROI Tracker</option><option>Contract Analyser</option><option>[Create New Bespoke]</option></select></div>
          <div style={{ flex: 1 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>ORGANISATION</div><select style={{ ...inputStyle, padding: "8px 8px" }}><option>Select org...</option>{ORGS.map(o => <option key={o.id}>{o.name}</option>)}</select></div>
          <div style={{ width: 120 }}><div style={{ fontSize: 9, fontFamily: F, color: C.textDim, marginBottom: 3 }}>VISIBILITY</div><select style={{ ...inputStyle, padding: "8px 8px" }}><option>Private</option><option>Public</option><option>Hidden</option></select></div>
          <button style={{ padding: "8px 16px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer", height: 34, whiteSpace: "nowrap" }}>Assign</button>
        </div>
        <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, marginBottom: 8 }}>CURRENT ASSIGNMENTS</div>
        {[
          { solution: "Menu Cost Analyser", org: "Hodge Insurance", visibility: "Private", assigned: "Feb 10" },
          { solution: "Employee Onboarding Tracker", org: "DataPulse Advisory", visibility: "Private", assigned: "Feb 6" },
          { solution: "Custom Pricing Model", org: "Dunn Consulting", visibility: "Private", assigned: "Jan 28" },
        ].map((a, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{a.solution}</span>
            <span style={{ fontSize: 11, color: C.textMuted }}>{a.org}</span>
            <Badge text={a.visibility.toLowerCase()} />
            <span style={{ fontSize: 10, color: C.textDim }}>{a.assigned}</span>
            <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Edit</button>
            <button style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Revoke</button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderLeadSearchOrder = () => {
    const meta = (k) => LEAD_ORDER_META.find(m => m.key === k) || { key: k, label: k, icon: "🔗", costLabel: "" };
    const move = (list, idx, dir) => {
      const n = list.length;
      if (dir === -1 && idx <= 0) return list;
      if (dir === 1 && idx >= n - 1) return list;
      const arr = [...list];
      [arr[idx], arr[idx + dir]] = [arr[idx + dir], arr[idx]];
      return arr;
    };
    const onMove = (type, idx, dir) => {
      const key = type === "leadSearch" ? "leadSearch" : "leadEnrichment";
      setLeadSearchOrder(prev => ({ ...prev, [key]: move(prev[key], idx, dir) }));
    };
    const onSave = async () => {
      if (!leadSearchOrderOrgId) return;
      setLeadOrderSaving(true);
      try {
        await api.admin.organisations.saveLeadSearchOrder(leadSearchOrderOrgId, leadSearchOrder);
      } catch (e) {
        console.error(e);
        alert("Failed to save. Please try again.");
      } finally {
        setLeadOrderSaving(false);
      }
    };
    const orgs = liveOrgs || ORGS;
    return (
      <div>
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 24 }}>
          <label style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>ORGANISATION</label>
          <select value={leadSearchOrderOrgId} onChange={e => setLeadSearchOrderOrgId(e.target.value)} style={{ ...inputStyle, width: 320, padding: "8px 12px" }}>
            <option value="" style={{ background: C.surface }}>Select an organisation...</option>
            {orgs.map(o => (
              <option key={o.id} value={o.id} style={{ background: C.surface }}>{o.name}</option>
            ))}
          </select>
          <button onClick={onSave} disabled={!leadSearchOrderOrgId || leadOrderSaving || leadOrderLoading} style={{ padding: "8px 16px", background: (!leadSearchOrderOrgId || leadOrderSaving || leadOrderLoading) ? C.border : C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 11, fontWeight: 600, cursor: (!leadSearchOrderOrgId || leadOrderSaving || leadOrderLoading) ? "not-allowed" : "pointer" }}>
            {leadOrderSaving ? "Saving..." : "Save Order"}
          </button>
        </div>
        {!leadSearchOrderOrgId ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textDim, fontSize: 13 }}>Select an organisation to configure lead search and enrichment order.</div>
        ) : leadOrderLoading ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textMuted }}>Loading...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>LEAD SEARCH ORDER</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>Order providers used to find leads (first available wins)</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {leadSearchOrder.leadSearch.map((k, idx) => (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                    <span style={{ fontFamily: F, fontSize: 11, color: C.accent, fontWeight: 600, width: 18 }}>{idx + 1}</span>
                    <span style={{ fontSize: 14 }}>{meta(k).icon}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{meta(k).label}</span>
                    <span style={{ fontSize: 9, color: C.textDim, fontFamily: F }}>{meta(k).costLabel}</span>
                    <button onClick={() => onMove("leadSearch", idx, -1)} disabled={idx === 0} style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, cursor: idx === 0 ? "not-allowed" : "pointer" }}>↑</button>
                    <button onClick={() => onMove("leadSearch", idx, 1)} disabled={idx === leadSearchOrder.leadSearch.length - 1} style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, cursor: idx === leadSearchOrder.leadSearch.length - 1 ? "not-allowed" : "pointer" }}>↓</button>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>LEAD ENRICHMENT ORDER</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>Order providers used to verify/enrich leads</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {leadSearchOrder.leadEnrichment.map((k, idx) => (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                    <span style={{ fontFamily: F, fontSize: 11, color: C.accent, fontWeight: 600, width: 18 }}>{idx + 1}</span>
                    <span style={{ fontSize: 14 }}>{meta(k).icon}</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>{meta(k).label}</span>
                    <span style={{ fontSize: 9, color: C.textDim, fontFamily: F }}>{meta(k).costLabel}</span>
                    <button onClick={() => onMove("leadEnrichment", idx, -1)} disabled={idx === 0} style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, cursor: idx === 0 ? "not-allowed" : "pointer" }}>↑</button>
                    <button onClick={() => onMove("leadEnrichment", idx, 1)} disabled={idx === leadSearchOrder.leadEnrichment.length - 1} style={{ padding: "4px 6px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, cursor: idx === leadSearchOrder.leadEnrichment.length - 1 ? "not-allowed" : "pointer" }}>↓</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderIntegrations = () => (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Connected Integrations" value="32" icon="🔗" sub="across all orgs" />
        <StatCard label="API Keys Active" value="8" icon="🔑" />
        <StatCard label="Webhook Deliveries (24h)" value="1,247" icon="📡" sub="99.2% success" trend="up" color={C.green} />
        <StatCard label="Failed Deliveries" value="10" icon="⚠️" color={C.warn} />
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>INTEGRATION HEALTH</div>
        {PROVIDERS.map(p => (
          <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.status === "Healthy" ? C.green : p.status === "Degraded" ? C.warn : C.danger, boxShadow: `0 0 4px ${p.status === "Healthy" ? C.green : C.warn}66` }} />
            <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{p.name}</span>
            <span style={{ fontSize: 11, color: C.textDim, width: 140 }}>{p.type}</span>
            <Badge text={p.status} />
            <span style={{ fontSize: 11, fontFamily: F, width: 70, textAlign: "right" }}>{p.calls}</span>
            <span style={{ fontSize: 10, color: parseFloat(p.errorRate) > 1 ? C.warn : C.green, width: 50, textAlign: "right" }}>{p.errorRate}</span>
            <span style={{ fontSize: 11, fontFamily: F, width: 50, textAlign: "right" }}>{p.avgLatency}</span>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>RATE LIMITS BY PLAN</div>
          {[
            { plan: "Starter", limit: "50 req/min", api: "No API access" },
            { plan: "Growth", limit: "100 req/min", api: "100 req/min" },
            { plan: "Scale", limit: "500 req/min", api: "500 req/min" },
          ].map(r => (
            <div key={r.plan} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}`, gap: 12 }}>
              <Badge text={r.plan} />
              <span style={{ flex: 1, fontSize: 12 }}>Internal: {r.limit}</span>
              <span style={{ fontSize: 12, color: C.textDim }}>API: {r.api}</span>
            </div>
          ))}
        </div>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>CUSTOMER API KEYS</div>
          {[
            { org: "DataPulse Advisory", label: "Production", lastUsed: "1 hr ago", calls: "2,400/mo" },
            { org: "Dunn Consulting", label: "Dev", lastUsed: "3 hrs ago", calls: "840/mo" },
            { org: "NexGen Consulting", label: "Production", lastUsed: "1 day ago", calls: "320/mo" },
          ].map((k, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}`, gap: 8 }}>
              <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>{k.org}</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{k.label}</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{k.calls}</span>
              <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Revoke</button>
            </div>
          ))}
        </div>
      </div>

      {/* LLM & Enrichment — org-scoped (configure per org) */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 16 }}>ORG-SCOPED INTEGRATIONS</div>
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 20 }}>
          <label style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim }}>Organisation</label>
          <select value={orgIntegrationsOrgId} onChange={e => setOrgIntegrationsOrgId(e.target.value)} style={{ ...inputStyle, width: 280, padding: "8px 12px" }}>
            <option value="" style={{ background: C.surface }}>Select org to configure...</option>
            {(liveOrgs || ORGS).map(o => (
              <option key={o.id} value={o.id} style={{ background: C.surface }}>{o.name}</option>
            ))}
          </select>
        </div>
        {orgIntegrationsOrgId ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 10 }}>LLM</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {ADMIN_LLM_META.map(intg => {
                  const connected = orgIntegrationsStatus[intg.key] === true || (orgIntegrationsStatus[intg.key]?.connected === true);
                  return (
                    <div key={intg.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                      <span style={{ fontSize: 16 }}>{intg.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{intg.label}</div>
                        <div style={{ fontSize: 10, color: C.textDim }}>{intg.desc}</div>
                      </div>
                      {connected && <Badge text="Connected" />}
                      <button onClick={() => setAdminConfigModal({ intg, orgId: orgIntegrationsOrgId })} style={{ padding: "4px 10px", background: connected ? "transparent" : C.accent, border: `1px solid ${connected ? C.border : C.accent}`, borderRadius: 4, color: connected ? C.textMuted : C.bg, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{connected ? "Configure" : "Connect"}</button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 10 }}>ENRICHMENT & LEAD DATA</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {ADMIN_ENRICHMENT_META.map(intg => {
                  const connected = orgIntegrationsStatus[intg.key] === true || (orgIntegrationsStatus[intg.key]?.connected === true);
                  return (
                    <div key={intg.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                      <span style={{ fontSize: 16 }}>{intg.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{intg.label}</div>
                        <div style={{ fontSize: 10, color: C.textDim }}>{intg.desc}</div>
                      </div>
                      {connected && <Badge text="Connected" />}
                      <button onClick={() => setAdminConfigModal({ intg, orgId: orgIntegrationsOrgId })} style={{ padding: "4px 10px", background: connected ? "transparent" : C.accent, border: `1px solid ${connected ? C.border : C.accent}`, borderRadius: 4, color: connected ? C.textMuted : C.bg, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>{connected ? "Configure" : "Connect"}</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: C.textDim }}>Select an organisation to configure LLM and enrichment integrations.</div>
        )}
      </div>
    </div>
  );

  const refreshOrgIntegrations = () => {
    if (orgIntegrationsOrgId) api.admin.organisations.getIntegrations(orgIntegrationsOrgId).then(res => setOrgIntegrationsStatus(res.integrations || {})).catch(() => {});
  };

  // ---- CONTENT & TEMPLATES ----
  const renderContent = () => (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>WORKFLOW LIBRARY</div>
            <button style={{ padding: "5px 12px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>+ Add</button>
          </div>
          {["Lead Enrichment Pipeline", "Cold Email Sequence Builder", "LinkedIn Content Engine", "AI Audit Transcript Analyzer", "Content Repurposing Workflow"].map((w, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 12 }}>{w}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <button style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Edit</button>
                <button style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>⭐</button>
                <button style={{ padding: "3px 6px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 8, cursor: "pointer" }}>✕</button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>SURVEY TEMPLATES</div>
            <button style={{ padding: "5px 12px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>+ Add</button>
          </div>
          {["AI Readiness Assessment", "Digital Maturity Survey", "Process Efficiency Audit", "Employee Sentiment Analysis"].map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 12 }}>{s}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <button style={{ padding: "3px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Edit</button>
                <button style={{ padding: "3px 6px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 8, cursor: "pointer" }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>HOOK LIBRARY — {47} HOOKS</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>Curated video hooks used in the Video (Short Form) module.</div>
          <button style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Manage Hooks</button>
        </div>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>ONBOARDING FLOW</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>Manage the new user onboarding sequence, welcome emails, and getting-started guides.</div>
          <button style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Edit Flow</button>
        </div>
      </div>
    </div>
  );

  // ---- SUPPORT ----
  const renderSupport = () => (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Open Tickets" value="3" icon="🎧" color={C.warn} />
        <StatCard label="Avg Resolution" value="4.2 hrs" icon="⏱️" />
        <StatCard label="Resolved This Week" value="12" icon="✅" color={C.green} />
        <StatCard label="CSAT Score" value="4.8/5" icon="⭐" color={C.green} />
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>TICKET INBOX</div>
        {TICKETS.map(t => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 11, fontFamily: F, color: C.textDim, width: 50 }}>{t.id}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{t.subject}</div>
              <div style={{ fontSize: 10, color: C.textDim }}>{t.org} · {t.created}</div>
            </div>
            <Badge text={t.priority === "High" ? "High" : t.priority === "Medium" ? "Medium" : "Low"} />
            <Badge text={t.status} />
            <button style={{ padding: "5px 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>Open</button>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>ANNOUNCEMENTS</div>
          <textarea placeholder="Write an in-app announcement..." rows={3} style={{ ...inputStyle, resize: "vertical", marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <select style={{ ...inputStyle, width: 140, padding: "6px 10px", fontSize: 10 }}><option>All Users</option><option>Scale Plan</option><option>Growth Plan</option><option>Trialling</option></select>
            <button style={{ padding: "6px 14px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Send</button>
          </div>
        </div>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>CHANGELOG</div>
          {[
            { version: "v3.2", date: "Feb 14", title: "AI Activity Feed + Custom Skills" },
            { version: "v3.1", date: "Feb 12", title: "Implementation Board Kanban rebuild" },
            { version: "v3.0", date: "Feb 10", title: "Solutions Marketplace launch" },
          ].map((c, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.accent }}>{c.version}</span>
              <span style={{ flex: 1, fontSize: 12 }}>{c.title}</span>
              <span style={{ fontSize: 10, color: C.textDim }}>{c.date}</span>
            </div>
          ))}
          <button style={{ marginTop: 8, padding: "6px 14px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>+ Add Entry</button>
        </div>
      </div>

      {/* Email Template Management */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>EMAIL TEMPLATES — TRANSACTIONAL</div>
          <button style={{ padding: "5px 12px", background: C.accent, color: C.bg, border: "none", borderRadius: 6, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>+ Add Template</button>
        </div>
        {[
          { name: "Welcome Email", trigger: "On signup", subject: "Welcome to Pipeline — let's get you set up", status: "Active", lastEdit: "Feb 8", sends: "48" },
          { name: "Trial Ending (3 days)", trigger: "3 days before trial ends", subject: "Your Pipeline trial ends in 3 days", status: "Active", lastEdit: "Feb 5", sends: "12" },
          { name: "Trial Ended", trigger: "Trial expiry", subject: "Your trial has ended — here's what you built", status: "Active", lastEdit: "Feb 5", sends: "6" },
          { name: "Credit Warning (80%)", trigger: "80% credits used", subject: "You've used 80% of your monthly credits", status: "Active", lastEdit: "Jan 28", sends: "34" },
          { name: "Credit Exhausted", trigger: "100% credits used", subject: "You've run out of credits this month", status: "Active", lastEdit: "Jan 28", sends: "8" },
          { name: "Weekly Digest", trigger: "Every Monday 9am", subject: "Your Pipeline weekly report", status: "Active", lastEdit: "Feb 10", sends: "147" },
          { name: "Campaign Complete", trigger: "Email/LinkedIn campaign finishes", subject: "Campaign complete — here are your results", status: "Active", lastEdit: "Feb 3", sends: "62" },
          { name: "Magic Link (Collaborator)", trigger: "Collaborator invited", subject: "You've been invited to view a project board", status: "Active", lastEdit: "Feb 1", sends: "28" },
          { name: "Payment Failed", trigger: "Stripe payment failure", subject: "We couldn't process your payment", status: "Active", lastEdit: "Jan 25", sends: "4" },
          { name: "Win-back (30 day inactive)", trigger: "No login 30 days", subject: "We miss you — here's what's new in Pipeline", status: "Draft", lastEdit: "Feb 12", sends: "0" },
        ].map((t, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ flex: 2 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{t.name}</div>
              <div style={{ fontSize: 10, color: C.textDim }}>{t.trigger}</div>
            </div>
            <div style={{ flex: 3, fontSize: 11, color: C.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div>
            <span style={{ fontSize: 10, fontFamily: F, color: C.textDim, width: 50, textAlign: "right" }}>{t.sends} sent</span>
            <Badge text={t.status === "Draft" ? "Draft" : "Active"} />
            <div style={{ display: "flex", gap: 4 }}>
              <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.accent}33`, borderRadius: 4, color: C.accent, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Preview</button>
              <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Test</button>
              <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>Edit</button>
              <div onClick={e => { const el = e.currentTarget; el.dataset.on = el.dataset.on === "true" ? "false" : "true"; const dot = el.querySelector("div"); if (el.dataset.on === "true") { el.style.background = C.accent; dot.style.transform = "translateX(14px)"; } else { el.style.background = C.border; dot.style.transform = "translateX(0)"; }}} data-on={t.status !== "Draft" ? "true" : "false"} style={{ width: 28, height: 14, borderRadius: 7, background: t.status !== "Draft" ? C.accent : C.border, position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FFF", position: "absolute", top: 2, left: 2, transition: "transform 0.2s", transform: t.status !== "Draft" ? "translateX(14px)" : "translateX(0)" }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ---- ANALYTICS ----
  const renderAnalytics = () => (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="DAU" value="28" icon="📊" />
        <StatCard label="WAU" value="34" icon="📈" />
        <StatCard label="Avg Session" value="18 min" icon="⏱️" />
        <StatCard label="Feature Adoption" value="74%" icon="✅" sub="Avg features used per org" color={C.green} />
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 16 }}>FEATURE USAGE HEATMAP — ALL ORGS</div>
        {[
          { feature: "Lead Discovery", usage: 94, sessions: 420 },
          { feature: "CRM Pipeline", usage: 88, sessions: 380 },
          { feature: "Cold Email Campaigns", usage: 82, sessions: 340 },
          { feature: "AI Assistant", usage: 76, sessions: 290 },
          { feature: "Strategy (Audits)", usage: 71, sessions: 245 },
          { feature: "Messaging Workshop", usage: 65, sessions: 210 },
          { feature: "LinkedIn Campaigns", usage: 58, sessions: 180 },
          { feature: "Implementation Board", usage: 52, sessions: 150 },
          { feature: "Script Generator", usage: 45, sessions: 120 },
          { feature: "AI Council", usage: 38, sessions: 95 },
          { feature: "Niche Researcher", usage: 32, sessions: 78 },
          { feature: "Video (Short Form)", usage: 24, sessions: 52 },
          { feature: "Call Analyser", usage: 18, sessions: 34 },
          { feature: "Community", usage: 12, sessions: 22 },
        ].map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0" }}>
            <span style={{ fontSize: 11, width: 160, color: C.textMuted }}>{f.feature}</span>
            <div style={{ flex: 1, height: 16, borderRadius: 3, background: C.bg }}>
              <div style={{ width: `${f.usage}%`, height: "100%", borderRadius: 3, background: f.usage > 70 ? C.green : f.usage > 40 ? C.accent : f.usage > 20 ? C.warn : C.danger + "88", transition: "width 0.3s" }} />
            </div>
            <span style={{ fontSize: 10, fontFamily: F, fontWeight: 600, width: 40, textAlign: "right" }}>{f.usage}%</span>
            <span style={{ fontSize: 9, color: C.textDim, width: 60, textAlign: "right" }}>{f.sessions} sess</span>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>TRIAL → PAID FUNNEL</div>
          {[
            { stage: "Signed Up", count: 48, pct: 100 },
            { stage: "Completed Onboarding", count: 38, pct: 79 },
            { stage: "Used Core Feature", count: 32, pct: 67 },
            { stage: "Reached AHA Moment", count: 24, pct: 50 },
            { stage: "Converted to Paid", count: 20, pct: 42 },
          ].map((s, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ fontSize: 11 }}>{s.stage}</span>
                <span style={{ fontSize: 11, fontFamily: F, fontWeight: 600 }}>{s.count} <span style={{ color: C.textDim, fontWeight: 400 }}>({s.pct}%)</span></span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: C.bg }}>
                <div style={{ width: `${s.pct}%`, height: "100%", borderRadius: 3, background: C.accent }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>COHORT RETENTION (monthly)</div>
          {[
            { month: "Oct 2025", m1: "92%", m2: "85%", m3: "78%", m4: "74%" },
            { month: "Nov 2025", m1: "88%", m2: "82%", m3: "76%", m4: "—" },
            { month: "Dec 2025", m1: "90%", m2: "84%", m3: "—", m4: "—" },
            { month: "Jan 2026", m1: "94%", m2: "—", m3: "—", m4: "—" },
          ].map((c, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr", padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 11, fontWeight: 600 }}>{c.month}</span>
              {[c.m1, c.m2, c.m3, c.m4].map((v, j) => (
                <span key={j} style={{ fontSize: 11, fontFamily: F, color: v === "—" ? C.textDim : parseInt(v) > 80 ? C.green : C.warn, textAlign: "center" }}>{v}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Onboarding Step Completion */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>ONBOARDING STEP COMPLETION — WHERE USERS DROP OFF</div>
        {[
          { step: "Account Created", completed: 48, pct: 100 },
          { step: "Brand Voice Setup", completed: 42, pct: 88 },
          { step: "Buyer Persona Defined", completed: 38, pct: 79 },
          { step: "First Lead Discovery", completed: 34, pct: 71 },
          { step: "Connected Integration", completed: 28, pct: 58 },
          { step: "First Campaign Launched", completed: 22, pct: 46 },
          { step: "First AI Audit Run", completed: 16, pct: 33 },
          { step: "Invited Team Member", completed: 12, pct: 25 },
        ].map((s, i, arr) => {
          const dropoff = i > 0 ? arr[i - 1].completed - s.completed : 0;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <span style={{ width: 18, fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, textAlign: "right" }}>{i + 1}.</span>
              <span style={{ width: 180, fontSize: 11, color: C.textMuted }}>{s.step}</span>
              <div style={{ flex: 1, height: 16, borderRadius: 3, background: C.bg }}>
                <div style={{ width: `${s.pct}%`, height: "100%", borderRadius: 3, background: s.pct > 70 ? C.green : s.pct > 40 ? C.accent : C.warn }} />
              </div>
              <span style={{ width: 50, fontSize: 10, fontFamily: F, fontWeight: 600, textAlign: "right" }}>{s.pct}%</span>
              <span style={{ width: 30, fontSize: 10, fontFamily: F, textAlign: "right", color: C.textDim }}>{s.completed}</span>
              {dropoff > 0 && <span style={{ width: 50, fontSize: 9, color: C.danger, textAlign: "right" }}>-{dropoff} lost</span>}
              {dropoff === 0 && <span style={{ width: 50 }} />}
            </div>
          );
        })}
        <div style={{ marginTop: 10, padding: "8px 12px", background: C.warnBg, border: `1px solid ${C.warn}22`, borderRadius: 6 }}>
          <span style={{ fontSize: 10, color: C.warn, fontWeight: 600 }}>💡 Biggest drop-off: </span>
          <span style={{ fontSize: 10, color: C.textDim }}>42% of users never connect an integration. Consider adding integration prompts to the onboarding flow or post-signup email sequence.</span>
        </div>
      </div>
    </div>
  );
  const renderSecurity = () => (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <StatCard label="Admin Users" value="3" icon="🔒" />
        <StatCard label="Audit Events (24h)" value="127" icon="📝" />
        <StatCard label="GDPR Requests" value="0" icon="🛡️" sub="No pending requests" color={C.green} />
        <StatCard label="Last Security Audit" value="Feb 10" icon="✅" />
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 6 }}>SIGNUP ACCESS TOKEN</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>Controls the token required on the public signup screen.</div>
          </div>
          <span style={{ fontSize: 10, color: signupAccessTokenSource === "admin" ? C.accent : C.textDim, fontFamily: F, fontWeight: 600 }}>
            {signupAccessTokenSource === "admin" ? "ADMIN MANAGED" : "FALLBACK DEFAULT"}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
          <input
            type={showSignupAccessToken ? "text" : "password"}
            value={signupAccessToken}
            onChange={e => { setSignupAccessToken(e.target.value); setSignupAccessTokenStatus(null); setSignupAccessTokenError(null); }}
            placeholder={signupAccessTokenLoading ? "Loading..." : "Signup access token"}
            disabled={signupAccessTokenLoading || signupAccessTokenSaving}
            style={{ flex: 1, padding: "10px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: F, fontSize: 12, outline: "none" }}
          />
          <button onClick={() => setShowSignupAccessToken(v => !v)} style={{ padding: "9px 12px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, fontFamily: F, fontSize: 9, fontWeight: 600, cursor: "pointer" }}>
            {showSignupAccessToken ? "Hide" : "Show"}
          </button>
          <button onClick={() => saveSignupAccessToken({ generate: true })} disabled={signupAccessTokenLoading || signupAccessTokenSaving} style={{ padding: "9px 12px", background: "transparent", border: `1px solid ${C.accent}44`, borderRadius: 6, color: C.accent, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: signupAccessTokenLoading || signupAccessTokenSaving ? "wait" : "pointer" }}>
            Generate
          </button>
          <button onClick={() => saveSignupAccessToken()} disabled={signupAccessTokenLoading || signupAccessTokenSaving || !signupAccessToken.trim()} style={{ padding: "9px 12px", background: C.accent, border: "none", borderRadius: 6, color: C.bg, fontFamily: F, fontSize: 9, fontWeight: 700, cursor: signupAccessTokenLoading || signupAccessTokenSaving ? "wait" : "pointer", opacity: signupAccessToken.trim() ? 1 : 0.6 }}>
            {signupAccessTokenSaving ? "Saving..." : "Save"}
          </button>
        </div>
        {(signupAccessTokenError || signupAccessTokenStatus) && (
          <div style={{ fontSize: 11, color: signupAccessTokenError ? C.danger : C.green }}>
            {signupAccessTokenError || signupAccessTokenStatus}
          </div>
        )}
      </div>
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 14 }}>ADMIN AUDIT LOG</div>
        {[
          { admin: "Andrew Dunn", action: "Adjusted credits for Dunn Consulting", detail: "+500 credits — Beta tester reward", time: "2 hrs ago" },
          { admin: "Andrew Dunn", action: "Changed model for Messaging Workshop", detail: "Sonnet → Haiku (cost optimisation)", time: "5 hrs ago" },
          { admin: "Andrew Dunn", action: "Impersonated DataPulse Advisory", detail: "Debugging API rate limit issue", time: "8 hrs ago" },
          { admin: "Rachel Green", action: "Resolved ticket T-1021", detail: "NexGen HeyReach integration fix", time: "1 day ago" },
          { admin: "Andrew Dunn", action: "Published solution update", detail: "Contract Analyser → draft status", time: "2 days ago" },
          { admin: "Andrew Dunn", action: "Suspended FinLeap Consulting", detail: "Payment failed 3x — auto-suspended", time: "4 days ago" },
        ].map((log, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: C.accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontFamily: F, fontWeight: 700, color: C.accent }}>{log.admin[0]}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{log.action}</div>
              <div style={{ fontSize: 10, color: C.textDim }}>{log.detail}</div>
            </div>
            <span style={{ fontSize: 11, color: C.textDim, fontWeight: 500 }}>{log.admin}</span>
            <span style={{ fontSize: 9, color: C.textDim }}>{log.time}</span>
          </div>
        ))}
      </div>

      {/* Impersonation Log */}
      <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.warn}18`, borderRadius: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 14 }}>👁️</span>
          <span style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.warn, letterSpacing: "0.06em" }}>IMPERSONATION LOG</span>
          <span style={{ fontSize: 10, color: C.textDim }}>— every admin login-as session is recorded</span>
        </div>
        {[
          { admin: "Andrew Dunn", org: "DataPulse Advisory", reason: "Debugging API rate limit issue", start: "Feb 14, 10:22am", duration: "12 min", actions: 8 },
          { admin: "Andrew Dunn", org: "Apex Advisory", reason: "Testing enrichment pipeline fix", start: "Feb 12, 3:45pm", duration: "6 min", actions: 3 },
          { admin: "Rachel Green", org: "NexGen Consulting", reason: "Verifying HeyReach integration", start: "Feb 11, 11:10am", duration: "18 min", actions: 14 },
          { admin: "Andrew Dunn", org: "Perry Salvagne Consulting", reason: "Reviewing audit deck generation", start: "Feb 9, 2:30pm", duration: "22 min", actions: 11 },
        ].map((imp, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 4 }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: C.warn + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontFamily: F, fontWeight: 700, color: C.warn }}>{imp.admin[0]}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12 }}><span style={{ fontWeight: 600 }}>{imp.admin}</span> <span style={{ color: C.textDim }}>→</span> <span style={{ fontWeight: 600, color: C.accent }}>{imp.org}</span></div>
              <div style={{ fontSize: 10, color: C.textDim }}>{imp.reason}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600 }}>{imp.duration}</div>
              <div style={{ fontSize: 8, color: C.textDim }}>DURATION</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600 }}>{imp.actions}</div>
              <div style={{ fontSize: 8, color: C.textDim }}>ACTIONS</div>
            </div>
            <span style={{ fontSize: 9, color: C.textDim }}>{imp.start}</span>
            <button style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>View Log</button>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em" }}>ADMIN ACCESS CONTROL</div>
            <button style={{ padding: "4px 10px", background: C.accent, color: C.bg, border: "none", borderRadius: 4, fontFamily: F, fontSize: 8, fontWeight: 600, cursor: "pointer" }}>+ Add Admin</button>
          </div>
          {[
            { name: "Andrew Dunn", role: "Super Admin", access: "Full access" },
            { name: "Rachel Green", role: "Support", access: "Orgs, Tickets, Users (read-only billing)" },
            { name: "Mike Thompson", role: "Read Only", access: "View all, edit nothing" },
          ].map((a, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: C.accent + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontFamily: F, fontWeight: 700, color: C.accent }}>{a.name[0]}</div>
              <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{a.name}</span>
              <Badge text={a.role.toLowerCase() === "super admin" ? "admin" : a.role.toLowerCase()} />
              <span style={{ fontSize: 10, color: C.textDim }}>{a.access}</span>
              {a.role !== "Super Admin" && <button style={{ padding: "3px 6px", background: "transparent", border: `1px solid ${C.danger}33`, borderRadius: 4, color: C.danger, fontFamily: F, fontSize: 8, cursor: "pointer" }}>✕</button>}
            </div>
          ))}
        </div>
        <div style={{ padding: "20px 24px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, fontFamily: F, fontWeight: 600, color: C.textDim, letterSpacing: "0.06em", marginBottom: 12 }}>DATA & COMPLIANCE</div>
          {[
            { label: "GDPR Data Export Requests", value: "0 pending", action: "Process" },
            { label: "Right to Deletion Requests", value: "0 pending", action: "Process" },
            { label: "Data Retention Policy", value: "90 days inactive", action: "Edit" },
            { label: "IP Allowlist (Admin)", value: "Not configured", action: "Configure" },
          ].map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}`, gap: 10 }}>
              <span style={{ flex: 1, fontSize: 12 }}>{d.label}</span>
              <span style={{ fontSize: 11, color: C.textDim }}>{d.value}</span>
              <button style={{ padding: "4px 10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textMuted, fontFamily: F, fontSize: 8, cursor: "pointer" }}>{d.action}</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ---- RENDER PAGE ----
  const renderPage = () => {
    switch (page) {
      case "overview": return renderOverview();
      case "agencies": return renderAgencies();
      case "orgs": return renderOrgs();
      case "credits": return renderCredits();
      case "leadgen": return renderLeadGen();
      case "ai": return renderAI();
      case "system": return renderSystem();
      case "knowledge": return renderKnowledge();
      case "users": return renderUsers();
      case "billing": return renderBilling();
      case "solutions": return renderSolutions();
      case "integrations": return renderIntegrations();
      case "lead_search_order": return renderLeadSearchOrder();
      case "content": return renderContent();
      case "support": return renderSupport();
      case "error_logs": return renderErrorLogs();
      case "analytics": return renderAnalytics();
      case "security": return renderSecurity();
      default: return renderOverview();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg, color: C.text, fontFamily: FB, fontSize: 14, overflow: "hidden" }}>
      {/* Environment Banner */}
      <div style={{ background: "#dc2626", padding: "3px 0", textAlign: "center", flexShrink: 0 }}>
        <span style={{ fontSize: 9, fontFamily: F, fontWeight: 700, color: "#FFF", letterSpacing: "0.12em" }}>🔴 PRODUCTION ENVIRONMENT — ALL CHANGES AFFECT LIVE CUSTOMERS</span>
      </div>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* Sidebar */}
      <div style={{ width: 240, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "20px 16px 14px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: C.accent + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 14 }}>⚙️</span>
            </div>
            <div>
              <div style={{ fontFamily: F, fontSize: 14, fontWeight: 700, color: C.accent, letterSpacing: "-0.02em" }}>PIPELINE</div>
              <div style={{ fontFamily: F, fontSize: 8, color: C.textDim, letterSpacing: "0.1em" }}>ADMIN CONSOLE</div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "8px 6px" }}>
          {NAV.map(n => (
            <button key={n.key} onClick={() => { setPage(n.key); setSelectedOrg(null); }} style={{
              width: "100%", padding: "9px 10px", marginBottom: 1, background: page === n.key ? C.accentBg : "transparent",
              border: page === n.key ? `1px solid ${C.accent}22` : "1px solid transparent", borderRadius: 6,
              color: page === n.key ? C.accent : C.textMuted, fontFamily: FB, fontSize: 12, fontWeight: 500,
              cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 8, transition: "all 0.15s",
            }}><span className="material-symbols-outlined" style={{ fontSize: 18 }}>{n.icon}</span>{n.label}</button>
          ))}
        </div>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 24, height: 24, borderRadius: 6, background: C.danger + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontFamily: F, fontWeight: 700, color: C.danger }}>A</div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600 }}>Admin</div>
            <div style={{ fontSize: 9, color: C.textDim }}>Super Admin</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
        <div style={{ marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontFamily: F, fontSize: 20, fontWeight: 600, margin: 0, letterSpacing: "-0.02em", display: "flex", alignItems: "center", gap: 10 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 26, color: C.textDim }}>{NAV.find(n => n.key === page)?.icon}</span> <span style={{ color: C.accent }}>{NAV.find(n => n.key === page)?.label}</span>
            </h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Global Search */}
            <div style={{ position: "relative" }}>
              <input value={globalSearch} onChange={e => { setGlobalSearch(e.target.value); setShowGlobalResults(e.target.value.length > 0); }} onBlur={() => setTimeout(() => setShowGlobalResults(false), 200)} placeholder="Search orgs, users, tickets..." style={{ width: 260, padding: "7px 12px 7px 30px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11, fontFamily: FB, color: C.text, outline: "none" }} />
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: C.textDim }}>🔍</span>
              {showGlobalResults && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", zIndex: 100, boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>
                  {ORGS.filter(o => o.name.toLowerCase().includes(globalSearch.toLowerCase()) || o.owner.toLowerCase().includes(globalSearch.toLowerCase()) || o.email.toLowerCase().includes(globalSearch.toLowerCase())).slice(0, 5).map(o => (
                    <div key={o.id} onClick={() => { setSelectedOrg(o); setPage("orgs"); setDetailTab("overview"); setGlobalSearch(""); setShowGlobalResults(false); }} style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${C.border}` }} onMouseEnter={e => e.currentTarget.style.background = C.surfaceHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 12 }}>🏢</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600 }}>{o.name}</div>
                        <div style={{ fontSize: 9, color: C.textDim }}>{o.owner} · {o.email}</div>
                      </div>
                      <Badge text={o.status} />
                    </div>
                  ))}
                  {TICKETS.filter(t => t.subject.toLowerCase().includes(globalSearch.toLowerCase()) || t.org.toLowerCase().includes(globalSearch.toLowerCase())).slice(0, 3).map(t => (
                    <div key={t.id} onClick={() => { setPage("support"); setGlobalSearch(""); setShowGlobalResults(false); }} style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${C.border}` }} onMouseEnter={e => e.currentTarget.style.background = C.surfaceHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span style={{ fontSize: 12 }}>🎧</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600 }}>{t.subject}</div>
                        <div style={{ fontSize: 9, color: C.textDim }}>{t.id} · {t.org}</div>
                      </div>
                      <Badge text={t.status} />
                    </div>
                  ))}
                  {ORGS.filter(o => o.name.toLowerCase().includes(globalSearch.toLowerCase())).length === 0 && TICKETS.filter(t => t.subject.toLowerCase().includes(globalSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding: "12px", textAlign: "center", fontSize: 11, color: C.textDim }}>No results for "{globalSearch}"</div>
                  )}
                </div>
              )}
            </div>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, boxShadow: `0 0 4px ${C.green}88` }} />
            <span style={{ fontSize: 10, color: C.textDim, fontFamily: F }}>All systems operational</span>
          </div>
        </div>
        {renderPage()}
      </div>
      </div>
      {adminConfigModal && (
        <AdminIntegrationConfigModal
          modal={adminConfigModal}
          onClose={() => setAdminConfigModal(null)}
          onSaved={refreshOrgIntegrations}
        />
      )}
    </div>
  );
}
